import React, { useMemo, useState, useEffect, useRef, createElement } from 'react';
import { useAtom } from 'jotai';
import moment from 'moment';
import XLSX from 'xlsx';
import { View, Text, Pressable, ScrollView, TextInput } from '../lib/react-native-compat';
import {
    selectedJobAtom, selectedJobDataAtom,
    jobCostDetailAtom, billingUserDataAtom, BILLING_VERSION, loadingCostAtom, userIdAtom, userNameAtom,
    jobBillingsAtom, userEmailAtom, jobOpenCommitmentsAtom,
} from '../store/company';
import { updateUserDataArray, deleteUserData, loadUserData, loadPOMatchStatus, loadJobTimecards, loadJdeBackupManifest, loadFieldTicketMap, loadAttendanceRoster, loadBillingSignatures, loadRentalHours, loadRentalUsage, pNum } from '../db/company';
import { showToast } from '../helpers/toast';
import { Chip, FilterDropdown, OutlineButton, SkeletonRows } from './procurementFilters';
import BillingRates, { round2, DEFAULT_SETTINGS, mergeRateSchedule } from './billingRates';
import { equipNamer, unitTiersOf, clientEquipLabel } from './billingEquipNames';
import { parseLaborMap, fieldSuggestions } from './billingLaborMap';
import { tapProxyUrl, tapDocId, TAP_API_BASE, useTapInvoiceViewer } from './tapInvoiceViewer';
import { buildFtmsPayload, sendToFtms, FTMS_ADD_URL } from './billingFtms';
import { fuelSurcharge, fuelSnapPct, parseFuelIndex, fmtPct } from './billingFuel';
import { PT_GROUP, isPassThru } from './billingPassThru';
import { contractOn, contractFor, contractTerms, effectLabel, EFFECT_TONE, RUBRIC, equipRowAs } from './billingContract';
import {
    bucketOf, normInvStatus, fDate, LABOR_BILLABLE_CT, NEVER_BILLED_CT_PREFIX,
    parseBillingRows, poStatusMap, jdeLaborClassesOf, jobEquipTiersOf, equipBillsOf, billsAsOf, sheetLaborOf, fuelCtxOf, priceCostRows, personClsOf,
    rateCfgOf, laborRateFor, laborWageOf,
} from './billingPricing';
import BillingExportCheck from './billingExportCheck';
import BillingTimecardApproval, { tcFileDate } from './billingTimecardApproval';
import BillingReport from './billingReport';
import BillingInvoiceWorkbook from './billingInvoiceWorkbook';
import BillingReconciliation from './billingReconciliation';
import { parseInvoiceDrafts } from './billingInvoiceDrafts';
import { jdeClaims, statusContext, summaryEnvelope, envStatus, signersOf, STATUS as SIG_STATUS } from './billingRecon';
import BillingInvoiceChase, { chaseRows } from './billingInvoiceChase';
import BillingLienWaivers from './billingLienWaivers';
import BillingContractDocs from './billingContractDocs';
import BillingInvoiceApprovals, { invoiceApprovals, approvedNumbers } from './billingInvoiceApprovals';
import CostBasisModal, { ocOpen } from './billingCostBasis';
import { rentalPos, rentalHoursMap, lineKey as rentalLineKey, rentalHoursTip } from './billingRentalHours';
import { rentalUsageOf, rentalElsewhereOf, rentalPosOf, isRentUsage, rentUsageFrom, NO_RENT_ELSEWHERE } from './billingRentalUsage';
import usePersistedState from './usePersistedState';
import BillingAddLines, { SigTag } from './billingAddLines';
import { parseDocs, envelopesByDoc, signedByDate, weekEnding } from './billingTicketDocs';
import { weekGroups, foldWeekLabor, signedStanding, ticketed as sigTicketed } from './billingWeekGroups';
// import BillingInvoices from './billingInvoices'; // old Invoices tab hidden
import { summaryQa, qaItems, QaTag, QaFindings } from './billingQa';
// import BillingManualTicket from './billingManualTicket'; // Custom Ticket tab hidden
import strikeLogo from '../assets/logo.png';
import voltaLogo from '../assets/voltagrid.png';

// Billing tab (admin only — see the guardAdmin gate in sideMenu/main).
//
// Cost-plus invoicing for the selected job: every actual cost transaction from
// the job cost history is either Unbilled or on an invoice. Pick unbilled
// lines, apply the markup for their cost class, and create a summary. The
// Invoices list shows where each summary's invoice approval stands in
// DocuSign (Not Sent / In DocuSign / Signed / Declined, off the 'inv…'
// envelope closing out its work dates — billingRecon.summaryEnvelope). The
// stored stage (Draft, Uploaded to FTMS, Kicked Back, Invoiced) still gates
// line edits and stays in the summary's history, but nothing in the list
// moves it any more. A Draft — or a summary voided before Void was retired —
// can be deleted outright (see deleteSummary): the record goes away and its
// lines return to Unbilled.
//
// Storage is Forecast_User_Data rows under tab 'Billing' (same JSON-in-Notes
// pattern as the equipment dashboard) at Version_GUID BILLING_VERSION (0) —
// job-level, NOT the selected forecast version. They used to ride the weekly
// version copy like CO Log entries, but the SQL Agent builds several hidden
// versions a day and copies the rows at build start, so anything saved after
// the copy was stranded on the old version (the 9/11 markup edit on 54026070
// sat on v118 while the app had moved to v120). Rows on versions 112-120 are
// dead history now; localserver/scripts/migrate-billing-to-v0.js moved the
// live state over.
//   fieldName 'settings' rowId 'settings'   Notes = { mk, prefix, next, terms }
//   fieldName 'invoice'  rowId 'inv_<no>'   Notes = { no, date, status, terms,
//     h }: h = stage-change stamps { s: status, r?: reason, u: user, t: time }
//   fieldName 'line'     rowId 'bl2_<jde key>#<n>' (legacy rows 'bl_<hash>')
//     Value = invoice no, Notes = line
//     snapshot { d, cc, ct, ds, c, m, hr, who, po } — the markup and the
//     labor hours are frozen at
//     invoice time so later rate changes never move an issued invoice.
//     The rowId is the transaction's identity — see the key derivation in
//     the txns memo for the bl2/legacy split.
//   fieldName 'meta'     rowId same as 'line'  Value = 'NB' while not billable,
//     Notes = { nb, rr, rs, lc, h }: rr = not-billable reason, rs = review status
//     ('Reviewed' | 'Flagged', absent = not reviewed), lc = per-line labor
//     classification fix (the rate sheet class the line bills as when the
//     row's JDE payroll class is wrong), h = the line's audit
//     trail: events { a: rev|flag|clr|nb|ok|bill|void|pull|lc|note, v?: invoice no,
//     r?: reason, n?: note text, c?: class, u: user, t: 'YYYY-MM-DD HH:mm' }, oldest dropped so the
//     blob stays under the 1024-char Notes truncation — a truncated JSON
//     blob would drop the whole history.
//   fieldName 'rateset' / 'rate' / 'rule' — the Rates & Rules view's rate
//     schedule and provisions (per-job overrides of the default VoltaGrid
//     rate sheet). Owned and documented by billingRates.js.
//   fieldName 'tcsheet'  rowId 'tcs_<date>_<Day|Night>' — the Timecard
//     Approval view's per-shift daily-sheet tracker. Owned and documented by
//     billingTimecardApproval.js.


// Invoice backup rolls the five cost classes up into the buckets the weekly
// invoice is grouped by: labor together, equipment together, every
// third-party expense (materials, subs, everything else) together — and
// pass-through delivery/freight (billingPassThru) on its own, whatever class
// it posted under, so the client sees what bills at cost.
const GROUPS = ['Labor', 'Equipment', 'Third Party', PT_GROUP];
const groupOf = (cls, pt) => (pt ? PT_GROUP : cls === 'Labor' || cls === 'Equipment' ? cls : 'Third Party');

// The five cost classes in LEMSO order. The backup PDF opens with the
// summary rolled up across these — the shape the client reads their own cost
// report in.
const LEMSO = ['Labor', 'Equipment', 'Materials', 'Subcontractors', 'Other'];

// Logos for the backup PDF cover. Drawn through a canvas so jsPDF gets a data
// URL and the natural size in one pass; cached for the session, and a logo
// that won't load resolves null and is skipped rather than failing the export.
const LOGO_CACHE = {};
const LOGO_TIMEOUT_MS = 3000;
const loadLogo = (src) => {
    if (!src) return Promise.resolve(null);
    if (!LOGO_CACHE[src]) {
        LOGO_CACHE[src] = new Promise((resolve) => {
            // Neither event is guaranteed to fire (jsdom never loads images at
            // all), and the export must not hang waiting on decoration — time
            // the load out and carry on without the logo.
            const done = setTimeout(() => resolve(null), LOGO_TIMEOUT_MS);
            const finish = (v) => { clearTimeout(done); resolve(v); };
            const img = new Image();
            img.onload = () => {
                try {
                    const c = document.createElement('canvas');
                    c.width = img.naturalWidth;
                    c.height = img.naturalHeight;
                    c.getContext('2d').drawImage(img, 0, 0);
                    finish({ data: c.toDataURL('image/png'), w: img.naturalWidth, h: img.naturalHeight });
                } catch (e) { finish(null); }
            };
            img.onerror = () => finish(null);
            img.src = src;
        });
    }
    return LOGO_CACHE[src];
};

// ── Timecard evidence ──
// Field timecards live on the job's SharePoint subsite under
// 02 - Project Controls / 02.06 - Timecards. The backup bundle lists
// them (via readJobTimecards), preselects the ones whose file/folder name
// carries a date inside the summary's line-date range, and merges the picked
// scans (PDF/image) into the backup-detail PDF as labor evidence — other file
// types attach to the AR email separately. Browsers can't call Graph with
// the app-only token, so the bytes stream through the jobTimecardFile proxy.
// A file's extension, lowercased and without the dot.
const attExt = (name) => ((String(name || '').match(/\.(\w+)$/) || [])[1] || '').toLowerCase();

// Anything that isn't already a page comes back rendered: the proxy hands the
// item to Graph's Office service, which turns the crew's .xlsx timesheets into
// PDF. That's the only way a spreadsheet gets into the backup — a browser
// can't draw one. What Graph won't convert (macro-enabled .xlsm among them)
// streams as its own bytes and is sorted out by the sniff on arrival.
const EMBEDDABLE_EXT = ['pdf', 'jpg', 'jpeg', 'png'];
const tcProxyUrl = (f) => {
    const raw = `${TAP_API_BASE}api/jobTimecardFile?driveId=${encodeURIComponent(f.driveId)}&id=${encodeURIComponent(f.id)}`;
    return EMBEDDABLE_EXT.includes(attExt(f.name)) ? raw : `${raw}&as=pdf`;
};

// ── Receiving-doc evidence ──
// The scans the field attaches to a PO line in JDE when material or a service
// is received — delivery tickets, packing slips, sometimes the vendor's own
// invoice. They're indexed in F00165 (jdeBackupManifest returns the
// coordinates for a job's POs) and the bytes come out of E1 through the AIS
// proxy — the UNC paths the index records are fiction, only JDE can read the
// actual files. GT4311 is the PO-detail structure receiving hangs on.
const moProxyUrl = (d) =>
    `${TAP_API_BASE}api/jdeAttachmentDoc?obj=${encodeURIComponent(d.moName)}&key=${encodeURIComponent(d.moKey)}&seq=${d.moId}&name=${encodeURIComponent(d.name || '')}`;

// The receiving documents behind a summary's lines: the job manifest cut down
// to the POs the summary actually bills, one entry per document. A doc backs
// every line on its PO — receiving is filed per PO line in JDE, but the
// billing rows carry only the PO, so the PO is the join.
const receivingDocsFor = (sum, docs) => {
    if (!docs?.length) return [];
    const keysByPo = {};
    sum.groups.forEach((g) => g.lines.forEach((t) => {
        const po = pNum(t.po);
        if (po > 0 && t.key) (keysByPo[po] = keysByPo[po] || []).push(t.key);
    }));
    const seen = new Set();
    return docs
        .filter((d) => keysByPo[pNum(d.po)])
        .filter((d) => {
            const id = `${d.moName}|${d.moKey}|${d.moId}`;
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
        })
        .map((d) => ({
            kind: 'receiving',
            name: d.name || d.filename || `attachment-${d.moId}`,
            title: `PO ${d.po} — ${d.name || d.filename || `attachment ${d.moId}`}`,
            proxy: moProxyUrl(d),
            url: '',
            po: d.po,
            keys: keysByPo[pNum(d.po)] || [],
        }));
};

// Timecards covering a summary: any card whose name (or the week folder it
// sits in) carries a date within six days either side of the summary's labor
// dates — a card named for the week ending, "WE 8-30", backs the days before
// it. Labor dates drive the window; a summary with no labor lines gets no
// timecards — a card proves hours worked, and a materials-only invoice has
// none to prove.
// An archive isn't a timecard. One job's folder keeps a 244 MB zip of
// orientation documents whose name carries a date — it would match the window
// and it has no business on an invoice.
const NOT_EVIDENCE = ['zip', 'rar', '7z', 'tar', 'gz'];
const timecardsInRange = (files, lines) => {
    const labor = lines.filter((t) => t.cls === 'Labor' && t.date);
    const dates = labor.map((t) => t.date).sort();
    if (!dates.length) return [];
    const min = moment.utc(dates[0]).subtract(6, 'days');
    const max = moment.utc(dates[dates.length - 1]).add(6, 'days');
    return (files || []).filter((f) => {
        if (NOT_EVIDENCE.includes(attExt(f.name))) return false;
        const fd = tcFileDate(f);
        return fd && moment.utc(fd).isBetween(min, max, 'day', '[]');
    });
};

// ── Backup attachments ──
// The backup PDF carries its own evidence: the scanned vendor invoice behind
// every third-party line (streamed out of TAP) and the timecards covering the
// labor dates (out of the job's SharePoint folder). Each document rides once
// however many lines stand behind it — cost history repeats a voucher's TAP
// link on every line the voucher paid for — and the line detail names the
// exhibit and the page it starts on, so a reader can go straight to it.

// pdf-lib embeds PNG and JPEG and nothing else, so anything else the browser
// can decode as an image — .webp, .gif, .bmp, a phone scan filed with the
// wrong extension — goes through a canvas and comes back as PNG. What the
// browser can't decode either (HEIC outside Safari, most TIFFs, and anything
// that isn't an image at all, like an xlsx timesheet) resolves null and ends
// up on the index as a link.
const IMG_TIMEOUT_MS = 6000;

// What a file actually is, from its first bytes — filed extensions lie, and
// the alternative (handing everything to the image decoder and waiting for it
// to give up) costs seconds per document. 'img' means "an image format the
// canvas can probably decode"; 'other' means nothing that can go on a page.
const sniff = (buf) => {
    const b = new Uint8Array(buf.slice(0, 12));
    const at = (i, ...bytes) => bytes.every((v, k) => b[i + k] === v);
    if (at(0, 0x25, 0x50, 0x44, 0x46)) return 'pdf';                       // %PDF
    if (at(0, 0x89, 0x50, 0x4e, 0x47)) return 'png';
    if (at(0, 0xff, 0xd8, 0xff)) return 'jpg';
    if (at(0, 0x47, 0x49, 0x46, 0x38)) return 'img';                       // GIF8
    if (at(0, 0x42, 0x4d)) return 'img';                                   // BM
    if (at(0, 0x52, 0x49, 0x46, 0x46) && at(8, 0x57, 0x45, 0x42, 0x50)) return 'img';  // RIFF/WEBP
    return 'other';                                                        // zip (xlsx), HEIC, TIFF, HTML…
};
// EXIF Orientation (tag 0x0112) from a JPEG's APP1 segment. 1 — or no tag at
// all — means the pixels already read upright; anything else is a phone photo
// stored sideways with a flag that viewers honor but embedJpg does not.
const jpgOrientation = (buf) => {
    try {
        const v = new DataView(buf);
        if (v.getUint16(0) !== 0xffd8) return 1;
        let o = 2;
        while (o + 4 <= v.byteLength) {
            const marker = v.getUint16(o);
            if ((marker & 0xff00) !== 0xff00) break;
            const len = v.getUint16(o + 2);
            if (marker === 0xffe1 && v.getUint32(o + 4) === 0x45786966) {     // "Exif"
                const t = o + 10;                                             // TIFF header
                const le = v.getUint16(t) === 0x4949;                         // "II" = little-endian
                const ifd = t + v.getUint32(t + 4, le);
                const n = v.getUint16(ifd, le);
                for (let i = 0; i < n; i += 1) {
                    const at = ifd + 2 + i * 12;
                    if (v.getUint16(at, le) === 0x0112) return v.getUint16(at + 8, le) || 1;
                }
                return 1;
            }
            if (marker === 0xffda) break;                                     // pixel data — no EXIF coming
            o += 2 + len;
        }
    } catch (e) { /* truncated or garbled EXIF reads as upright */ }
    return 1;
};
const imageToPng = (buf, ext) => new Promise((resolve) => {
    let url = '';
    try {
        url = URL.createObjectURL(new Blob([buf], { type: ext ? `image/${ext}` : 'application/octet-stream' }));
    } catch (e) {
        resolve(null);
        return;
    }
    // Neither event is guaranteed to fire — time the decode out rather than
    // hanging the export on one bad file.
    const timer = setTimeout(() => resolve(null), IMG_TIMEOUT_MS);
    const done = (v) => { clearTimeout(timer); URL.revokeObjectURL(url); resolve(v); };
    const img = new Image();
    img.onload = () => {
        try {
            const c = document.createElement('canvas');
            c.width = img.naturalWidth;
            c.height = img.naturalHeight;
            c.getContext('2d').drawImage(img, 0, 0);
            c.toBlob(
                (b) => (b && b.arrayBuffer ? b.arrayBuffer().then(done, () => done(null)) : done(null)),
                'image/png',
            );
        } catch (e) { done(null); }
    };
    img.onerror = () => done(null);
    img.src = url;
});

// The distinct scanned invoices behind a summary's lines, in the order the
// backup prints them. The TAP document id is the identity; a Media_Object_URL
// with no parseable id can't be proxied either, so it keys as itself and ends
// up on the index as a link.
const invoiceDocsFor = (sum) => {
    const byDoc = new Map();
    sum.groups.forEach((g) => g.lines.forEach((t) => {
        if (!t.pdf) return;
        const id = tapDocId(t.pdf) || t.pdf;
        const e = byDoc.get(id) || {
            kind: 'invoice', url: t.pdf, proxy: tapProxyUrl(t.pdf), docId: tapDocId(t.pdf),
            invNo: '', vendor: '', po: '', keys: [], name: 'invoice.pdf',
        };
        e.invNo = e.invNo || t.vinvNo || '';
        e.vendor = e.vendor || t.who || '';
        e.po = e.po || t.po || '';
        if (t.key) e.keys.push(t.key);
        byDoc.set(id, e);
    }));
    return [...byDoc.values()].map((e) => ({
        ...e,
        title: [e.invNo ? `Inv ${e.invNo}` : '', e.vendor, e.po ? `PO ${e.po}` : ''].filter(Boolean).join(' · ')
            || `TAP ${e.docId || 'document'}`,
    }));
};

// Exhibit labels, in plan order — invoices, then receiving docs, then timecards.
const labelAttachments = (entries) => {
    let inv = 0;
    let rcv = 0;
    let tc = 0;
    entries.forEach((e) => {
        e.label = e.kind === 'invoice' ? `INV-${(inv += 1)}`
            : e.kind === 'receiving' ? `RCV-${(rcv += 1)}`
                : `TC-${(tc += 1)}`;
    });
    return entries;
};

// Pull each document's bytes and work out how many pages it adds. Whatever
// won't download, won't embed, or won't fit the byte budget is marked for a
// link — or, where the caller can carry loose files (the AR email can; a
// download can't), for its own attachment. Evidence is never silently
// dropped, it just moves from "in the PDF" to "here's where it lives", and
// `why` records which so the index can say so in as many words.
const loadAttachmentBytes = async (entries, opts = {}) => {
    const cap = opts.maxBytes || 0;
    // Only a caller that sends a real envelope can offer "attached separately";
    // for a plain download that would be a lie, so those documents link.
    const loose = !!opts.canAttachSeparately;
    let PDFDocument = null;
    try { ({ PDFDocument } = await import('pdf-lib')); } catch (e) { PDFDocument = null; }
    let used = 0;
    for (const e of entries) {
        const url = e.proxy || '';
        e.status = 'link';
        if (!url) { e.why = 'nolink'; continue; }
        if (!PDFDocument) { e.why = 'nopdflib'; continue; }
        if (cap && (used >= cap || used + pNum(e.size) > cap)) { e.why = 'size'; continue; }
        let buf;
        try {
            const r = await fetch(url);
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            buf = await r.arrayBuffer();
        } catch (err) {
            e.error = err?.message || String(err);
            e.why = 'download';
            continue;
        }
        used += buf.byteLength;
        e.buf = buf;
        const shape = sniff(buf);
        const noPage = () => {
            // An xlsx timesheet, a HEIC the browser won't decode, a TAP error
            // page served as HTML — nothing that can go on a page. Something
            // that was supposed to be a PDF and isn't reads as broken, not as
            // the wrong kind of file.
            e.why = e.kind === 'invoice' || attExt(e.name) === 'pdf' ? 'corrupt' : 'type';
            e.status = loose && e.kind === 'timecard' ? 'attach' : 'link';
        };
        try {
            if (shape === 'pdf') {
                e.src = await PDFDocument.load(buf, { ignoreEncryption: true });
                e.pages = e.src.getPageCount();
                e.status = 'merge';
            } else if (shape === 'png' || shape === 'jpg') {
                // A sideways-stored phone photo gets re-rendered upright
                // through the canvas (the browser applies its EXIF flag on
                // decode); if that fails, the raw bytes still merge — a
                // sideways page beats a missing one.
                if (shape === 'jpg' && jpgOrientation(buf) !== 1) {
                    const png = await imageToPng(buf, 'jpeg');
                    if (png) { e.buf = png; e.img = 'png'; e.pages = 1; e.status = 'merge'; continue; }
                }
                e.img = shape;
                e.pages = 1;
                e.status = 'merge';
            } else if (shape === 'img') {
                const png = await imageToPng(buf, attExt(e.name));
                if (png) {
                    e.buf = png;
                    e.img = 'png';
                    e.pages = 1;
                    e.status = 'merge';
                } else {
                    noPage();
                }
            } else {
                noPage();
            }
        } catch (err) {
            e.error = err?.message || String(err);
            e.why = 'corrupt';
            e.status = loose && e.kind === 'timecard' ? 'attach' : 'link';
        }
    }
    return entries;
};

// Where each document lands, once the base document's page count is known.
// Everything appends after the base, in plan order.
const layoutAttachments = (entries, basePages) => {
    let p = basePages;
    entries.forEach((e) => {
        if (e.status === 'merge') {
            e.startPage = p + 1;
            p += e.pages || 1;
        } else {
            e.startPage = 0;
        }
    });
    return p;
};

// Append the evidence to the built backup, stamping every appended page with
// its exhibit label and position so a page separated from the bundle can still
// be traced back. Every appended page is redrawn onto a fresh portrait letter
// page, scaled to fit — a mixed bag of scanner output comes out one uniform
// size, matching the summary pages in front of it. The stamp sits in a small
// chip inside the top 15pt, within the margin of anything that came off a
// scanner. A document that fails to copy still lays down its page count as
// placeholders, so the index's page numbers hold.
const mergeAttachments = async (baseBytes, entries) => {
    const { PDFDocument, StandardFonts, rgb, degrees } = await import('pdf-lib');
    const out = await PDFDocument.load(baseBytes);
    const PW = 612;
    const PH = 792;
    const font = await out.embedFont(StandardFonts.Helvetica);
    const INK = rgb(0.28, 0.33, 0.41);
    const stamp = (page, text) => {
        const w = page.getWidth();
        const h = page.getHeight();
        page.drawRectangle({
            x: 8, y: h - 15, width: Math.min(font.widthOfTextAtSize(text, 6.5) + 8, w - 16), height: 11,
            color: rgb(1, 1, 1), borderColor: rgb(0.79, 0.83, 0.88), borderWidth: 0.5, opacity: 0.92,
        });
        page.drawText(text, { x: 12, y: h - 12, size: 6.5, font, color: INK });
    };
    for (const e of entries) {
        if (e.status !== 'merge') continue;
        const head = `${e.label} · ${e.title || e.name}`;
        try {
            if (e.src) {
                // embedPdf drops the /Rotate flag a scanner leaves on the
                // page, so a rotated scan is drawn back at the orientation a
                // viewer showed it — hence the per-angle placement below.
                const srcPages = e.src.getPages();
                // A page with no content stream (a truly blank scan) can't be
                // embedded — and pdf-lib only throws at save time, where one
                // such page poisons the whole document. Give it an invisible
                // no-op stream first.
                srcPages.forEach((pg) => {
                    if (!pg.node.Contents()) pg.drawRectangle({ x: 0, y: 0, width: 0.01, height: 0.01, opacity: 0, borderOpacity: 0 });
                });
                const embedded = await out.embedPdf(e.src, e.src.getPageIndices());
                embedded.forEach((ep, i) => {
                    const rot = ((srcPages[i].getRotation().angle % 360) + 360) % 360;
                    const [vw, vh] = rot % 180 ? [ep.height, ep.width] : [ep.width, ep.height];
                    const sc = Math.min((PW - 40) / vw, (PH - 56) / vh);
                    const dw = vw * sc;
                    const dh = vh * sc;
                    const cx = (PW - dw) / 2;
                    const cy = (PH - 28 - dh) / 2;
                    const page = out.addPage([PW, PH]);
                    const draw = { width: ep.width * sc, height: ep.height * sc };
                    if (rot === 90) page.drawPage(ep, { ...draw, x: cx, y: cy + dh, rotate: degrees(-90) });
                    else if (rot === 180) page.drawPage(ep, { ...draw, x: cx + dw, y: cy + dh, rotate: degrees(180) });
                    else if (rot === 270) page.drawPage(ep, { ...draw, x: cx + dw, y: cy, rotate: degrees(90) });
                    else page.drawPage(ep, { ...draw, x: cx, y: cy });
                    stamp(page, `${head} · page ${i + 1} of ${embedded.length}`);
                });
            } else {
                const img = e.img === 'jpg' ? await out.embedJpg(e.buf) : await out.embedPng(e.buf);
                // Same portrait letter page as everything else; a wide scan
                // just sits smaller, centered. No upscaling — a low-res photo
                // blown to page width reads worse than a margin does.
                const page = out.addPage([PW, PH]);
                const sc = Math.min((PW - 56) / img.width, (PH - 70) / img.height, 1);
                page.drawImage(img, {
                    x: (PW - img.width * sc) / 2, y: (PH - 28 - img.height * sc) / 2,
                    width: img.width * sc, height: img.height * sc,
                });
                stamp(page, head);
            }
        } catch (err) {
            for (let i = 0; i < (e.pages || 1); i += 1) {
                stamp(out.addPage([PW, PH]), `${head} · page ${i + 1} — could not be embedded`);
            }
        }
    }
    return out.save();
};

// Byte budgets for the fetched evidence: generous for a download or the FTMS
// hand-off, tight for email (the relay caps the whole message).
const ATT_MAX_BYTES = 40 * 1024 * 1024;
const FTMS_MAX_BYTES = 20 * 1024 * 1024;

// The filename/folder date parser lives with the Timecard Approval view now
// (it matches SharePoint docs to tracked days with the same rules) — imported
// above as tcNameDate / tcFileDate.

// Hours print to one decimal everywhere.
const round1 = (n) => Math.round(pNum(n) * 10) / 10;

const money = (n) => {
    const v = pNum(n);
    const abs = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return (v < 0 ? '-$' : '$') + abs;
};

// Whole dollars, for the stat strip — cents are noise at job scale.
const money0 = (n) => {
    const v = Math.round(pNum(n));
    return (v < 0 ? '-$' : '$') + Math.abs(v).toLocaleString('en-US');
};


// Escape for the printable backup document.
const esc = (v) => String(v == null ? '' : v).replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));

const BADGES = {
    Unbilled: { bg: '#fef3c7', fg: '#92400e' },
    Billed: { bg: '#dcfce7', fg: '#166534' },
    'Not Billable': { bg: '#e2e8f0', fg: '#475569' },
    // Labor line the rate sheet can't price yet — needs a map/schedule fix.
    'No Rate': { bg: '#ffedd5', fg: '#9a3412' },
    Reviewed: { bg: '#dcfce7', fg: '#166534' },
    Flagged: { bg: '#fee2e2', fg: '#b91c1c' },
    Draft: { bg: '#f1f5f9', fg: '#475569' },
    // Legacy — the AR review stage, retired when the FTMS upload became the
    // hand-off. Both spellings kept so old history stamps still render.
    'With AR': { bg: '#ede9fe', fg: '#5b21b6' },
    'With Chandra': { bg: '#ede9fe', fg: '#5b21b6' },
    'Kicked Back': { bg: '#ffedd5', fg: '#9a3412' },
    'Uploaded to FTMS': { bg: '#dbeafe', fg: '#1e40af' },
    // Legacy — the FTMS upload was the 'Sent to Client' stage before the
    // hand-off moved onto the FTMS button. Kept so old history stamps render.
    'Sent to Client': { bg: '#dbeafe', fg: '#1e40af' },
    Invoiced: { bg: '#dcfce7', fg: '#166534' },
    // Legacy — the final stage was called Paid before the Invoiced rename.
    Paid: { bg: '#dcfce7', fg: '#166534' },
    Void: { bg: '#fee2e2', fg: '#b91c1c' },
    // Where a summary's invoice approval stands in DocuSign — the Invoices
    // list's STATUS column (sumSign).
    'Not Sent': { bg: '#f1f5f9', fg: '#475569' },
    'In DocuSign': { bg: '#dbeafe', fg: '#1e40af' },
    Signed: { bg: '#dcfce7', fg: '#166534' },
    Declined: { bg: '#fee2e2', fg: '#b91c1c' },
    // Line edits stamped in a summary's history — audit events, not stages.
    'Line Removed': { bg: '#e2e8f0', fg: '#475569' },
    'Line Added': { bg: '#e2e8f0', fg: '#475569' },
    // Vendor-invoice match status (JDE PO receipt records, live)
    Vouchered: { bg: '#dcfce7', fg: '#166534' },
    Received: { bg: '#fef3c7', fg: '#92400e' },
    Partial: { bg: '#dbeafe', fg: '#1e40af' },
    Reversed: { bg: '#fee2e2', fg: '#b91c1c' },
};

// Per-line audit events, stored compact in the meta row's Notes.
const HIST_CAP = 12;
const NOTE_MAX = 140;
const histLabel = (e) => ({
    rev: 'Marked reviewed',
    flag: 'Flagged for review',
    clr: 'Review status cleared',
    nb: `Marked not billable${e.r ? ' — ' + e.r : ''}`,
    ok: 'Marked billable',
    bill: `Billed on ${e.v || ''}`,
    void: `Released — ${e.v || ''} voided`,
    pull: `Removed from ${e.v || ''}`,
    lc: e.c ? `Classification adjusted — bills as ${e.c}` : 'Classification adjustment cleared',
    pt: e.c === 'Y' ? 'Set as pass-through' : e.c === 'N' ? 'Set as not pass-through' : 'Pass-through back to the rules',
    note: e.n || '',
}[e.a] || e.a);


const NB_QUICK_REASONS = [
    'Incorrect items on invoice',
    'Duplicate charge',
    'Wrong job or cost code',
    'Rate discrepancy',
];

// Summary stages (stored on the summary, stamped in its history). The list
// no longer moves them by hand — its STATUS column reads DocuSign instead.
const OPEN_STAGES = ['Draft', 'Kicked Back', 'Uploaded to FTMS'];
// Stages a summary can be deleted from. Draft never left our desk, and a
// summary voided before Void was retired is already released and stamped, so
// neither loses an audit trail anyone relies on. One that has gone up to FTMS
// keeps its record.
const DELETABLE_STAGES = ['Draft', 'Void'];

// Stages still on our side — lines can be added to or removed from these.
const NOT_SENT_STAGES = ['Draft', 'Kicked Back'];

const initials = (name) => String(name || '').trim().split(/\s+/).map((w) => w[0]).join('').toUpperCase().slice(0, 3);

// Hairline border for a badge, mixed from its own text colour toward the
// tint so each chip reads as one object instead of a floating blob — saves
// carrying a third colour on every BADGES entry.
const mixWhite = (hex, amt) => {
    const n = parseInt(String(hex).slice(1), 16);
    const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255]
        .map((v) => Math.round(v + (255 - v) * amt));
    return `#${ch.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
};

const Badge = ({ kind }) => {
    const c = BADGES[kind] || BADGES.Draft;
    return (
        <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: c.bg, borderWidth: 1, borderColor: mixWhite(c.fg, 0.7), borderRadius: 999, paddingTop: 2, paddingBottom: 2, paddingLeft: 6, paddingRight: 8, alignSelf: 'flex-start' }}>
            <View style={{ width: 5, height: 5, borderRadius: 999, backgroundColor: c.fg, marginRight: 5 }} />
            {/* nowrap: a long stage name like Uploaded to FTMS wrapped inside
                the pill turned it into a two-line lozenge. */}
            <Text style={{ fontSize: 10, fontWeight: '700', color: c.fg, letterSpacing: 0.2, whiteSpace: 'nowrap' }}>{kind}</Text>
        </View>
    );
};

// Which of the three invoice groups a summary actually carries. The tags on
// the Summaries row let you tell a labour-only week from a full one without
// opening the row; cost type is the only class marker the frozen snapshot
// keeps, so the group is derived from it the same way the backup groups.
const invGroups = (lines) => {
    const seen = new Set((lines || []).map((l) => groupOf(bucketOf(l.ct), !!l.pt)));
    return GROUPS.filter((g) => seen.has(g));
};

const GROUP_TAG = { Labor: 'Labor', Equipment: 'Equip', 'Third Party': '3rd Party', [PT_GROUP]: 'Pass-Thru' };

// Tiny monochrome PT marker for pass-through lines. `strong` inverts it for a
// per-line override so a forced line reads differently from a rule match.
const PtTag = ({ strong }) => (
    <View style={{ borderWidth: 1, borderColor: strong ? '#0f172a' : '#94a3b8', borderStyle: 'solid', borderRadius: 3, backgroundColor: strong ? '#0f172a' : '#fff', paddingTop: 0, paddingBottom: 0, paddingLeft: 3, paddingRight: 3, marginRight: 4 }}>
        <Text style={{ fontSize: 8, fontWeight: '700', color: strong ? '#fff' : '#475569', letterSpacing: 0.4 }}>PT</Text>
    </View>
);

// Contract tag (billingContract): CT on a line the contract flags for a call
// ('warn' — a tool for the rate sheet list, fuel on a rental invoice, a medic
// the Company has to approve) or bills at cost with no markup ('cost' —
// freight, hauling, affiliates). Hover gives the rule and its clause. Lines
// the contract puts inside the rates aren't tagged — they read as Not
// Billable with the rule as their reason — and plain 'bill' lines carry
// nothing: the contract just agrees they bill.
const CtTag = ({ rule }) => {
    const tone = (EFFECT_TONE[rule.effect] || EFFECT_TONE.warn)[0];
    return (
        <View
            title={`${effectLabel(rule.effect)}: ${rule.label} — ${rule.clause}${rule.note ? `. ${rule.note}` : ''}`}
            style={{ borderWidth: 1, borderColor: tone, borderStyle: 'solid', borderRadius: 3, backgroundColor: '#fff', paddingTop: 0, paddingBottom: 0, paddingLeft: 3, paddingRight: 3, marginRight: 4 }}
        >
            <Text style={{ fontSize: 8, fontWeight: '700', color: tone, letterSpacing: 0.4 }}>{rule.effect === 'cost' ? 'CT 0%' : 'CT'}</Text>
        </View>
    );
};

const GroupTag = ({ g }) => (
    <View style={{ borderWidth: 1, borderColor: '#cbd5e1', borderRadius: 4, backgroundColor: '#f8fafc', paddingTop: 1, paddingBottom: 1, paddingLeft: 5, paddingRight: 5, marginRight: 4, marginTop: 1, marginBottom: 1 }}>
        <Text style={{ fontSize: 9, fontWeight: '700', color: '#475569', letterSpacing: 0.3, whiteSpace: 'nowrap' }}>{GROUP_TAG[g]}</Text>
    </View>
);

// Invoices (the summaries list, view key `invoices`) carries a live count in
// its label, so it is built at render time rather than sitting in here.
const VIEW_LABELS = {
    transactions: 'Third Party',
    // Labor / Equipment / Third Party across every summary the job has
    // issued (billingInvoices.js). Read-only. Off the tab strip for now —
    // the summaries list took over the Invoices name.
    // invview: 'Invoices',
    // One tab covers the daily-sheet tracker and its field ticket /
    // force report deliverables.
    timecards: 'Daily Field Tickets',
    // Hand-entered daily sheet + document drop for days HEET has not landed
    // yet (the first day or two of a job) — see billingManualTicket.js.
    manual: 'Custom Ticket',
    // Straight cost-history roll-up (cost code / cost type / billable) — no
    // markup or invoice math, see billingReport.js.
    report: 'Cost Report',
    // Pick weeks, download the labor and equipment invoice as an Excel
    // workbook priced by formulas over its backup (time card, HeavyJob,
    // JDE payroll, per diem, fuel) — billingInvoiceWorkbook.js.
    workbook: 'Invoice Workbook',
    // Field ticket reconciliation: the HeavyJob values each ticket went out
    // with, against the cards now (HeavyJob Check) and the JDE lines the
    // invoice bills (Invoice Check) — billingReconciliation.js.
    recon: 'Labor & Equipment',
    // Rates & Rules is a collapsible section at the foot of the Invoices
    // view now, not a tab of its own.
    // rates: 'Rates & Rules',
};

const card = {
    backgroundColor: '#ffffff', borderRadius: 6, borderWidth: 1, borderColor: '#e2e8f0',
    paddingTop: 16, paddingBottom: 16, paddingLeft: 18, paddingRight: 18, marginBottom: 16,
};

const th = { fontSize: 11, fontWeight: '700', color: '#475569', paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8 };
const td = { fontSize: 12, color: '#1e293b', paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8 };
// Transactions-grid cell: same as td, tighter vertically so more lines fit.
const gtd = { ...td, paddingTop: 3, paddingBottom: 3 };

// Stat tile: label stacked over value so the strip reads as columns.
// Tiles after the first carry a left divider (first skips it), so
// conditional tiles at the tail never leave a dangling rule.
// `note` is a smaller second line under the figure — a related number
// folded into this stat rather than standing as its own.
// `w` is a minimum content width: give every tile in a strip the same one
// and the columns and dividers land on an even grid (the 19 is the divider
// plus its padding, which the non-first tiles carry).
const Stat = ({ label, value, sub, tone, first, note, noteTone, w }) => (
    <View style={{ marginRight: 18, marginBottom: 2, paddingLeft: first ? 0 : 18, borderLeftWidth: first ? 0 : 1, borderColor: '#e2e8f0', minWidth: w ? w + (first ? 0 : 19) : undefined }}>
        <Text style={{ fontSize: 9, color: '#94a3b8', fontWeight: '700', letterSpacing: 0.6, marginBottom: 2, whiteSpace: 'nowrap' }}>{label}</Text>
        <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
            <Text style={{ fontSize: 15, fontWeight: '700', color: tone || '#0f172a', whiteSpace: 'nowrap' }}>{value}</Text>
            {sub ? <Text style={{ fontSize: 10, color: '#94a3b8', marginLeft: 5, whiteSpace: 'nowrap' }}>{sub}</Text> : null}
        </View>
        {note ? <Text style={{ fontSize: 10, color: noteTone || '#94a3b8', marginTop: 1, whiteSpace: 'nowrap' }}>{note}</Text> : null}
    </View>
);

// Labeled column in the filter bar: group label on top, options stacked
// vertically underneath so the whole bar stays a narrow row of columns.
// A week's (or one person's week's) standing against the client's signed
// week summaries, off billingWeekGroups.signedStanding: every ticketed day
// signed → the Signed tag; part of the week → the count; nothing signed →
// the stage the summary sits at, or a muted note when there is none.
const SigStanding = ({ sig, loading }) => {
    if (!sig || !sig.n) return null;
    if (loading) return <Text style={{ fontSize: 11, color: '#cbd5e1', marginLeft: 8 }}>…</Text>;
    if (sig.stage === 'Signed') return <View style={{ marginLeft: 8 }}><SigTag sig={{ stage: 'Signed', docs: [] }} /></View>;
    if (sig.k) return <Text style={{ fontSize: 11, color: '#92400e', marginLeft: 8 }}>{sig.k} of {sig.n} days signed</Text>;
    if (sig.stage) return <View style={{ marginLeft: 8 }}><SigTag sig={{ stage: sig.stage, docs: [] }} /></View>;
    return <Text style={{ fontSize: 11, color: '#94a3b8', marginLeft: 8 }}>no signed summary</Text>;
};

const FilterGroup = ({ label, last, children }) => (
    <View style={{ marginRight: 16, marginBottom: 6, paddingRight: last ? 0 : 16, borderRightWidth: last ? 0 : 1, borderColor: '#e2e8f0' }}>
        <Text style={{ fontSize: 9, fontWeight: '700', color: '#94a3b8', letterSpacing: 0.6, marginBottom: 4 }}>{label}</Text>
        <View style={{ flexDirection: 'column', alignItems: 'flex-start' }}>{children}</View>
    </View>
);

// One row in a filter column — checkbox glyph + label, no chip borders.
// count (when given) shows how many rows the option matches right now.
const ListOpt = ({ label, active, onPress, count }) => (
    <Pressable onPress={onPress} style={{ flexDirection: 'row', alignItems: 'center', paddingTop: 2, paddingBottom: 2, cursor: 'pointer' }}>
        <View
            style={{
                width: 12, height: 12, borderRadius: 3, borderWidth: 1.5, borderStyle: 'solid',
                marginRight: 6, alignItems: 'center', justifyContent: 'center',
                borderColor: active ? '#0f172a' : '#94a3b8',
                backgroundColor: active ? '#0f172a' : '#fff',
            }}
        >
            {active ? <Text style={{ color: '#fff', fontSize: 8, lineHeight: 1 }}>✓</Text> : null}
        </View>
        <Text style={{ fontSize: 12, color: active ? '#0f172a' : '#475569', fontWeight: active ? '600' : '400', whiteSpace: 'nowrap' }}>{label}</Text>
        {count !== undefined ? (
            <Text style={{ fontSize: 11, color: '#94a3b8', marginLeft: 6 }}>{count}</Text>
        ) : null}
    </Pressable>
);

// Native date input, same pattern as jobInfo's DateInput.
const DateInput = ({ value, onValueChange }) => createElement('input', {
    type: 'date',
    value: value || '',
    onChange: (e) => onValueChange(e.target.value || ''),
    style: { fontSize: 12, padding: 4, borderRadius: 4, border: '1px solid #cbd5e1', marginRight: 8, marginBottom: 6 },
});

const CheckBox = ({ checked, indeterminate, disabled, onPress }) => (
    <Pressable
        onPress={disabled ? undefined : onPress}
        style={{
            // Empty boxes sit on alternating white/#fafafa rows, so the unchecked
            // border carries a slate-600 weight to stay findable at a glance.
            // Pressable's style converter doesn't add borderStyle the way View
            // does, so spell it out — without it the box renders borderless
            // and disappears on white rows.
            width: 15, height: 15, borderRadius: 3, borderWidth: 1.5, borderStyle: 'solid',
            alignItems: 'center', justifyContent: 'center',
            borderColor: disabled ? '#cbd5e1' : checked || indeterminate ? '#0f172a' : '#475569',
            backgroundColor: checked || indeterminate ? '#0f172a' : '#fff',
            cursor: disabled ? 'default' : 'pointer',
        }}
    >
        {checked ? <Text style={{ color: '#fff', fontSize: 10, lineHeight: 1 }}>✓</Text> : null}
        {!checked && indeterminate ? <Text style={{ color: '#fff', fontSize: 10, lineHeight: 1, fontWeight: '700' }}>–</Text> : null}
    </Pressable>
);

// Tiny per-row switch in the BILLABLE column: filled/dark = the line bills,
// hollow = flagged not billable. Lets one line be flipped without the
// select-then-bulk-button trip. A not-billable line's reason rides on the
// hover text.
const NbToggle = ({ billable, disabled, reason, onPress }) => (
    <Pressable
        title={disabled ? 'On a summary — status is locked'
            : billable ? 'Billable — click to flag not billable'
            : `Not billable${reason ? `: ${reason}` : ''} — click to make billable`}
        disabled={disabled}
        onPress={disabled ? undefined : onPress}
        style={{
            width: 26, height: 14, borderRadius: 999, borderWidth: 1.5, borderStyle: 'solid',
            justifyContent: 'center',
            borderColor: billable ? '#0f172a' : '#94a3b8',
            backgroundColor: billable ? '#0f172a' : '#fff',
            opacity: disabled ? 0.35 : 1,
        }}
    >
        <View style={{
            width: 8, height: 8, borderRadius: 999,
            backgroundColor: billable ? '#fff' : '#94a3b8',
            marginLeft: billable ? 14 : 2,
        }} />
    </Pressable>
);

// Fixed column widths — header, body and skeleton all read from here.
const COLS = [
    { key: 'sel', label: '', w: 34 },
    { key: 'date', label: 'WORK DATE', w: 80, sortable: true },
    { key: 'ds', label: 'DESCRIPTION', w: 190 },
    { key: 'cc', label: 'COST CODE', w: 140 },
    { key: 'nbt', label: 'BILLABLE', w: 64 },
    { key: 'ct', label: 'COST TYPE', w: 116 },
    { key: 'hrs', label: 'HOURS', w: 62, right: true, sortable: true },
    // What's still open on the PO / PO line — ordered, not received, so
    // never in COST (poOpenAmt). Headers only; the Third Party grid only.
    { key: 'opn', label: 'OPEN AMOUNT', w: 100, right: true },
    { key: 'cost', label: 'COST', w: 95, right: true, sortable: true },
    // The % on cost-plus lines, the sheet rate on rate-billed ones (mkText).
    { key: 'mk', label: 'MARKUP', w: 90, right: true },
    { key: 'bill', label: 'BILL AMOUNT', w: 100, right: true, sortable: true },
    // What the PO / PO line already has on summaries (tpInvoiced) — a
    // part-billed line keeps its open entries here, so this tracks the rest.
    // The Third Party grid only; an opened invoice leaves it out.
    { key: 'invd', label: 'INVOICED', w: 104, right: true },
    { key: 'eq', label: 'EQUIPMENT', w: 120 },
    // Vendor-invoice match: status, supplier invoice #, and the voucher GL
    // date (receipt date on Received) each get a column so the row stays
    // one line tall. The invoice # links to its TAP PDF.
    { key: 'vinv', label: 'INV STATUS', w: 100 },
    { key: 'vinvNo', label: 'INV #', w: 88 },
    { key: 'vinvDate', label: 'INV DATE', w: 72 },
    { key: 'hist', label: 'HISTORY', w: 80 },
    { key: 'rev', label: 'REVIEW', w: 100 },
    // Where the client's signed week summary covering the work date stands
    // — labor and equipment only (billingTicketDocs.signedByDate).
    { key: 'sig', label: 'SIGNED', w: 72 },
];
const TABLE_W = COLS.reduce((s, c) => s + c.w, 0);
// Column widths by key so rows never depend on COLS ordering.
const W = Object.fromEntries(COLS.map((c) => [c.key, c.w]));

// PO group header: everything left of HOURS collapses into one label cell so
// the group's hours/cost/bill/invoiced totals land under their own columns,
// and everything right of INVOICED is one blank run.
const colsW = (cols) => cols.reduce((s, c) => s + c.w, 0);
const GROUP_LABEL_W = colsW(COLS.slice(1, COLS.findIndex((c) => c.key === 'hrs')));
const GROUP_TAIL_W = colsW(COLS.slice(COLS.findIndex((c) => c.key === 'invd') + 1));

// Lines under a PO sit behind an indent gutter so the group reads as a tree.
// The gutter comes out of the checkbox column rather than pushing the row
// over, so every other column still lines up with the grid header.
const ROW_INDENT_W = 16;

// tpInvoiced entries added together (a vendor header over its POs); null
// when none of them has anything on a summary.
const sumInvd = (list) => list.filter(Boolean).reduce((a, v) => (a
    ? { bill: a.bill + v.bill, cost: a.cost + v.cost, n: a.n + v.n, invs: [...new Set([...a.invs, ...v.invs])] }
    : v), null);

// poOpenAmt key: the PO as text without JDE's leading zeros (not pNum — a
// lettered PO like "PO-1245" would read 0 and collide), plus '|line'.
const openKey = (po, line) => {
    const p = String(po ?? '').trim().replace(/^0+(?=.)/, '');
    return line == null ? p : `${p}|${pNum(line)}`;
};

// poOpenAmt entries added together, the same way; null when none has one.
const sumOpen = (list) => list.filter(Boolean).reduce((a, v) => (a ? { open: a.open + v.open, n: a.n + v.n } : v), null);

// The value every row gives, or undefined when they differ. A PO line
// header fills a column only when its entries agree on it.
const shared = (rows, f) => {
    const v = f(rows[0]);
    return rows.every((t) => f(t) === v) ? v : undefined;
};

// Labor (51xxx) is worked on the Labor & Equipment tab, so none of it sits in
// the Third Party grid or its filter dropdowns.
const isLaborCt = (ct) => String(ct).startsWith('51');

// Hours mean something on labor rows, fleet usage rows (52100) and rental
// usage lines (HeavyJob card hours) — 1 decimal, blank elsewhere. Rental
// vouchers (52200), fuel and repairs (52300/52400) put voucher/unit
// quantities in Total_Hours, so their entries stay blank; a rental PO line
// with no usage lines shows its unit's HeavyJob hours instead (poGroupsOf's
// `rentalHrs`).
const showsHours = (t) => t.cls === 'Labor' || t.ct === '52100' || isRentUsage(t);

// PO lines in line-number order, the "No line #" bucket last.
const byPol = (a, b) => (a.pol === '' ? 1 : 0) - (b.pol === '' ? 1 : 0) || pNum(a.pol) - pNum(b.pol);

// Third Party rows bucketed by PO, keeping the order of each PO's first
// row. PO-less rows — p-card charges and the like — share one "No PO"
// bucket pinned last. `open` is a group's selectable (not yet invoiced)
// lines — the group checkbox and its totals both work off it. The Third
// Party tab groups its filtered rows with it, an opened invoice its
// third-party lines. `rentalHrs` (billingRentalHours.rentalHoursMap) gives
// a rental PO line its unit's HeavyJob hours (ln.hj), summed into the PO's.
const poGroupsOf = (rows, rentalHrs) => {
    const map = new Map();
    rows.forEach((t) => {
        const k = t.po || '';
        let g = map.get(k);
        if (!g) { g = { po: k, rows: [], open: [], vendor: '', cost: 0, bill: 0, hrs: 0 }; map.set(k, g); }
        g.rows.push(t);
        if (!t.inv && !t.nbInv) g.open.push(t);
        g.cost += t.cost;
        if (!t.nb) g.bill += t.bill;
        if (showsHours(t)) g.hrs += t.hrs;
        if (!g.vendor && t.who) g.vendor = t.who;
    });
    // A real PO splits once more by PO line (Line 1, Line 2 …), each
    // summed the same way, so an opened PO reads line by line and a line
    // opens to its JDE entries. Line status (vendor invoice) is per PO
    // line already, so the line header carries it.
    map.forEach((g) => {
        if (!g.po || !g.rows.some((t) => t.pol)) return;
        const lines = new Map();
        g.rows.forEach((t) => {
            const pol = t.pol || '';
            let ln = lines.get(pol);
            if (!ln) {
                ln = { key: `${g.po}|${pol}`, pol, rows: [], open: [], cost: 0, bill: 0, hrs: 0, ds: t.ds, vinv: t.vinv, vinvNo: t.vinvNo };
                lines.set(pol, ln);
            }
            ln.rows.push(t);
            if (!t.inv && !t.nbInv) ln.open.push(t);
            ln.cost += t.cost;
            if (!t.nb) ln.bill += t.bill;
            if (showsHours(t)) ln.hrs += t.hrs;
        });
        g.lines = [...lines.values()].sort(byPol);
        if (rentalHrs) {
            g.lines.forEach((ln) => {
                const hj = ln.pol && !ln.rows.some(showsHours) ? rentalHrs[rentalLineKey(g.po, ln.pol)] : null;
                if (!hj || !hj.hrs) return;
                ln.hj = hj;
                ln.hrs = hj.hrs;
                g.hrs += hj.hrs;
            });
        }
    });
    // The PO header says what the PO is mostly for: the description of
    // its largest line by cost (its largest row when it has no lines).
    map.forEach((g) => {
        if (!g.po) return;
        const units = g.lines || g.rows;
        const top = units.reduce((a, b) => (b.cost > a.cost ? b : a), units[0]);
        g.topDs = top.ds || '';
        g.moreDs = units.filter((u) => u !== top && u.ds).length;
    });
    return [...map.values()].sort((a, b) => (a.po ? 0 : 1) - (b.po ? 0 : 1));
};

// "9/12/26" -> "9/12", for a rolled week row's date span.
const shortDate = (d) => fDate(d).replace(/\/\d+$/, '');
const hoursText = (t) => (showsHours(t) && t.hrs ? (Math.round(t.hrs * 10) / 10).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '—');

// Billing rate for the RATE column: the rate sheet tier rate on rate-billed
// equipment lines, the mapped ST/OT labor rate on labor lines (context —
// their bill still computes off cost × markup). '' where no rate applies.
const rateText = (t) => (t.rateAmt ? `${money(t.rateAmt)}/${t.rateUom || 'hr'}` : '');

// MARKUP cell: the % on cost-plus lines, the sheet rate on rate-billed ones,
// and the No Rate flag ('no hours' when the rate is known but hours aren't).
const mkText = (t) => (t.nb ? '—'
    : t.noRate ? (t.rateAmt ? 'no hours' : 'no rate')
    : t.hourly ? rateText(t) || 'rate'
    : t.pt && !t.mk ? 'at cost' : `+${t.mk}%`);
const isNoRateText = (s) => s === 'no rate' || s === 'no hours';

// No Billed chip: lines on a summary leave the Third Party grid (tpTxns).
const STATUS_CHIPS = ['All', 'Unbilled', 'No Rate', 'Not Billable'];

// Cost codes that never belong on an invoice. Their rows stay visible so the

const Billing = () => {
    const [selectedJob] = useAtom(selectedJobAtom);
    const [selectedJobData] = useAtom(selectedJobDataAtom);
    const [jobCostDetail] = useAtom(jobCostDetailAtom);
    const [jobBillings] = useAtom(jobBillingsAtom);
    // OPEN AMOUNT column: each PO line's open balance off the job's open
    // commitments (v_Form_Open_PO_Items, as the cost basis popup reads
    // them), keyed openKey(po, line); each PO's under openKey(po).
    const [openCommitments] = useAtom(jobOpenCommitmentsAtom);
    const poOpenAmt = useMemo(() => {
        const m = {};
        const add = (k, amt) => { const v = m[k] || { open: 0, n: 0 }; v.open += amt; v.n += 1; m[k] = v; };
        (Array.isArray(openCommitments) ? openCommitments : []).forEach((o) => {
            if (!o || !openKey(o.PO_Number)) return;
            const amt = ocOpen(o);
            if (o.PO_Line_Number != null) add(openKey(o.PO_Number, o.PO_Line_Number), amt);
            add(openKey(o.PO_Number), amt);
        });
        return m;
    }, [openCommitments]);
    // { job, rows, error }: the selected job's Billing rows at BILLING_VERSION.
    // Loaded here, not with the version-scoped jobUserData - see the store.
    const [billingData, setBillingData] = useAtom(billingUserDataAtom);
    const [loadingCost] = useAtom(loadingCostAtom);
    const billingLoaded = !!billingData && billingData.job === selectedJob && Array.isArray(billingData.rows);
    const billingLoadError = !!billingData && billingData.job === selectedJob && !!billingData.error;
    // Nothing writes until the job's rows are in: a save before the load
    // lands would number a summary off the default counter or overwrite a
    // settings blob it never read. Never tied to the forecast version lock -
    // billing is job-level, and a version cut while the tab was open used to
    // flip the lock and silently drop the next save.
    const versionLocked = !billingLoaded;
    useEffect(() => {
        if (!selectedJob) return undefined;
        if (billingData && billingData.job === selectedJob && (Array.isArray(billingData.rows) || billingData.error)) return undefined;
        let stale = false;
        loadUserData({ id: selectedJob, forecastGuid: BILLING_VERSION, live: true })
            .then((res) => {
                if (stale) return;
                if (!res || res.error) throw new Error(res?.error || 'no response');
                const rows = (Array.isArray(res.data) ? res.data : []).filter((x) => x?.Forecast_Tab === 'Billing');
                setBillingData({ job: selectedJob, rows, error: null });
            })
            .catch((e) => {
                if (stale) return;
                setBillingData({ job: selectedJob, rows: null, error: String(e?.message || e || 'load failed') });
            });
        return () => { stale = true; };
    }, [selectedJob]); // only the job: billingData is read, not tracked, so a save's mirror can't refetch
    const [userId] = useAtom(userIdAtom);
    const [userName] = useAtom(userNameAtom);
    // Stamped on signature envelopes as the Strike-side sender, so a client
    // reply-all reaches the person who actually sent the ticket.
    const [userEmail] = useAtom(userEmailAtom);

    // The FTMS hand-off drives a live client-invoicing system through the
    // browser extension, and the wizard has never been run end-to-end against
    // a real invoice. Until it has, keep the button off the Summaries rows so
    // nobody pushes a half-proven invoice at a client: it shows on dev/test
    // builds and for Seth, and nowhere else. Widen (or drop) this when the
    // hand-off is proven — nothing else about the flow is gated.
    const canPushFtms = process.env.NODE_ENV !== 'production' || userId === '154045';

    // Filters and view picks survive a refresh (usePersistedState). The ones
    // that name the job's own data — search, cost codes, cost types, vendors,
    // POs, work dates — save per job, so one job's picks never empty
    // another job's grid.
    const jobPref = (name) => `billing.${selectedJob}.${name}`;

    const [viewMode, setViewMode] = usePersistedState('billing.viewMode', 'recon');
    // Days picked on the Reconciliation view, handed to Daily Field Tickets'
    // Ticket Map to open as a Draft: { kind, cells, nonce }.
    const [tcPrefill, setTcPrefill] = useState(null);
    // Weeks picked on the Reconciliation view, handed to Invoice Workbook to
    // pick and build the invoice folder: { weeks, build, nonce }.
    const [wbPrefill, setWbPrefill] = useState(null);
    // Rates & Rules sits collapsed at the foot of the Invoices view.
    const [ratesOpen, setRatesOpen] = useState(false);
    const [sel, setSel] = useState({});
    // Grouped view: one collapsible header per PO, ticking the header ticks
    // every open line under it. Off falls back to the flat transaction list.
    // Grid grouping: by PO (third-party cost), by week ending → person / unit
    // (labor and equipment — billingWeekGroups.js), or flat. `groupPo` is
    // simply "grouped at all" for the shared plumbing (indent, group
    // checkboxes, the group budget).
    const [groupMode, setGroupMode] = usePersistedState('billing.groupMode', 'po');
    const groupPo = groupMode !== '';
    const groupWeek = groupMode === 'week';
    const groupVendor = groupMode === 'vendor';
    // Week grouping: weeks start open with their people collapsed. Vendor
    // headers share the map but start closed — one summary row per vendor
    // until opened (false = opened).
    const [weekClosed, setWeekClosed] = useState({});
    const hdrClosed = (w) => (w.vendorHdr ? weekClosed[w.we] !== false : !!weekClosed[w.we]);
    const [poOpen, setPoOpen] = useState({}); // PO ('' = no PO) -> expanded
    // `${po}|${line}` -> PO line expanded to its entries (Group by PO).
    const [lineOpen, setLineOpen] = useState({});
    // Third Party sub-view: 'lines' (the cost grid) | 'chase' (invoice chase).
    const [tpView, setTpView] = usePersistedState('billing.tpView', 'lines');
    // Rolled week rows (foldWeekLabor) opened to show their days, by row key.
    const [foldOpen, setFoldOpen] = useState({});
    const [search, setSearch] = usePersistedState(jobPref('search'), '');
    // ccFilter holds the ticked cost codes; ccExclude says whether those are
    // the only ones shown or the only ones hidden.
    const [ccFilter, setCcFilter] = usePersistedState(jobPref('cc'), []);
    const [ccExclude, setCcExclude] = usePersistedState(jobPref('ccExclude'), false);
    const [ctFilter, setCtFilter] = usePersistedState(jobPref('ct'), []);
    const [ctExclude, setCtExclude] = usePersistedState(jobPref('ctExclude'), false);
    const [pcardOnly, setPcardOnly] = usePersistedState('billing.pcardOnly', false);
    const [ptOnly, setPtOnly] = usePersistedState('billing.ptOnly', false);
    const [classFilter, setClassFilter] = usePersistedState('billing.classFilter', []);
    const [whoFilter, setWhoFilter] = usePersistedState(jobPref('who'), []);
    const [poFilter, setPoFilter] = usePersistedState(jobPref('po'), []);
    const [statusFilter, setStatusFilter] = usePersistedState('billing.statusFilter', 'All', (v) => STATUS_CHIPS.includes(v));
    // The tab opens on the worklist: lines nobody has reviewed yet. Billed
    // lines count as reviewed, so this also hides everything already invoiced.
    const [reviewFilter, setReviewFilter] = usePersistedState('billing.reviewFilter', 'Not Reviewed');
    // 'All' | 'Signed' | 'Not signed' — against the signed week summaries.
    const [sigFilter, setSigFilter] = usePersistedState('billing.sigFilter', 'All');
    const [dateFrom, setDateFrom] = usePersistedState(jobPref('dateFrom'), '');
    const [dateTo, setDateTo] = usePersistedState(jobPref('dateTo'), '');
    const [sortKey, setSortKey] = usePersistedState('billing.sortKey', 'date');
    const [sortDir, setSortDir] = usePersistedState('billing.sortDir', 'desc');
    const [showAll, setShowAll] = useState(false);
    // Selection modal: false, 'create' (new invoice) or 'add' (onto an
    // existing one — addTarget is the invoice number picked in it).
    const [showModal, setShowModal] = useState(false);
    const [addTarget, setAddTarget] = useState('');
    const [openInvoice, setOpenInvoice] = useState(null);
    // An opened invoice's Third Party grid: POs and PO lines clicked shut
    // (both start open — a summary's list is short, and its line fixes sit
    // on the lines), by `${summary}|g|${po}` / `${summary}|l|${line key}`.
    const [invOpen, setInvOpen] = useState({});
    const [histLine, setHistLine] = useState(null);
    // { title, rows, po, pol } — the COST figure whose basis is open.
    const [costBasis, setCostBasis] = useState(null);
    const [nbPrompt, setNbPrompt] = useState(false);
    const [lcPrompt, setLcPrompt] = useState(null); // { t, inv? } — labor line awaiting a classification pick
    const [lcApplyAll, setLcApplyAll] = useState(false);
    const [nbReason, setNbReason] = useState('');
    const [delPrompt, setDelPrompt] = useState(null); // summary awaiting delete confirmation
    const [exportCheck, setExportCheck] = useState(null); // { rows, tag } — summary PDF awaiting pre-export validation
    // Summary receiving lines via the Add Lines picker (billingAddLines.js —
    // the picker owns its own selection and filters).
    const [addTo, setAddTo] = useState(null);
    // The job's DocuSign envelopes — the SIGNED column, the week headers, the
    // Invoices list's STATUS and the Add Lines picker's tags read them.
    // Fetched with the job, and again
    // each time the picker opens: a signature can land between two opens.
    const [jobSigs, setJobSigs] = useState({ job: null, loading: false, list: [], consoleUrlBase: '' });
    const loadSigs = (job) => {
        setJobSigs((p) => ({ job, loading: true, list: p.job === job ? p.list : [], consoleUrlBase: p.consoleUrlBase || '' }));
        Promise.resolve()
            .then(() => loadBillingSignatures({ job: String(job) }))
            .then((res) => setJobSigs((p) => (p.job === job ? { job, loading: false, list: res?.envelopes || [], consoleUrlBase: String(res?.consoleUrlBase || '') } : p)))
            // No envelopes just means no tags — the grid and picker still work.
            .catch(() => setJobSigs((p) => (p.job === job ? { job, loading: false, list: [] } : p)));
    };
    useEffect(() => {
        if (selectedJob) loadSigs(selectedJob);
    }, [selectedJob]);
    const [noteDraft, setNoteDraft] = useState('');
    // Inline scanned-invoice viewer (shared with the third-party tabs).
    const { openTapDoc, viewerNode } = useTapInvoiceViewer();
    // A vendor invoice # that opens its TAP PDF in the viewer (a real link, so
    // middle-click still opens the proxied PDF in a tab).
    const invNoLink = (pdf, label, title) => createElement('a', {
        href: tapProxyUrl(pdf) || pdf,
        target: '_blank',
        rel: 'noopener noreferrer',
        title: 'Open the invoice PDF',
        onClick: (ev) => {
            ev.stopPropagation();
            ev.preventDefault();
            openTapDoc(pdf, title);
        },
        style: {
            fontSize: 12, color: '#1e40af', fontWeight: 600, textDecoration: 'none',
            display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        },
    }, label);

    // The job's SharePoint timecard files, cached per job. Filled lazily by
    // jobTimecardFiles() the first time a backup PDF or an FTMS push needs the
    // crew's scans as evidence.
    const [tcCat, setTcCat] = useState({ job: null, loading: false, data: null });

    // The job's JDE receiving-doc manifest (F00165 coordinates per PO), cached
    // per job the same way. Filled lazily when a backup bundle builds.
    const [moCat, setMoCat] = useState({ job: null, docs: null });

    const toggleIn = (list, setList, value) =>
        setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

    // ── Live vendor-invoice match status per PO line ──
    // Keyed `${PO_Number}|${PO_Line_Number}`; matches cost history rows on
    // (PO_Number, Line_ID). Always live — invoice status keeps moving after a
    // WIP version is cut, so it never comes from the PCV snapshot.
    const [poStatus, setPoStatus] = useState({});
    useEffect(() => {
        let stale = false;
        setPoStatus({});
        if (!selectedJob) return undefined;
        loadPOMatchStatus({ id: selectedJob }).then((res) => {
            if (stale) return;
            setPoStatus(poStatusMap(res?.data));
        }).catch(() => { /* status column just stays empty */ });
        return () => { stale = true; };
    }, [selectedJob]);

    // ── Stored billing data ──
    // The Rates & Rules view parses its own fieldNames (rate/rule/rateset)
    // out of the same tab's rows — see billingRates.js.
    const billingUserRows = useMemo(
        () => (billingLoaded ? billingData.rows : []).filter((x) => x?.Forecast_Tab === 'Billing'),
        [billingData, billingLoaded],
    );
    const { billed, nbLines, invoices, settings, metas } = useMemo(() => parseBillingRows(billingUserRows, selectedJob), [billingUserRows, selectedJob]);
    // Work date -> where the client's signed week summary covering it stands
    // (billingTicketDocs.signedByDate). Only labor and equipment ride tickets.
    const signedBy = useMemo(
        () => signedByDate(parseDocs(billingUserRows), envelopesByDoc(jobSigs.job === selectedJob ? jobSigs.list : [])),
        [billingUserRows, jobSigs, selectedJob],
    );
    const sigOf = (t) => (sigTicketed(t) ? signedBy[t.date] || null : null);
    const ticketDocs = useMemo(() => parseDocs(billingUserRows), [billingUserRows]);
    // The job's invoice drafts (billingInvoiceDrafts.js), so Labor & Equipment can say a week already has one.
    const invoiceDrafts = useMemo(() => parseInvoiceDrafts(billingUserRows), [billingUserRows]);
    useEffect(() => { setTcPrefill(null); setWbPrefill(null); }, [selectedJob]);
    // The SIGNED facet: Signed = under a signed summary; Not signed = a
    // ticketed line that isn't (third-party cost matches neither).
    const sigIs = (t, want) => (want === 'Signed' ? sigOf(t)?.stage === 'Signed' : sigTicketed(t) && sigOf(t)?.stage !== 'Signed');
    // the Rates & Rules view's JDE → rate sheet classification map.
    const jdeLaborClasses = useMemo(() => jdeLaborClassesOf(jobCostDetail), [jobCostDetail]);
    // ── HeavyJob usage of rental units on this job's cards ──
    // Rentals bill hours × the rate sheet rate off it (billingRentalUsage),
    // plus which of this job's own rental units log hours on another job's
    // cards (they bill there). null until it loads — or when the function
    // isn't there / the read fails — which keeps the rental vouchers on
    // cost + %.
    const rentPoKey = useMemo(() => rentalPosOf(jobCostDetail).join(','), [jobCostDetail]);
    const [rentUse, setRentUse] = useState({ key: '', data: null, elsewhere: [] });
    useEffect(() => {
        const key = `${selectedJob}|${rentPoKey}`;
        if (!selectedJob) { setRentUse({ key: '', data: null, elsewhere: [] }); return undefined; }
        let dead = false;
        Promise.resolve()
            .then(() => loadRentalUsage({ job: String(selectedJob), pos: rentPoKey ? rentPoKey.split(',') : [] }))
            .then((res) => { if (!dead) setRentUse({ key, data: rentalUsageOf(res), elsewhere: rentalElsewhereOf(res) }); })
            .catch(() => { if (!dead) setRentUse({ key, data: null, elsewhere: [] }); });
        return () => { dead = true; };
    }, [selectedJob, rentPoKey]);
    const rentLoaded = rentUse.key === `${selectedJob}|${rentPoKey}`;
    const rentalUsage = rentLoaded ? rentUse.data : null;
    const rentalElsewhere = rentLoaded ? rentUse.elsewhere : NO_RENT_ELSEWHERE;
    // Whether rentals bill on that usage and from which work date — the
    // Rates & Rules rental card shows it; priceCostRows derives the same.
    const rentBasis = useMemo(() => rentUsageFrom(settings, billed, rentalUsage), [settings, billed, rentalUsage]);
    // Distinct JDE equipment tiers on the job's fleet (52100) and rental
    // (52200) rows, plus the tiers its rental usage runs under, for the
    // Rates & Rules view's tier → rate sheet map — see billingPricing's
    // jobEquipTiersOf for the fleet/rental rules.
    const jobEquipTiers = useMemo(() => jobEquipTiersOf(jobCostDetail, rentalUsage), [jobCostDetail, rentalUsage]);

    // Tier code → the rate sheet equipment row it bills at (billingPricing's
    // equipBillsOf: saved map, else the Rates & Rules auto-suggestion).
    const equipBills = useMemo(() => equipBillsOf(billingUserRows, jobEquipTiers), [billingUserRows, jobEquipTiers]);
    // (unit, description) => the rate sheet item that unit prints under on
    // the client's tickets and week summaries — the same tier map the lines
    // above price with, so a ticket and its invoice name a unit alike. A unit
    // named off its description takes the job's Service Order swap (PB4's
    // UTVs are its "ATV - 4 Wheel").
    const equipName = useMemo(() => equipNamer({
        unitTiers: unitTiersOf(jobCostDetail, rentalUsage),
        equipBills,
        equipRows: mergeRateSchedule(billingUserRows).equip,
        equipAs: equipRowAs(selectedJob),
    }), [jobCostDetail, rentalUsage, equipBills, billingUserRows, selectedJob]);

    // The field's labor-classification mapping: the newest Multi Phase Field
    // Ticket workbook's Staff List on the job's SharePoint (02.06), refreshed
    // on every job switch so the Rates page and the ticket views track what
    // the field currently bills each person as.
    const [fieldMap, setFieldMap] = useState(null);
    useEffect(() => {
        setFieldMap(null);
        if (!selectedJob) return undefined;
        let dead = false;
        loadFieldTicketMap({ job: String(selectedJob) })
            .then((res) => { if (!dead) setFieldMap(res && res.found ? res : null); })
            .catch(() => { if (!dead) setFieldMap(null); });
        return () => { dead = true; };
    }, [selectedJob]);
    // Staff ID → Tier 4 classification, the per-person override ticketFrom
    // applies ahead of the pay-class map.
    const fieldStaffCls = useMemo(() => {
        const m = {};
        (fieldMap?.rows || []).forEach((r) => { if (r.id && r.tier4) m[String(r.id)] = r.tier4; });
        return m;
    }, [fieldMap]);
    const fieldClassSug = useMemo(() => fieldSuggestions(fieldMap?.rows), [fieldMap]);

    // The Attendance & Force Report roster: each person's own billing
    // classification, which prices their lines ahead of the class-wide map
    // (billingPricing's personClsOf). Missing roster = no per-person map.
    const [roster, setRoster] = useState(null);
    useEffect(() => {
        setRoster(null);
        if (!selectedJob) return undefined;
        let dead = false;
        loadAttendanceRoster({ job: String(selectedJob) })
            .then((res) => { if (!dead) setRoster(res && res.found && Array.isArray(res.roster) ? res.roster : []); })
            .catch(() => { if (!dead) setRoster([]); });
        return () => { dead = true; };
    }, [selectedJob]);

    // JDE classification → the rate sheet classification it bills as (plus the
    // sheet's ST/OT rates) — billingPricing's billsAsOf: saved map, then the
    // field workbook's own mapping, then the Rates & Rules auto-suggestion.
    const billsAs = useMemo(() => billsAsOf(billingUserRows, jdeLaborClasses, fieldClassSug), [billingUserRows, jdeLaborClasses, fieldClassSug]);
    // wrong craft) bills as the class picked on the line instead of what the
    // Rate sheet labor classes by name, for the per-line classification fix
    // (billingPricing's sheetLaborOf).
    const sheetLabor = useMemo(() => sheetLaborOf(billingUserRows), [billingUserRows]);
    // The job's wage multiplier and OT factor — labor bills at the person's
    // JDE wage × these (billingPricing's laborRateFor), never the sheet wage.
    const rateCfg = useMemo(() => rateCfgOf(billingUserRows), [billingUserRows]);
    const personCls = useMemo(() => personClsOf(roster, sheetLabor), [roster, sheetLabor]);
    // disagree about what a person bills as.
    const laborRatemap = useMemo(() => parseLaborMap(billingUserRows), [billingUserRows]);

    // Are the contract's billability rules live on this job? On for the
    // VoltaGrid jobs unless Rates & Rules pinned the toggle either way.
    const contractRules = useMemo(() => contractOn(settings, selectedJob), [settings, selectedJob]);

    // The job's fuel surcharge index rows and fuel settings — what billingFuel
    // prices the equipment fuel surcharge off.
    const fuelCtx = useMemo(() => fuelCtxOf(billingUserRows, settings), [billingUserRows, settings.fuel]);

    // ── Transactions from live cost history ──
    // Priced by billingPricing's priceCostRows — the same pass the Overview
    // VoltaGrid tab reads its revenue off, so the two never disagree.
    const txns = useMemo(() => priceCostRows(jobCostDetail, { billed, nbLines, metas, settings, poStatus, billsAs, equipBills, sheetLabor, personCls, rateCfg, contractRules, contractJob: selectedJob, rentalUsage, rentalElsewhere }),
        [jobCostDetail, billed, nbLines, metas, settings, poStatus, billsAs, equipBills, sheetLabor, personCls, rateCfg, contractRules, selectedJob, rentalUsage, rentalElsewhere]);
    // txns — nothing about them is reviewable or invoiceable — but the Cost
    // Report is the cost side of the job, and without them its total reads
    // short of the Forecast tab's. They ride along there only, always Not
    // Billable. No hours: burden rows repeat the hours of the ST/OT rows they
    // burden, and counting them twice would inflate the report's hour total.
    const neverBilledTxns = useMemo(() => (jobCostDetail || [])
        .filter((r) => {
            const c = String(r?.Cost_Type_Code || '');
            return NEVER_BILLED_CT_PREFIX.some((p) => c.startsWith(p)) && pNum(r.Job_Cost) !== 0;
        })
        .map((r, i) => {
            const glDate = r.GL_Date ? moment.utc(r.GL_Date).format('YYYY-MM-DD') : '';
            return {
                key: 'nvb_' + i,
                cls: bucketOf(r.Cost_Type_Code),
                cost: pNum(r.Job_Cost),
                date: r.Service_Date ? moment.utc(r.Service_Date).format('YYYY-MM-DD') : glDate,
                nb: true, autoNb: true, nbReason: 'Never-billed cost type', neverBilled: true,
                hrs: 0, bill: 0, inv: null, cr: null,
                cc: r.Phase_Code || '',
                ccDesc: r.Phase_Code_Description || '',
                sid: r.Staff_ID ? String(r.Staff_ID) : '',
                who: r.Staff_First_Last_Name || r.Supplier_Name || '',
                eq: '', po: r.PO_Number || '',
                ds: r.Remark_Description || '',
                ct: r.Cost_Type_Code || '',
                ctDesc: r.Cost_Type_Code_Description || r.Cost_Type_Description || '',
            };
        }), [jobCostDetail]);
    const reportTxns = useMemo(() => [...txns, ...neverBilledTxns], [txns, neverBilledTxns]);

    // ── HeavyJob hours on rental PO lines ──
    // JDE's rental rows carry only the voucher quantity, so a rental line's
    // HOURS comes from the HeavyJob cards its unit (PO-<po>-<line>) is on —
    // billingRentalHours.js. Read once per job and set of rental POs; a
    // missing function or a failed read just leaves HOURS blank.
    const rentalPoKey = useMemo(() => rentalPos(txns).join(','), [txns]);
    const [rentalHrs, setRentalHrs] = useState({ key: '', map: null });
    useEffect(() => {
        const key = `${selectedJob}|${rentalPoKey}`;
        if (!selectedJob || !rentalPoKey) { setRentalHrs({ key: '', map: null }); return undefined; }
        if (rentalHrs.key === key) return undefined;
        let dead = false;
        Promise.resolve()
            .then(() => loadRentalHours({ pos: rentalPoKey.split(',') }))
            .then((res) => { if (!dead) setRentalHrs({ key, map: res && !res.error ? rentalHoursMap(res.data) : null }); })
            .catch(() => { if (!dead) setRentalHrs({ key, map: null }); });
        return () => { dead = true; };
    }, [selectedJob, rentalPoKey]);
    const rentalHrsMap = rentalHrs.key === `${selectedJob}|${rentalPoKey}` ? rentalHrs.map : null;

    const statusOf =(t) => (t.inv ? 'Billed' : t.nb ? 'Not Billable' : t.noRate ? 'No Rate' : 'Unbilled');
    // Lines on a summary were reviewed on their way in, so they count as
    // Reviewed here even without an explicit review stamp.
    const reviewOf = (t) => (t.inv ? 'Reviewed' : t.rs || 'Not Reviewed');

    // TAP media-object link per cost-history line, for invoice detail rows.
    const pdfByKey = useMemo(() => {
        const m = {};
        txns.forEach((t) => { if (t.pdf) m[t.key] = t.pdf; });
        return m;
    }, [txns]);

    // Each billed line's amount as its summary froze it, by cost-history
    // key — the INVOICED figures on Third Party's two views.
    const invAmtByKey = useMemo(() => {
        const m = {};
        Object.entries(billed).forEach(([key, rec]) => {
            const s = rec.inv && rec.snap;
            if (s) m[key] = s.b != null ? pNum(s.b) : pNum(s.c) * (1 + pNum(s.m) / 100);
        });
        return m;
    }, [billed]);

    // The third-party vendor invoices the next client invoice waits on, for
    // the Daily Field Tickets billing header: PO cost received with no vendor
    // invoice vouchered yet (waiting), and vouchered cost not on a client
    // invoice (ready to bill). Same rows as Invoice Chase; summarized here so
    // the header never recomputes the PO fold on its own.
    const thirdPartyOutlook = useMemo(() => {
        const rows = chaseRows({ poStatus, txns, openCommitments, today: moment().format('YYYY-MM-DD'), invAmtByKey })
            .filter((r) => r.matched && Math.abs(r.unbilled) >= 0.005);
        const fold = (xs, dateOf, keyOf) => {
            const keys = new Set();
            let amt = 0; let oldest = '';
            xs.forEach((r) => { keys.add(keyOf(r)); amt += r.unbilled; const d = dateOf(r); if (d && (!oldest || d < oldest)) oldest = d; });
            return { n: keys.size, amt, oldest };
        };
        return {
            waiting: fold(rows.filter((r) => !r.vouchered), (r) => r.firstRecv, (r) => r.key),
            ready: fold(rows.filter((r) => r.vouchered), (r) => r.invDate, (r) => `${r.vendor}|${r.invNo}`),
        };
    }, [poStatus, txns, openCommitments, invAmtByKey]);

    // Invoice totals from the frozen line snapshots (source of truth for
    // issued paper — live cost rows can lag or reload, snapshots don't move).
    const invTotals = useMemo(() => {
        const map = {};
        Object.entries(billed).forEach(([key, rec]) => {
            if (!rec.inv || !rec.snap) return;
            const t = map[rec.inv] || (map[rec.inv] = { lines: [], hrs: 0, cost: 0, total: 0 });
            const c = pNum(rec.snap.c);
            // Carry the cost-history key so the invoice detail can look up the
            // live TAP invoice link — the URL never goes in the frozen snapshot
            // (Notes blob is capped at 1024 chars).
            t.lines.push({ ...rec.snap, key });
            t.hrs += pNum(rec.snap.hr);
            t.cost += c;
            // b overrides the cost×markup math for hours-billed rental lines.
            t.total += rec.snap.b != null ? pNum(rec.snap.b) : c * (1 + pNum(rec.snap.m) / 100);
        });
        Object.values(map).forEach((t) => {
            t.lines.sort((a, b) => String(a.d).localeCompare(String(b.d)));
            // The fuel surcharge on the equipment lines bills on top of them:
            // lineTotal is the lines alone, total is what the summary bills.
            t.fuel = fuelSurcharge(t.lines.map((l) => ({
                cls: bucketOf(l.ct), ct: l.ct, date: l.d, fs: l.fs, pt: !!l.pt,
                // fu: 0 froze "burns no fuel"; anything else stays in the base.
                fu: l.fu === 0 ? false : undefined,
                bill: l.b != null ? pNum(l.b) : pNum(l.c) * (1 + pNum(l.m) / 100),
            })), fuelCtx);
            t.lineTotal = t.total;
            t.total += t.fuel.amt;
        });
        return map;
    }, [billed, fuelCtx]);

    // ── Filters ──
    // Everything the Third Party grid can show: the job's transactions less
    // labor and less the lines already on a summary (drafts included) — those
    // live on Invoices now. tpBilled is how many that leaves out.
    const { tpTxns, tpBilled } = useMemo(() => {
        const rows = txns.filter((t) => !isLaborCt(t.ct));
        const open = rows.filter((t) => !t.inv);
        return { tpTxns: open, tpBilled: rows.length - open.length };
    }, [txns]);
    // What the summaries (drafts included) already bill per PO ('p:<po>')
    // and PO line ('l:<po>|<line>'), off the frozen line snapshots — a PO
    // part-billed keeps its open entries on the grid, and the INVOICED
    // column says how much of it has gone out and on which summaries.
    const tpInvoiced = useMemo(() => {
        const m = {};
        const add = (k, t) => {
            const e = m[k] || (m[k] = { bill: 0, cost: 0, n: 0, invs: [] });
            e.bill += invAmtByKey[t.key] ?? t.bill;
            e.cost += t.cost;
            e.n += 1;
            if (!e.invs.includes(t.inv)) e.invs.push(t.inv);
        };
        txns.forEach((t) => {
            if (!t.inv || !t.po || isLaborCt(t.ct)) return;
            add(`p:${t.po}`, t);
            add(`l:${t.po}|${t.pol || ''}`, t);
        });
        return m;
    }, [txns, invAmtByKey]);
    const filterOptions = useMemo(() => {
        const tally = (pick) => {
            const map = new Map();
            tpTxns.forEach((t) => {
                const v = pick(t);
                if (!v) return;
                map.set(v, (map.get(v) || 0) + 1);
            });
            return [...map.entries()].map(([value, count]) => ({ value, count })).sort((a, b) => b.count - a.count);
        };
        // Cost codes carry their phase description so the dropdown can show
        // "03-3100 — Structural" instead of the bare code.
        const descByCode = {};
        const descByType = {};
        const descByPo = {};
        tpTxns.forEach((t) => {
            if (t.cc && t.ccDesc && !descByCode[t.cc]) descByCode[t.cc] = t.ccDesc;
            if (t.ct && t.ctDesc && !descByType[t.ct]) descByType[t.ct] = t.ctDesc;
            if (t.po && t.who && !descByPo[t.po]) descByPo[t.po] = t.who;
        });
        // Fleet equipment (52100) is worked on the Labor & Equipment tab too,
        // so the Third Party cost type dropdown leaves it out.
        return {
            costCodes: tally((t) => t.cc).map((o) => ({ ...o, desc: descByCode[o.value] || '' })),
            costTypes: tally((t) => t.ct).filter((o) => o.value !== '52100')
                .map((o) => ({ ...o, desc: descByType[o.value] || '' }))
                .sort((a, b) => String(a.value).localeCompare(String(b.value))),
            people: tally((t) => t.who),
            pos: tally((t) => t.po).map((o) => ({ ...o, desc: descByPo[o.value] || '' })),
        };
    }, [tpTxns]);

    // Rows passing everything except the status/review facets — each facet's
    // option counts come from here with the *other* facet applied, so a count
    // always says "how many rows if I click this".
    const preFacet = useMemo(() => {
        const q = search.trim().toLowerCase();
        const ok = (t) =>
            (!q || [t.cc, t.ccDesc, t.who, t.eq, t.eqDs, t.po, t.ds, t.inv || '', t.vinv, t.vinvNo].join(' ').toLowerCase().includes(q))
            && (!ccFilter.length || (ccExclude ? !ccFilter.includes(t.cc) : ccFilter.includes(t.cc)))
            && (!ctFilter.length || (ctExclude ? !ctFilter.includes(t.ct) : ctFilter.includes(t.ct)))
            && (!pcardOnly || t.pcard)
            && (!ptOnly || t.pt)
            && (!classFilter.length || classFilter.includes(t.cls))
            && (!whoFilter.length || whoFilter.includes(t.who))
            && (!poFilter.length || poFilter.includes(t.po))
            && (!dateFrom || t.date >= dateFrom)
            && (!dateTo || t.date <= dateTo);
        return tpTxns.filter(ok);
    }, [tpTxns, search, ccFilter, ccExclude, ctFilter, ctExclude, pcardOnly, ptOnly, classFilter, whoFilter, poFilter, dateFrom, dateTo]);

    const statusCounts = useMemo(() => {
        const m = {};
        preFacet.forEach((t) => {
            if (reviewFilter !== 'All' && reviewOf(t) !== reviewFilter) return;
            if (sigFilter !== 'All' && !sigIs(t, sigFilter)) return;
            const s = statusOf(t);
            m[s] = (m[s] || 0) + 1;
            m.All = (m.All || 0) + 1;
        });
        return m;
    }, [preFacet, reviewFilter, sigFilter, signedBy]);

    const reviewCounts = useMemo(() => {
        const m = {};
        preFacet.forEach((t) => {
            if (statusFilter !== 'All' && statusOf(t) !== statusFilter) return;
            if (sigFilter !== 'All' && !sigIs(t, sigFilter)) return;
            const s = reviewOf(t);
            m[s] = (m[s] || 0) + 1;
        });
        return m;
    }, [preFacet, statusFilter, sigFilter, signedBy]);

    const sigCounts = useMemo(() => {
        const m = {};
        preFacet.forEach((t) => {
            if (statusFilter !== 'All' && statusOf(t) !== statusFilter) return;
            if (reviewFilter !== 'All' && reviewOf(t) !== reviewFilter) return;
            if (sigIs(t, 'Signed')) m.Signed = (m.Signed || 0) + 1;
            else if (sigIs(t, 'Not signed')) m['Not signed'] = (m['Not signed'] || 0) + 1;
        });
        return m;
    }, [preFacet, statusFilter, reviewFilter, signedBy]);

    const visible = useMemo(() => {
        const rows = preFacet.filter((t) =>
            (statusFilter === 'All' || statusOf(t) === statusFilter)
            && (reviewFilter === 'All' || reviewOf(t) === reviewFilter)
            && (sigFilter === 'All' || sigIs(t, sigFilter)));
        const dir = sortDir === 'asc' ? 1 : -1;
        rows.sort((a, b) => {
            if (sortKey === 'cost') return (a.cost - b.cost) * dir;
            if (sortKey === 'bill') return (a.bill - b.bill) * dir;
            if (sortKey === 'hrs') return (a.hrs - b.hrs) * dir;
            return String(a.date).localeCompare(String(b.date)) * dir;
        });
        return rows;
    }, [preFacet, statusFilter, reviewFilter, sigFilter, signedBy, sortKey, sortDir]);

    const shown = showAll ? visible : visible.slice(0, 250);
    // Every selectable row matching the filters — the row cap only limits
    // rendering, so "select all matches" can reach past the first 250.
    const openMatches = visible.filter((t) => !t.inv);

    // ── PO grouping ──
    // The filtered rows by PO › PO line (poGroupsOf). Week grouping (billingWeekGroups.js): weeks → vendors / units, the
    // week's not-billable lines last. `groups` is the flat sub-bucket list
    // the PO-group plumbing below runs on.
    const weekView = useMemo(() => (groupWeek
        ? weekGroups(visible, { signedBy, showsHours, asc: sortKey === 'date' && sortDir === 'asc' })
        : null), [groupWeek, visible, signedBy, sortKey, sortDir]);
    // Vendor grouping: vendor → PO → PO line → entries, largest spend
    // first. A PO files under its own vendor (poGroupsOf's g.vendor);
    // PO-less rows get one "No PO" bucket per vendor. Vendor headers ride
    // the week-header plumbing ({ w }), so `weeks` is the vendor list.
    const vendorView = useMemo(() => {
        if (!groupVendor) return null;
        const map = new Map();
        const add = (v, g) => {
            let w = map.get(v);
            if (!w) {
                w = { we: `v|${v}`, label: v || 'No vendor', vendorHdr: true, groups: [], rows: [], open: [], cost: 0, bill: 0, hrs: 0, pos: 0 };
                map.set(v, w);
            }
            g.inVendor = true;
            w.groups.push(g);
            w.rows.push(...g.rows);
            w.open.push(...g.open);
            w.cost += g.cost;
            w.bill += g.bill;
            w.hrs += g.hrs;
            if (g.label !== 'No PO') w.pos += 1;
        };
        poGroupsOf(visible.filter((t) => t.po), rentalHrsMap).forEach((g) => add(g.vendor, g));
        const noPo = new Map();
        visible.filter((t) => !t.po).forEach((t) => {
            const v = t.who || '';
            if (!noPo.has(v)) noPo.set(v, []);
            noPo.get(v).push(t);
        });
        // Keyed per vendor so each No PO bucket opens on its own.
        noPo.forEach((rows, v) => add(v, { ...poGroupsOf(rows)[0], po: `|${v}`, label: 'No PO' }));
        const weeks = [...map.values()].sort((a, b) => (a.label === 'No vendor') - (b.label === 'No vendor') || b.cost - a.cost);
        return { weeks, groups: weeks.flatMap((w) => w.groups) };
    }, [groupVendor, visible, rentalHrsMap]);
    const poGroups = useMemo(() => {
        if (weekView) return weekView.groups;
        if (vendorView) return vendorView.groups;
        return groupPo ? poGroupsOf(visible, rentalHrsMap) : [];
    }, [groupPo, weekView, vendorView, visible, rentalHrsMap]);

    // ── PO context lines ──
    // An opened PO reads whole: the PO lines the grid leaves out ride along
    // read-only ({ cx } entries), so a line's billability can be judged
    // against the rest of its PO. That is a line already on a summary, a
    // line the filters hide, and a line ordered with nothing received yet
    // (`oc`, off the open commitments — no cost rows). Keyed by PO; a line
    // with any entry on the grid is not context. PO and vendor grouping only.
    const poContext = useMemo(() => {
        const m = {};
        if (!groupPo || groupWeek) return m;
        const shownPos = new Set();
        const shownLines = new Set();
        visible.forEach((t) => {
            if (!t.po) return;
            shownPos.add(t.po);
            shownLines.add(`${t.po}|${t.pol || ''}`);
        });
        const lines = new Map();
        const line = (po, pol, ds, oc) => {
            const k = `${po}|${pol}`;
            let ln = lines.get(k);
            if (!ln) {
                ln = { key: k, po, pol, rows: [], cost: 0, bill: 0, ds, oc };
                lines.set(k, ln);
                (m[po] || (m[po] = [])).push(ln);
            }
            return ln;
        };
        // PO lines with cost on the job — the rest of the open commitments
        // are the not-received ones.
        const costed = new Set();
        txns.forEach((t) => {
            if (!t.po) return;
            if (t.pol) costed.add(openKey(t.po, t.pol));
            if (isLaborCt(t.ct) || !shownPos.has(t.po) || shownLines.has(`${t.po}|${t.pol || ''}`)) return;
            const ln = line(t.po, t.pol || '', t.ds, null);
            ln.rows.push(t);
            ln.cost += t.cost;
            if (!t.inv && !t.nb) ln.bill += t.bill;
        });
        const poByOpen = {};
        shownPos.forEach((po) => { poByOpen[openKey(po)] = po; });
        (Array.isArray(openCommitments) ? openCommitments : []).forEach((o) => {
            if (!o || o.PO_Line_Number == null) return;
            const po = poByOpen[openKey(o.PO_Number)];
            if (!po || costed.has(openKey(o.PO_Number, o.PO_Line_Number))) return;
            const ds = [o.PO_Line_Description, o.PO_Line_Description_2].map((s) => String(s || '').trim()).filter(Boolean).join(' ');
            line(po, String(pNum(o.PO_Line_Number)), ds, o);
        });
        Object.values(m).forEach((list) => list.sort(byPol));
        return m;
    }, [groupPo, groupWeek, visible, txns, openCommitments]);

    // Grouped mode caps on groups, not rows, so a PO is never half-rendered.
    const shownGroups = showAll ? poGroups : poGroups.slice(0, 250);

    // The rendered sequence: group headers interleaved with the rows of the
    // expanded groups ({ g } = header, { t } = transaction), or the plain row
    // list when grouping is off. Range-select and drag-paint walk this order.
    // Week grouping adds { w } week headers above the sub-buckets.
    const shownSet = new Set(shownGroups);
    // In the week view a person's week reads as one ST row and one OT row
    // (billingWeekGroups.foldWeekLabor) — only lines standing the same on
    // status and review roll together. An opened row lists its days under
    // it ({ t, sub }), each still its own line.
    const foldLabor = (rows) => foldWeekLabor(rows, (t) => `${statusOf(t)}|${reviewOf(t)}`)
        .flatMap((t) => [{ t }, ...(t.parts && foldOpen[t.key] ? t.parts.map((p) => ({ t: p, sub: true })) : [])]);
    // An opened PO lists its lines in line order, the context lines
    // (poContext) slotted in among the open ones.
    const poEntries = (g) => {
        if (!poOpen[g.po]) return [{ g }];
        const cx = (poContext[g.po] || []).map((c) => ({ cx: c, pol: c.pol }));
        if (!g.lines) return [{ g }, ...g.rows.map((t) => ({ t })), ...cx];
        const units = [...g.lines.map((ln) => ({ ln, pol: ln.pol })), ...cx].sort(byPol);
        return [{ g }, ...units.flatMap((u) => (u.cx ? [u]
            : [{ ln: u.ln }, ...(lineOpen[u.ln.key] ? u.ln.rows.map((t) => ({ t })) : [])]))];
    };
    const hdrView = weekView || vendorView;
    const flatRows = hdrView
        ? hdrView.weeks.flatMap((w) => [
            { w },
            ...(hdrClosed(w) ? [] : w.groups.filter((g) => shownSet.has(g)).flatMap((g) => (weekView
                ? [{ g }, ...(poOpen[g.po] ? foldLabor(g.rows) : [])]
                : poEntries(g)))),
        ])
        : groupPo
        ? shownGroups.flatMap(poEntries)
        : shown.map((t) => ({ t }));
    const rowOrder = groupPo ? flatRows.filter((e) => e.t).map((e) => e.t) : shown;

    const onSort = (key) => {
        if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        else { setSortKey(key); setSortDir('desc'); }
    };

    // ── Selection / headline stats ──
    // Any non-invoiced line can be selected (for review / not-billable
    // actions); only the billable subset can go on an invoice. A line a
    // summary closed out not billable is as locked as a billed one.
    const selectedRows = useMemo(() => txns.filter((t) => sel[t.key] && !t.inv && !t.nbInv), [txns, sel]);
    // Invoice-eligible selection: not-billable and no-rate lines can still be
    // selected (for review / not-billable actions) but never land on a summary.
    const selectedTxns = useMemo(() => selectedRows.filter((t) => !t.nb && !t.noRate), [selectedRows]);
    const selectedNB = useMemo(() => selectedRows.filter((t) => t.nb), [selectedRows]);
    // Not-billable candidates include no-rate lines — same as the row toggle.
    const selectedBillable = useMemo(() => selectedRows.filter((t) => !t.nb), [selectedRows]);
    const selTotal = selectedTxns.reduce((s, t) => s + t.bill, 0);
    const selFuel = fuelSurcharge(selectedTxns, fuelCtx);
    const selHours = Math.round(selectedTxns.reduce((s, t) => s + t.hrs, 0) * 10) / 10;
    // Labor outside 511 / 512 / 515 is labor cost, never labor billing: the
    // Transactions view hides it (preFacet), so it can't be ready to invoice
    // either — not in the headline stat, and not in the Add Lines picker.
    const unbilled = txns.filter((t) => !t.inv && !t.nb && !t.noRate
        && (t.cls !== 'Labor' || LABOR_BILLABLE_CT.includes(t.ct)));
    const noRateTxns = txns.filter((t) => t.noRate);
    const nbTxns = txns.filter((t) => !t.inv && t.nb);
    const billedTxns = txns.filter((t) => t.inv);
    const unbilledAmt = unbilled.reduce((s, t) => s + t.bill, 0);
    // No-rate lines have no bill amount yet — their cost is what's stuck.
    const noRateAmt = noRateTxns.reduce((s, t) => s + t.cost, 0);
    const nbAmt = nbTxns.reduce((s, t) => s + t.cost, 0);
    const billedAmt = billedTxns.reduce((s, t) => s + t.bill, 0);
    // The job's invoice approvals in DocuSign (billingInvoiceApprovals) — the
    // list at the top of Invoices, and with the summaries the tab's count.
    const approvalRows = useMemo(
        () => invoiceApprovals(jobSigs.job === selectedJob ? jobSigs.list : []).rows,
        [jobSigs, selectedJob],
    );
    // Outstanding is a summary at an open stage that the client hasn't
    // approved in DocuSign. Nothing moves the stage once a summary is up in
    // FTMS (STATUS reads DocuSign), so an approved one would sit at Uploaded
    // to FTMS, outstanding, for good.
    const approvedNos = useMemo(() => approvedNumbers(approvalRows), [approvalRows]);
    const openInvs = invoices.filter((v) => OPEN_STAGES.includes(v.status) && !approvedNos.has(String(v.no).toUpperCase()));
    // Invoices still on our side — the ones Add Lines / Add to Invoice can
    // append to. Newest first, same order as the Invoices view.
    const addableInvs = invoices.filter((v) => NOT_SENT_STAGES.includes(v.status));
    const outstandingAmt = openInvs.reduce((s, v) => s + (invTotals[v.no]?.total || 0), 0);

    // What the header checkbox acts on. Grouped, that's every open line under
    // the shown PO groups — collapsed groups included, since their header
    // checkbox would tick them anyway.
    const visibleOpen = groupPo ? shownGroups.flatMap((g) => g.open) : shown.filter((t) => !t.inv && !t.nbInv);
    const allChecked = visibleOpen.length > 0 && visibleOpen.every((t) => sel[t.key]);
    const toggleAll = () => setSel((prev) => {
        const next = { ...prev };
        if (allChecked) visibleOpen.forEach((t) => delete next[t.key]);
        else visibleOpen.forEach((t) => { next[t.key] = true; });
        return next;
    });

    // ── PO group header controls ──
    const groupChecked = (g) => g.open.length > 0 && g.open.every((t) => sel[t.key]);
    const groupSome = (g) => g.open.some((t) => sel[t.key]);
    const toggleGroupSel = (g) => setSel((prev) => {
        const next = { ...prev };
        if (groupChecked(g)) g.open.forEach((t) => delete next[t.key]);
        else g.open.forEach((t) => { next[t.key] = true; });
        return next;
    });
    const toggleGroupOpen = (g) => setPoOpen((p) => ({ ...p, [g.po]: !p[g.po] }));
    const anyGroupOpen = shownGroups.some((g) => poOpen[g.po]);
    // Expand-all fills a row budget rather than opening every PO — a big job
    // has tens of thousands of lines and the grid renders them all at once.
    const toggleAllGroups = () => {
        // Vendor headers open and shut with their POs.
        if (anyGroupOpen) { setPoOpen({}); setLineOpen({}); if (vendorView) setWeekClosed({}); return; }
        const next = {};
        const nextLines = {};
        let rows = 0;
        for (let i = 0; i < poGroups.length; i += 1) {
            const g = poGroups[i];
            if (rows && rows + g.rows.length > 1500) break;
            next[g.po] = true;
            // Expand all opens the PO lines too, down to every entry.
            (g.lines || []).forEach((ln) => { nextLines[ln.key] = true; });
            rows += g.rows.length;
        }
        const opened = Object.keys(next).length;
        setPoOpen(next);
        setLineOpen(nextLines);
        if (vendorView) {
            setWeekClosed(Object.fromEntries(vendorView.weeks
                .filter((w) => w.groups.some((g) => next[g.po])).map((w) => [w.we, false])));
        }
        if (opened < poGroups.length) {
            showToast(`Expanded ${opened} ${weekView ? 'groups' : 'POs'} — ${poGroups.length - opened} left collapsed to keep the grid responsive`, 'warn');
        }
    };

    // Add Lines picker (billingAddLines.js) — offers `unbilled`.
    // How much 51xxx cost sits outside the billable three, never-billed cost
    // types included — the picker's labor section says so.
    const otherLabor = useMemo(() => {
        const rows = reportTxns.filter((t) => t.cls === 'Labor' && !LABOR_BILLABLE_CT.includes(t.ct));
        return { n: rows.length, cost: rows.reduce((s, t) => s + t.cost, 0) };
    }, [reportTxns]);
    const openAddPicker = (inv) => {
        setAddTo(inv);
        // Refresh the envelopes: a signature can land between two opens.
        loadSigs(selectedJob);
    };
    const closeAddPicker = () => setAddTo(null);

    // ── Persistence ──
    const saveRows = (rowsToSave) => {
        if (versionLocked) return;
        updateUserDataArray(rowsToSave.map((row) => ({
            userId: userId || '', userName: userName || '',
            id: selectedJob, version: BILLING_VERSION + '', tab: 'Billing',
            active: 1, lockedVersion: false,
            value: '', costType: '', costCode: '', notes: '',
            ...row,
        })));
        // Mirror into the atom so the page reflects the save without a reload.
        setBillingData((prev) => {
            if (!prev || prev.job !== selectedJob || !Array.isArray(prev.rows)) return prev;
            const list = [...prev.rows];
            rowsToSave.forEach((row) => {
                const idx = list.findIndex((x) => x?.Forecast_Tab === 'Billing' && x?.Forecast_Row_ID === row.rowId && x?.Forecast_Field_Name === row.fieldName);
                const patch = {
                    Forecast_Tab: 'Billing', Forecast_Row_ID: row.rowId, Forecast_Field_Name: row.fieldName,
                    Forecast_Value: row.value ?? '', Notes: row.notes ?? '', BU_ID: selectedJob, Version_GUID: BILLING_VERSION,
                };
                if (idx >= 0) list[idx] = { ...list[idx], ...patch };
                else list.push(patch);
            });
            return { ...prev, rows: list };
        });
    };

    // ── Per-line meta: review status, not-billable flag + audit history ──
    const appendHist = (h, a, extra) => [
        ...(h || []),
        { a, ...(extra || {}), u: String(userName || '').slice(0, 30), t: moment().format('YYYY-MM-DD HH:mm') },
    ].slice(-HIST_CAP);

    // m: { nb, bo, rr, rs, lc, h }. bo = billable override on a non-billable cost
    // code (100105 …) — those default to NB, so their billable state is a flag
    // of its own. lc = the line's labor classification fix. Drops the oldest
    // events until the blob fits the 1024-char Notes column — a truncated
    // JSON blob would lose everything.
    // pt = the pass-through override ('Y' / 'N'); callers that don't name it
    // carry the stored one forward, so a review stamp or a billing never
    // silently drops it.
    const metaRow = (key, m) => {
        const body = {};
        if (m.nb) body.nb = 1;
        if (m.bo) body.bo = 1;
        if (m.nb && m.rr) body.rr = String(m.rr).slice(0, 60);
        if (m.rs) body.rs = m.rs;
        if (m.lc) body.lc = String(m.lc).slice(0, 46);
        const pt = m.pt !== undefined ? m.pt : metas[key]?.pt;
        if (pt === 'Y' || pt === 'N') body.pt = pt;
        let h = m.h || [];
        let notes = JSON.stringify({ ...body, h });
        while (h.length > 1 && notes.length > 1000) {
            h = h.slice(1);
            notes = JSON.stringify({ ...body, h });
        }
        return { fieldName: 'meta', rowId: key, value: m.nb ? 'NB' : '', notes };
    };

    const setReviewStatus = (status) => {
        if (!selectedRows.length || versionLocked) return;
        const a = status === 'Reviewed' ? 'rev' : status === 'Flagged' ? 'flag' : 'clr';
        saveRows(selectedRows.map((t) => metaRow(t.key, { nb: t.nb, bo: t.autoNb && !t.nb, rr: t.nbReason, rs: status, lc: t.lcOv, h: appendHist(t.hist, a) })));
        setSel({});
        showToast(`${selectedRows.length} transaction${selectedRows.length === 1 ? '' : 's'} ${status ? 'marked ' + status.toLowerCase() : 'review cleared'}`, 'success');
    };

    // keepSel: the per-row toggle flips one line in place — clearing the
    // selection there would throw away an in-progress bulk pick.
    const setNotBillable = (rowsIn, nb, reason, keepSel) => {
        const rows = rowsIn;
        if (!rows.length || versionLocked) return;
        const r = nb ? String(reason || '').trim().slice(0, 60) : '';
        // Flipping an auto-NB row (non-billable cost code, reversed vendor
        // invoice) to billable is a deliberate override — recorded as bo so
        // the default stays NB.
        saveRows(rows.map((t) => metaRow(t.key, {
            nb, bo: t.autoNb && !nb, rr: r, rs: t.rs, lc: t.lcOv,
            h: appendHist(t.hist, nb ? 'nb' : 'ok', r ? { r } : undefined),
        })));
        if (!keepSel) setSel({});
        setNbPrompt(false);
        showToast(`${rows.length} transaction${rows.length === 1 ? '' : 's'} marked ${nb ? 'not billable' : 'billable'}`, nb ? 'warn' : 'success');
    };

    // Per-line labor classification fix: the JDE payroll class on a row is
    // sometimes just wrong (someone coded as the wrong craft), so meta.lc
    // records the rate sheet class the line bills as instead — empty clears
    // back to the Rates & Rules map. Unbilled lines simply re-price on the
    // next render. A line already on a summary still on our side (onInv)
    // also re-freezes its snapshot at the new class's rate and stamps the
    // summary's history, so the paper follows the fix.
    const setBillClass = (rowsIn, cls, onInv) => {
        if (versionLocked) return;
        const v = String(cls || '').slice(0, 46);
        const rows = [];
        let adjusted = 0;
        rowsIn.forEach((t) => {
            if (t.inv && !onInv) return;
            rows.push(metaRow(t.key, {
                nb: t.nb, bo: t.autoNb && !t.nb, rr: t.nbReason, rs: t.rs, lc: v,
                h: appendHist(t.hist, 'lc', v ? { c: v } : undefined),
            }));
            adjusted += 1;
            if (!t.inv) return;
            const snap = billed[t.key]?.snap;
            if (!snap) return;
            const sheet = v ? sheetLabor.byName[v] : (t.jde ? billsAs[t.jde] : null);
            const ba = sheet ? (v || billsAs[t.jde].name) : '';
            const isRate = t.ct === '51100' || t.ct === '51200';
            const hrs = pNum(snap.hr);
            // Re-price at the wage the line froze (or, for a snapshot from
            // before wages were frozen, the wage its own cost implies) × the
            // job's multiplier — the class only changes what it bills as.
            const wage = snap.w != null ? pNum(snap.w) : laborWageOf({ ct: t.ct, cost: pNum(snap.c), hrs, sid: t.sid, date: snap.d }, null);
            const rate = sheet && isRate ? laborRateFor(t.ct, wage, rateCfg, ba) : 0;
            rows.push({
                fieldName: 'line', rowId: t.key, value: onInv.no,
                notes: JSON.stringify({
                    ...snap,
                    ba: ba ? String(ba).slice(0, 46) : undefined,
                    ...(isRate && rate ? { b: round2(hrs * rate), ra: round2(rate), ru: 'hr', w: Math.round(wage * 1000) / 1000 } : {}),
                }),
            });
            const r = [snap.who || snap.ds, ba ? `bills as ${ba}` : 'classification cleared'].filter(Boolean).join(' · ').slice(0, 60);
            rows.push(invoiceRow(onInv, { s: 'Line Adjusted', ...(r ? { r } : {}), u: String(userName || '').slice(0, 30), t: moment().format('YYYY-MM-DD HH:mm') }));
        });
        if (!rows.length) return;
        saveRows(rows);
        // An uploaded summary now disagrees with what FTMS holds — say so.
        const stale = onInv && onInv.status === 'Uploaded to FTMS' ? ` — re-upload ${onInv.no} to FTMS` : '';
        showToast(
            v
                ? `${adjusted} line${adjusted === 1 ? '' : 's'} now bill${adjusted === 1 ? 's' : ''} as ${v}${stale}`
                : `Classification fix cleared on ${adjusted} line${adjusted === 1 ? '' : 's'} — back to the map${stale}`,
            stale ? 'warn' : 'success',
        );
    };

    // Pass-through override: 'Y' forces the lines to bill as pass-through,
    // 'N' keeps them on their class markup, '' hands them back to the Rates &
    // Rules description / cost type rules. Unbilled lines re-price on the
    // next render. Lines already on a summary still on our side (onInv) —
    // Uploaded to FTMS included, as with the classification fix — re-freeze
    // their snapshot at the new basis and stamp the summary's history.
    // Labor never passes through, and neither does rental usage (it
    // rate-bills like fleet), so both are skipped.
    const setPassThru = (rowsIn, ov, onInv) => {
        if (versionLocked) return;
        const rows = [];
        let n = 0;
        let stamped = 0;
        rowsIn.forEach((t) => {
            if (t.cls === 'Labor' || isRentUsage(t)) return;
            if (t.inv && !(onInv && t.inv === onInv.no)) return;
            rows.push(metaRow(t.key, {
                nb: t.nb, bo: t.autoNb && !t.nb, rr: t.nbReason, rs: t.rs, lc: t.lcOv, pt: ov,
                h: appendHist(t.hist, 'pt', ov ? { c: ov } : undefined),
            }));
            n += 1;
            if (!t.inv) return;
            const snap = billed[t.key]?.snap;
            if (!snap) return;
            const pt = isPassThru({ cls: t.cls, ct: t.ct, ds: t.ds }, settings.pt, ov);
            // Re-price the frozen line the way txns would price it unbilled:
            // pass-through at the job's pass-through %; otherwise the class
            // markup, or hours × the sheet rate for a mapped fleet tier.
            const er = !pt && t.ct === '52100' && t.tier && pNum(snap.hr) ? equipBills[t.tier] : null;
            const rest = { ...snap };
            ['b', 'ra', 'ru', 'fs', 'fu', 'pt'].forEach((k) => { delete rest[k]; });
            const live = { ...t, pt, bill: 0 };
            rows.push({
                fieldName: 'line', rowId: t.key, value: onInv.no,
                notes: JSON.stringify({
                    ...rest,
                    m: pt ? settings.pt.mk : pNum(settings.mk[t.cls]),
                    ...(er ? { b: round2(pNum(snap.hr) * er.hourly), ra: round2(er.rate), ru: String(er.uom).slice(0, 8) } : {}),
                    fs: fuelSnapPct(live, fuelCtx),
                    fu: live.fu === false ? 0 : undefined,
                    pt: pt ? 1 : undefined,
                }),
            });
            const r = [snap.who || snap.ds, pt ? 'set pass-through' : 'set not pass-through'].filter(Boolean).join(' · ').slice(0, 60);
            rows.push(invoiceRow(onInv, { s: 'Line Adjusted', r, u: String(userName || '').slice(0, 30), t: moment().format('YYYY-MM-DD HH:mm') }));
            stamped += 1;
        });
        if (!rows.length) return;
        saveRows(rows);
        const stale = stamped && onInv && onInv.status === 'Uploaded to FTMS' ? ` — re-upload ${onInv.no} to FTMS` : '';
        showToast(
            `${n} line${n === 1 ? '' : 's'} ${ov === 'Y' ? 'set as pass-through' : ov === 'N' ? 'set as not pass-through' : 'back on the pass-through rules'}${stale}`,
            stale ? 'warn' : 'success',
        );
    };

    // One-click flip of a line's pass-through state. Lands back on the rules
    // ('') when the rules already give the wanted answer, so an override is
    // only stored where the line genuinely departs from them.
    const flipPassThru = (t, onInv) => {
        const want = !t.pt;
        setPassThru([t], want === !!t.ptWhy ? '' : want ? 'Y' : 'N', onInv);
    };

    const openHistory = (t) => { setHistLine(t); setNoteDraft(''); };

    // A rolled week row (foldWeekLabor) stands for its `parts`: every
    // selection gesture lands on the real lines, never the display row.
    const partsOf = (t) => t.parts || [t];
    const rowSel = (t) => partsOf(t).every((p) => sel[p.key]);
    const rowSome = (t) => partsOf(t).some((p) => sel[p.key]);
    const toggleRow = (t) => setSel((prev) => {
        const next = { ...prev };
        const on = !partsOf(t).every((p) => next[p.key]);
        partsOf(t).forEach((p) => { if (on) next[p.key] = true; else delete next[p.key]; });
        return next;
    });

    // ── Multi-select gestures ──
    // Beyond one-at-a-time checkbox clicks: shift-click selects the range
    // between the last-clicked row and this one, and holding the mouse down
    // on a checkbox cell while dragging across rows paints the pressed value
    // onto every row passed. Both run in the grid's current sort order.
    const lastPickRef = useRef(null); // key of the last checkbox the user clicked
    const dragPaint = useRef(null); // { anchor, val, engaged } while the button is held

    const paintOne = (t, val) => {
        if (t.inv || t.nbInv) return;
        setSel((p) => {
            const next = { ...p };
            partsOf(t).forEach((x) => { if (val) next[x.key] = true; else delete next[x.key]; });
            return next;
        });
    };

    const setRange = (fromKey, toKey, val) => {
        const a = rowOrder.findIndex((x) => x.key === fromKey);
        const b = rowOrder.findIndex((x) => x.key === toKey);
        if (a < 0 || b < 0) return;
        const [lo, hi] = a < b ? [a, b] : [b, a];
        setSel((p) => {
            const next = { ...p };
            rowOrder.slice(lo, hi + 1).forEach((x) => {
                if (x.inv || x.nbInv) return;
                partsOf(x).forEach((y) => { if (val) next[y.key] = true; else delete next[y.key]; });
            });
            return next;
        });
    };

    // Checkbox-cell click: plain toggles, shift extends from the last click.
    const pickRow = (t, e) => {
        if (dragPaint.current?.engaged) return; // trailing click after a drag — already painted
        if (e && e.shiftKey && lastPickRef.current && lastPickRef.current !== t.key) {
            setRange(lastPickRef.current, t.key, true);
        } else {
            toggleRow(t);
        }
        lastPickRef.current = t.key;
    };

    const startPaint = (t, e) => {
        if (t.inv || t.nbInv || (e && e.shiftKey)) return; // shift stays a range click
        if (e && e.preventDefault) e.preventDefault(); // no text selection mid-drag
        dragPaint.current = { anchor: t.key, val: !rowSel(t), engaged: false };
    };

    // Row mouse-enter while the button is down: paint this row. The anchor
    // row is painted on the first row change so a plain click still goes
    // through the normal click path.
    const paintRow = (t) => {
        const d = dragPaint.current;
        if (!d || t.key === d.anchor) return;
        if (!d.engaged) {
            d.engaged = true;
            const anchor = rowOrder.find((x) => x.key === d.anchor);
            if (anchor) paintOne(anchor, d.val);
        }
        paintOne(t, d.val);
        lastPickRef.current = t.key;
    };

    useEffect(() => {
        const up = () => {
            if (!dragPaint.current) return;
            // An engaged drag must outlive the click that follows mouseup so
            // pickRow can swallow it; a plain press clears now.
            if (dragPaint.current.engaged) setTimeout(() => { dragPaint.current = null; }, 0);
            else dragPaint.current = null;
        };
        window.addEventListener('mouseup', up);
        return () => window.removeEventListener('mouseup', up);
    }, []);

    const addNote = () => {
        const n = noteDraft.trim().slice(0, NOTE_MAX);
        if (!n || !histLine || versionLocked) return;
        const row = metaRow(histLine.key, {
            nb: histLine.nb, bo: histLine.autoNb && !histLine.nb, rr: histLine.nbReason, rs: histLine.rs, lc: histLine.lcOv,
            h: appendHist(histLine.hist, 'note', { n }),
        });
        saveRows([row]);
        // Refresh the open modal from what actually got saved (metaRow may
        // have dropped old events to fit the Notes column).
        let saved = null;
        try { saved = JSON.parse(row.notes); } catch (e) { /* can't happen — we built it */ }
        setHistLine({ ...histLine, hist: saved?.h || histLine.hist });
        setNoteDraft('');
    };

    // Next invoice number: the stored counter, bumped past any number already
    // taken (covers a stale counter after the weekly version copy).
    const nextInvoiceNo = () => {
        let n = pNum(settings.next) || DEFAULT_SETTINGS.next;
        const taken = new Set(invoices.map((v) => v.no));
        while (taken.has(settings.prefix + n)) n += 1;
        return { no: settings.prefix + n, n };
    };

    // Frozen line snapshot written when a transaction lands on a summary —
    // markup (and hours) captured at billing time so later rate changes
    // never move an issued summary.
    const lineSnapRow = (t, no) => ({
        fieldName: 'line', rowId: t.key, value: no,
        notes: JSON.stringify({
            d: t.date, cc: t.cc, ct: t.ct, ds: String(t.ds).slice(0, 80),
            c: Math.round(t.cost * 100) / 100, m: t.mk, hr: round1(t.hrs) || undefined,
            // Hours-billed rentals freeze the bill amount itself — no
            // cost×markup to recompute it from later — plus the sheet
            // rate and unit that produced it.
            b: t.hourly ? Math.round(t.bill * 100) / 100 : undefined,
            ra: t.hourly && t.rateAmt ? Math.round(t.rateAmt * 100) / 100 : undefined,
            ru: t.hourly && t.rateUom ? String(t.rateUom).slice(0, 8) : undefined,
            // Labor: the JDE wage the rate was built on, so a later class
            // fix re-prices at the same wage.
            w: t.hourly && t.wage ? Math.round(t.wage * 1000) / 1000 : undefined,
            who: String(t.who).slice(0, 40), po: t.po,
            vn: t.vinvNo || undefined,
            // Labor: JDE classification + the rate sheet classification it
            // billed as, frozen so remapping never moves issued paper.
            lc: t.jde ? String(t.jde).slice(0, 32) : undefined,
            ba: t.ba ? String(t.ba).slice(0, 46) : undefined,
            // Equipment fuel surcharge adder in force on the work date —
            // frozen like markup so a later price or index edit never moves
            // the summary. Undefined off equipment, or while the quarter's
            // diesel price isn't entered yet (the line keeps pricing live).
            fs: fuelSnapPct(t, fuelCtx),
            // Equipment the rate sheet says burns no fuel — frozen so the
            // summary's surcharge base doesn't move if the flag is edited
            // later. Only the exclusion is stored; anything else stays in.
            fu: t.fu === false ? 0 : undefined,
            // Pass-through delivery/freight — frozen so a rule edit never
            // moves the line between invoice groups after the fact.
            pt: t.pt ? 1 : undefined,
        }),
    });

    const confirmCreate = () => {
        if (!selectedTxns.length) return;
        const { no, n } = nextInvoiceNo();
        const today = moment().format('YYYY-MM-DD');
        saveRows([
            { fieldName: 'invoice', rowId: 'inv_' + no, value: 'Draft', notes: JSON.stringify({ no, date: today, status: 'Draft', terms: settings.terms, h: [{ s: 'Draft', u: String(userName || '').slice(0, 30), t: moment().format('YYYY-MM-DD HH:mm') }] }) },
            ...selectedTxns.map((t) => lineSnapRow(t, no)),
            ...selectedTxns.map((t) => metaRow(t.key, { nb: false, bo: t.autoNb, rr: '', rs: t.rs, lc: t.lcOv, h: appendHist(t.hist, 'bill', { v: no }) })),
            { fieldName: 'settings', rowId: 'settings', notes: JSON.stringify({ ...settings, next: n + 1 }) },
        ]);
        setSel({});
        setShowModal(false);
        setViewMode('invoices');
        showToast(`Created ${no} from ${selectedTxns.length} transactions`, 'success');
    };

    // Serialize the invoice with a new history stamp. Same size-guard idea
    // as metaRow: drop the oldest stamps until the Notes blob fits the
    // 1024-char column.
    const invoiceRow = (inv, stamp, status = inv.status) => {
        let h = [...(inv.h || []), stamp].slice(-HIST_CAP);
        const body = { ...inv, status };
        let notes = JSON.stringify({ ...body, h });
        while (h.length > 1 && notes.length > 1000) {
            h = h.slice(1);
            notes = JSON.stringify({ ...body, h });
        }
        return { fieldName: 'invoice', rowId: 'inv_' + inv.no, value: status, notes };
    };

    // The not-billable lines a summary closed out at $0 ('nbline' rows) go
    // back to plain not billable when the summary is voided or deleted —
    // blanking the row is the release, same as a billed line's.
    const releaseNbLines = (no) => Object.entries(nbLines)
        .filter(([, rec]) => rec.inv === no)
        .map(([key]) => ({ fieldName: 'nbline', rowId: key, value: '', notes: '' }));

    // One closed-out line off a summary still on our side.
    const releaseNbLine = (inv, key) => {
        if (versionLocked) return;
        const snap = nbLines[key]?.snap || {};
        const r = [fDate(snap.d), snap.who || snap.ds, money(pNum(snap.c))].filter(Boolean).join(' · ').slice(0, 60);
        const stamp = { s: 'Line Removed', ...(r ? { r } : {}), u: String(userName || '').slice(0, 30), t: moment().format('YYYY-MM-DD HH:mm') };
        saveRows([{ fieldName: 'nbline', rowId: key, value: '', notes: '' }, invoiceRow(inv, stamp)]);
        showToast(`Not-billable line released from ${inv.no}`, 'success');
    };

    const setInvoiceStatus = (inv, status, reason, toast) => {
        const r = String(reason || '').trim().slice(0, 60);
        const stamp = { s: status, ...(r ? { r } : {}), u: String(userName || '').slice(0, 30), t: moment().format('YYYY-MM-DD HH:mm') };
        const rows = [invoiceRow(inv, stamp, status)];
        if (status === 'Void') {
            Object.entries(billed).forEach(([key, rec]) => {
                if (rec.inv === inv.no) {
                    rows.push({ fieldName: 'line', rowId: key, value: '', notes: '' });
                    rows.push(metaRow(key, { nb: false, rr: '', rs: metas[key]?.rs || '', lc: metas[key]?.lc || '', h: appendHist(metas[key]?.h, 'void', { v: inv.no }) }));
                }
            });
            rows.push(...releaseNbLines(inv.no));
        }
        saveRows(rows);
        showToast(toast || `${inv.no} marked ${status.toLowerCase()}`, status === 'Void' ? 'warn' : 'success');
    };

    // Delete a summary outright — the escape hatch for one created by mistake.
    // Only offered on Draft (never left our desk) and Void (already released
    // and stamped), so a summary that is live with AR or the client can't
    // disappear: those have to be voided first, which leaves the audit trail.
    //
    // Every transaction still attached comes back to Unbilled with a 'pull'
    // event on its own history, so the lines are immediately re-billable on a
    // different summary. Then the invoice row itself goes away: a hard delete
    // by Forecast_User_Data_GUID (which the deleteUserData function mirrors
    // into PC.Forecast_Audit_Log), falling back to blanking the row for a
    // summary created this session that hasn't been re-read yet and so has no
    // GUID. Blanking is enough on its own — the invoice parser skips rows with
    // empty Notes — it just leaves a tombstone row behind.
    const deleteSummary = (inv) => {
        if (versionLocked) return;
        if (!DELETABLE_STAGES.includes(inv.status)) return;
        const rows = [];
        let released = 0;
        Object.entries(billed).forEach(([key, rec]) => {
            if (rec.inv !== inv.no) return;
            released += 1;
            rows.push({ fieldName: 'line', rowId: key, value: '', notes: '' });
            rows.push(metaRow(key, { nb: false, rr: '', rs: metas[key]?.rs || '', lc: metas[key]?.lc || '', h: appendHist(metas[key]?.h, 'pull', { v: inv.no }) }));
        });
        rows.push(...releaseNbLines(inv.no));
        // Reclaim the number, but only when this was the last one issued —
        // deleting an older summary must not hand its number to a new one
        // while a higher summary already exists.
        const seq = parseInt(String(inv.no).slice(String(settings.prefix || '').length), 10);
        if (Number.isFinite(seq) && pNum(settings.next) === seq + 1) {
            rows.push({ fieldName: 'settings', rowId: 'settings', notes: JSON.stringify({ ...settings, next: seq }) });
        }

        const rowId = 'inv_' + inv.no;
        const guid = billingUserRows.find(
            (x) => x.Forecast_Field_Name === 'invoice' && x.Forecast_Row_ID === rowId,
        )?.Forecast_User_Data_GUID;
        if (guid) {
            deleteUserData({
                id: selectedJob, forecastGuid: BILLING_VERSION, lockedVersion: false,
                rowId: guid + '', tab: 'Billing',
            }).catch(() => {});
        } else {
            rows.push({ fieldName: 'invoice', rowId, value: '', notes: '' });
        }
        if (rows.length) saveRows(rows);
        // saveRows only upserts, so drop the deleted summary from the atom by
        // hand to keep the page in step without a reload.
        if (guid) {
            setBillingData((prev) => (!prev || !Array.isArray(prev.rows) ? prev : {
                ...prev,
                rows: prev.rows.filter((x) => !(x?.Forecast_Tab === 'Billing' && x?.Forecast_Row_ID === rowId && x?.Forecast_Field_Name === 'invoice')),
            }));
        }
        showToast(
            released
                ? `${inv.no} deleted — ${released} transaction${released === 1 ? '' : 's'} back in Unbilled`
                : `${inv.no} deleted`,
            'warn',
        );
    };

    // Pull one line off a summary while it's still on our side (Draft or
    // Kicked Back): the line goes back to Unbilled — fixable, re-billable on
    // the next summary — and the removal lands on its history trail. Once a
    // summary has gone up to FTMS, sending it back (kick back, then reopen to
    // Draft) is the way to change it.
    const removeFromSummary = (inv, key) => {
        if (versionLocked) return;
        const meta = metas[key] || {};
        // Stamp the removal on the summary's history too, with enough of the
        // line (date · who/desc · cost) to tell which one left.
        const snap = billed[key]?.snap || {};
        const r = [fDate(snap.d), snap.who || snap.ds, money(pNum(snap.c))].filter(Boolean).join(' · ').slice(0, 60);
        const stamp = { s: 'Line Removed', ...(r ? { r } : {}), u: String(userName || '').slice(0, 30), t: moment().format('YYYY-MM-DD HH:mm') };
        saveRows([
            { fieldName: 'line', rowId: key, value: '', notes: '' },
            metaRow(key, { nb: false, rr: '', rs: meta.rs || '', lc: meta.lc || '', h: appendHist(meta.h, 'pull', { v: inv.no }) }),
            invoiceRow(inv, stamp),
        ]);
        showToast(`Line removed from ${inv.no} — back in Unbilled`, 'success');
    };

    // removeFromSummary for a batch, with the option of marking what leaves
    // not-billable on the way out so the next summary doesn't pick it back
    // up. One save for the whole batch: the meta row carries both the pull
    // and the not-billable flag, and doing it as two writes would race — the
    // second would build its row off a metas map the first has already moved.
    // The pre-export chat's remove_line lands here.
    const removeLinesFromSummary = (inv, list, opts = {}) => {
        if (versionLocked || !list.length) return;
        const nb = Boolean(opts.nb);
        const reason = String(opts.reason || '').trim().slice(0, 60);
        const out = [];
        list.forEach((t) => {
            const key = t.key;
            if (!key) return;
            const meta = metas[key] || {};
            out.push({ fieldName: 'line', rowId: key, value: '', notes: '' });
            let h = appendHist(meta.h, 'pull', { v: inv.no });
            if (nb) h = appendHist(h, 'nb', reason ? { r: reason } : undefined);
            out.push(metaRow(key, { nb, rr: reason, rs: meta.rs || '', lc: meta.lc || '', h }));
        });
        if (!out.length) return;
        const r = (list.length === 1
            ? [fDate(list[0].date), list[0].who || list[0].ds, money(list[0].cost)].filter(Boolean).join(' · ')
            : `${list.length} lines · ${money(list.reduce((x, t) => x + t.cost, 0))}`).slice(0, 60);
        out.push(invoiceRow(inv, {
            s: 'Line Removed', ...(r ? { r } : {}),
            u: String(userName || '').slice(0, 30), t: moment().format('YYYY-MM-DD HH:mm'),
        }));
        saveRows(out);
        showToast(
            `${list.length} line${list.length === 1 ? '' : 's'} removed from ${inv.no}`
            + (nb ? ' and marked not billable' : ' — back in Unbilled'),
            'success',
        );
    };

    // Counterpart of removeFromSummary: append unbilled transactions to a
    // summary that's still on our side. Same frozen snapshot as at create
    // time, and the addition lands on the summary's history the way removals
    // do — one stamp for the batch.
    const addToSummary = (inv, list) => {
        if (versionLocked || !list.length) return;
        const r = (list.length === 1
            ? [fDate(list[0].date), list[0].who || list[0].ds, money(list[0].cost)].filter(Boolean).join(' · ')
            : `${list.length} lines · ${money(list.reduce((s, t) => s + t.cost, 0))}`).slice(0, 60);
        const stamp = { s: 'Line Added', ...(r ? { r } : {}), u: String(userName || '').slice(0, 30), t: moment().format('YYYY-MM-DD HH:mm') };
        saveRows([
            ...list.map((t) => lineSnapRow(t, inv.no)),
            ...list.map((t) => metaRow(t.key, { nb: false, bo: t.autoNb, rr: '', rs: t.rs, lc: t.lcOv, h: appendHist(t.hist, 'bill', { v: inv.no }) })),
            invoiceRow(inv, stamp),
        ]);
        closeAddPicker();
        showToast(`${list.length} line${list.length === 1 ? '' : 's'} added to ${inv.no}`, 'success');
    };

    // Transactions-view side of the same move: the grid's selection goes
    // onto an existing invoice through addToSummary, then the Invoices view
    // opens on it — the way Create Invoice lands on the new one.
    const openAddModal = () => {
        if (!selectedTxns.length || versionLocked || !addableInvs.length) return;
        setAddTarget((cur) => (addableInvs.some((v) => v.no === cur) ? cur : addableInvs[0].no));
        setShowModal('add');
    };
    const confirmAdd = () => {
        const inv = addableInvs.find((v) => v.no === addTarget);
        if (!inv || !selectedTxns.length) return;
        addToSummary(inv, selectedTxns);
        setSel({});
        setShowModal(false);
        setViewMode('invoices');
        setOpenInvoice(inv.no);
    };

    // One flat row per transaction — shared by the filtered export and the
    // detail sheet of the weekly invoice backup.
    const detailRow = (t) => ({
        'Work Date': fDate(t.date),
        'Cost Code': t.cc,
        'Cost Code Description': t.ccDesc,
        Class: t.cls,
        'Pass-Through': t.pt ? 'Y' : '',
        'Cost Type': t.ct,
        'Cost Type Description': t.ctDesc,
        'Employee / Vendor': t.who,
        'JDE Classification': t.jde || '',
        'Bills As': t.ba || '',
        // Unit and rate sheet item — the JDE tier code stays off exports.
        Equipment: t.cls === 'Equipment' || t.unit ? clientEquipLabel({ ...t, who: '' }, equipBills) : '',
        'PO Number': t.po,
        'P-Card': t.pcard ? 'Y' : '',
        'Vendor Invoice Status': t.vinv || '',
        'Vendor Invoice #': t.vinvNo || '',
        'Vendor Invoice Date': t.vinvDate || '',
        'TAP Invoice Link': t.pdf || '',
        Description: t.ds,
        Hours: showsHours(t) ? Math.round(t.hrs * 100) / 100 : '',
        Cost: Math.round(t.cost * 100) / 100,
        Rate: rateText(t) || '',
        'Markup %': t.nb ? '' : t.noRate ? 'no rate' : t.hourly ? 'rate' : t.mk,
        'Bill Amount': Math.round(t.bill * 100) / 100,
        Status: statusOf(t),
        'Not Billable Reason': t.nbReason || '',
        'Invoice #': t.inv || '',
        'Review Status': t.rs || '',
        'Reviewed By': t.lastRev ? t.lastRev.u : '',
        'Reviewed On': t.lastRev ? fDate(t.lastRev.t) : '',
        'Last Note': [...t.hist].reverse().find((e) => e.a === 'note')?.n || '',
    });

    const exportExcel = () => {
        const ws = XLSX.utils.json_to_sheet(visible.map((t) => ({
            ...detailRow(t),
            'PO Line': t.pol || '',
            // How much of the entry's PO line is already on summaries — the
            // part-billed lines this grid still carries.
            'PO Line Invoiced': t.po && tpInvoiced[`l:${t.po}|${t.pol || ''}`] ? Math.round(tpInvoiced[`l:${t.po}|${t.pol || ''}`].bill * 100) / 100 : '',
            'PO Line Invoiced On': t.po ? (tpInvoiced[`l:${t.po}|${t.pol || ''}`]?.invs || []).join(', ') : '',
            'Week Ending': fDate(weekEnding(t.date)),
            'Signed Summary': sigTicketed(t) ? (sigOf(t)?.stage || '') : '',
        })));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Billing');
        XLSX.writeFile(wb, `Billing_${selectedJob}_${moment().format('YYYY-MM-DD')}.xlsx`);
    };

    // ── Weekly invoice backup ──
    // Roll the selected lines up the way the invoice is built: labor together,
    // equipment together, third-party spend together, with a cost-code
    // breakdown inside each group.
    const summaryOf = (rows) => {
        const groups = GROUPS.map((g) => {
            // Lines sort by what AR reconciles them against: labor by person
            // then day (one employee's week reads top to bottom against their
            // timecards), equipment by unit, third-party spend by vendor then
            // PO. A date-only sort scattered each entity's lines through the
            // section — and blanks sort last, not first.
            const ent = (t) => ((g === 'Equipment' ? t.eq || t.who : t.who) || '');
            const lines = rows.filter((t) => groupOf(t.cls, t.pt) === g)
                .sort((a, b) => ((ent(a) === '') - (ent(b) === ''))
                    || ent(a).localeCompare(ent(b))
                    || String(a.date).localeCompare(String(b.date))
                    || String(a.po || '').localeCompare(String(b.po || ''))
                    || String(a.cc).localeCompare(String(b.cc))
                    || String(a.ct).localeCompare(String(b.ct)));
            const byCode = {};
            const byType = {};
            const byClass = {};
            lines.forEach((t) => {
                const k = t.cc || '(no cost code)';
                const b = byCode[k] || (byCode[k] = { cc: k, desc: t.ccDesc || '', n: 0, hrs: 0, cost: 0, bill: 0 });
                b.n += 1; b.hrs += t.hrs; b.cost += t.cost; b.bill += t.bill;
                const k2 = t.ct || '(no cost type)';
                const b2 = byType[k2] || (byType[k2] = { ct: k2, desc: t.ctDesc || '', n: 0, hrs: 0, cost: 0, bill: 0 });
                b2.n += 1; b2.hrs += t.hrs; b2.cost += t.cost; b2.bill += t.bill;
                // Labor rolls up by billed-as rate sheet classification too —
                // the grouping VoltaGrid's auditors check against the sheet.
                if (g === 'Labor') {
                    const k3 = t.ba || '(unmapped)';
                    const b3 = byClass[k3] || (byClass[k3] = { ba: k3, jde: new Set(), n: 0, hrs: 0, cost: 0, bill: 0 });
                    if (t.jde) b3.jde.add(t.jde);
                    b3.n += 1; b3.hrs += t.hrs; b3.cost += t.cost; b3.bill += t.bill;
                }
            });
            return {
                g,
                lines,
                hrs: lines.reduce((x, t) => x + t.hrs, 0),
                cost: lines.reduce((x, t) => x + t.cost, 0),
                bill: lines.reduce((x, t) => x + t.bill, 0),
                codes: Object.values(byCode).sort((a, b) => b.bill - a.bill),
                types: Object.values(byType).sort((a, b) => String(a.ct).localeCompare(String(b.ct))),
                classes: Object.values(byClass).map((c) => ({ ...c, jde: [...c.jde].sort().join(', ') }))
                    .sort((a, b) => b.bill - a.bill),
            };
        }).filter((x) => x.lines.length);

        // Cover-page rollup, cut across the invoice groups rather than inside
        // them: the summary's totals by LEMSO cost class, with pass-through
        // freight/delivery pulled out onto its own row after Other so its
        // at-cost basis doesn't dilute the class markups beside it.
        const byLemso = {};
        rows.forEach((t) => {
            const k = t.pt ? PT_GROUP : t.cls;
            const c = byLemso[k] || (byLemso[k] = { cls: k, n: 0, hrs: 0, cost: 0, bill: 0 });
            c.n += 1; c.hrs += t.hrs; c.cost += t.cost; c.bill += t.bill;
        });

        // Fuel surcharge: the fuel index's Equipment % adder on the equipment
        // lines only, split by the adder period each work date falls in. It
        // bills on top of the lines — lineBill is the lines alone, bill is
        // the total to invoice.
        const fuel = fuelSurcharge(rows, fuelCtx);
        const lineBill = groups.reduce((x, g) => x + g.bill, 0);
        return {
            groups,
            // Canonical L/E/M/S/O order, not whatever order the lines arrived in.
            classes: [...LEMSO, PT_GROUP].filter((c) => byLemso[c]).map((c) => byLemso[c]),
            hrs: groups.reduce((x, g) => x + g.hrs, 0),
            cost: groups.reduce((x, g) => x + g.cost, 0),
            fuel,
            lineBill,
            bill: lineBill + fuel.amt,
        };
    };

    // The backup document as a real PDF: every line under its group with
    // subtotals. Shared by the Summaries download, the FTMS hand-off and the
    // Backup bundle. `entries` is the attachment plan (see
    // buildBackupBundle) — with one, the line detail names the exhibit and
    // page behind each line and an index page says what rides along.
    const buildBackupPdf = async (sum, stamp, tag, entries = []) => {
        const { jsPDF } = await import('jspdf');
        const autoTableMod = await import('jspdf-autotable');
        const autoTable = autoTableMod.default || autoTableMod.autoTable;
        const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
        const W = doc.internal.pageSize.getWidth();
        const H = doc.internal.pageSize.getHeight();
        const M = 28;
        const lineCount = sum.groups.reduce((n, g) => n + g.lines.length, 0);
        const R = { halign: 'right' };

        // Strike left, the client's mark right — the same pairing the field
        // ticket template carries. Either can come back null (missing asset,
        // canvas blocked); the band just draws without it.
        const [sLogo, vLogo] = await Promise.all([loadLogo(strikeLogo), loadLogo(voltaLogo)]);
        const LOGO_H = 20;
        const drawLogos = (top) => {
            let bottom = top;
            if (sLogo) {
                const w = (LOGO_H * sLogo.w) / sLogo.h;
                doc.addImage(sLogo.data, 'PNG', M, top, w, LOGO_H);
                bottom = Math.max(bottom, top + LOGO_H);
            }
            if (vLogo) {
                const w = (LOGO_H * vLogo.w) / vLogo.h;
                doc.addImage(vLogo.data, 'PNG', W - M - w, top, w, LOGO_H);
                bottom = Math.max(bottom, top + LOGO_H);
            }
            return bottom;
        };

        // ── Cover page: how the summary rolls up ──
        // The class totals, so the reader sees where the money sits before the
        // line-by-line backup that follows. Driven off the whole summary,
        // cutting across the Labor / Equipment / Third Party invoice groups.
        let y = drawLogos(M) + 18;
        doc.setFont('helvetica', 'bold').setFontSize(14).setTextColor('#0f172a');
        doc.text(`Billing Summary${tag ? ' ' + tag : ''} — ${selectedJob}`, M, y);
        doc.setFont('helvetica', 'normal').setFontSize(8).setTextColor('#475569');
        doc.text(`Prepared ${stamp} by ${userName || ''}   ·   ${lineCount} line${lineCount === 1 ? '' : 's'}${sum.hrs ? `   ·   ${round1(sum.hrs)} hours` : ''}   ·   cost ${money(sum.cost)}   ·   billed ${money(sum.bill)}${entries.length ? `   ·   ${entries.length} backup document${entries.length === 1 ? '' : 's'}` : ''}`, M, y + 14);
        y += 30;

        const rollupTable = (label, head, body, columnStyles, opts) => {
            doc.setFont('helvetica', 'bold').setFontSize(9).setTextColor('#0f172a');
            doc.text(label, M, y + 8);
            autoTable(doc, {
                startY: y + 14,
                margin: { left: M, right: M, top: M },
                head: [head],
                body,
                ...opts,
                styles: { font: 'helvetica', fontSize: 7, cellPadding: 3, textColor: '#0f172a', lineColor: '#e2e8f0', lineWidth: 0.5, overflow: 'linebreak' },
                headStyles: { fillColor: '#f1f5f9', textColor: '#475569', fontStyle: 'bold', fontSize: 6.5 },
                columnStyles,
                didParseCell: (d) => {
                    if (d.section === 'body' && d.row.index === body.length - 1) {
                        d.cell.styles.fontStyle = 'bold';
                        d.cell.styles.fillColor = '#f8fafc';
                    }
                },
            });
            y = doc.lastAutoTable.finalY + 18;
        };

        rollupTable(
            'ROLLUP BY COST CLASS (LEMSO)',
            ['Cost Class', 'Lines', 'Hours', 'Cost', 'Markup', 'Bill Amount', '% of Bill'],
            [
                ...sum.classes.map((c) => [
                    c.cls, String(c.n), c.hrs ? String(round1(c.hrs)) : '', money(c.cost), money(c.bill - c.cost), money(c.bill),
                    sum.bill ? `${Math.round((c.bill / sum.bill) * 1000) / 10}%` : '',
                ]),
                // The fuel surcharge is its own row: it isn't any class's
                // cost, it's an adder on the Equipment subtotal.
                ...(sum.fuel.amt ? [[
                    'Fuel Surcharge (Equipment)', '', '', '', money(sum.fuel.amt), money(sum.fuel.amt),
                    sum.bill ? `${Math.round((sum.fuel.amt / sum.bill) * 1000) / 10}%` : '',
                ]] : []),
                ['Total', String(lineCount), sum.hrs ? String(round1(sum.hrs)) : '', money(sum.cost), money(sum.bill - sum.cost), money(sum.bill), sum.bill ? '100%' : ''],
            ],
            {
                0: { cellWidth: 110 }, 1: { ...R, cellWidth: 45 }, 2: { ...R, cellWidth: 50 },
                3: { ...R, cellWidth: 75 }, 4: { ...R, cellWidth: 75 }, 5: { ...R, cellWidth: 80 }, 6: { ...R, cellWidth: 55 },
            },
            // Seven narrow columns stretched across the full page width
            // read as a scatter of numbers; size this one to its content.
            { tableWidth: 'wrap' },
        );

        // ── Fuel surcharge calculation ──
        // Two tables, because the surcharge is two questions. First, WHICH
        // equipment it applies to: the adder recovers diesel, so equipment
        // that burns none (towed trailers, excavator attachments, hand tools)
        // is held out of the base and the summary shows exactly what came
        // out. Second, at WHAT RATE: one row per adder period the remaining
        // equipment's work dates fall in — the EIA quarter average, the index
        // adder it lands on, and the base it applies to.
        const fx = sum.fuel.excl || { noFuel: { base: 0, n: 0 }, fuelBuy: { base: 0, n: 0 }, passThru: { base: 0, n: 0 } };
        const fUnk = sum.fuel.unk || { base: 0, n: 0 };
        const equipAll = round2(sum.fuel.base + fx.noFuel.base + fx.fuelBuy.base + fx.passThru.base);
        const equipAllN = sum.fuel.n + fx.noFuel.n + fx.fuelBuy.n + fx.passThru.n;
        if (sum.fuel.periods.length || fx.noFuel.n || fx.fuelBuy.n) {
            if (y + 200 > H - M) { doc.addPage(); y = M; }
            // Which equipment the adder applies to. Reconciles top to bottom:
            // everything billed as equipment, less what carries no fuel, is
            // the base the percentage runs on.
            rollupTable(
                'FUEL SURCHARGE — WHAT THE ADDER APPLIES TO',
                ['Equipment on This Summary', 'Lines', 'Amount'],
                [
                    ['Equipment billed', String(equipAllN), money(equipAll)],
                    ...(fx.noFuel.n ? [[
                        'Less: equipment that burns no fuel (per rate sheet)',
                        String(fx.noFuel.n), `(${money(fx.noFuel.base)})`,
                    ]] : []),
                    ...(fx.fuelBuy.n ? [[
                        'Less: fuel purchases billed at cost (52300)',
                        String(fx.fuelBuy.n), `(${money(fx.fuelBuy.base)})`,
                    ]] : []),
                    ...(fx.passThru.n ? [[
                        'Less: pass-through freight/delivery, re-billed at cost',
                        String(fx.passThru.n), `(${money(fx.passThru.base)})`,
                    ]] : []),
                    ['Surcharge base (fuel burning equipment)', String(sum.fuel.n), money(sum.fuel.base)],
                ],
                { 0: { cellWidth: 240 }, 1: { ...R, cellWidth: 45 }, 2: { ...R, cellWidth: 85 } },
                { tableWidth: 'wrap' },
            );
        }
        if (sum.fuel.periods.length) {
            if (y + 120 > H - M) { doc.addPage(); y = M; }
            const price3 = (p) => (p.price != null ? `$${Number(p.price).toFixed(3)}` : 'not entered');
            rollupTable(
                'FUEL SURCHARGE — RATE BY PERIOD',
                ['Equipment Work Dates', 'EIA Price Quarter', 'Gulf Coast Diesel Avg $/gal', 'Equipment % Adder', 'Lines', 'Fuelled Equipment', 'Fuel Surcharge'],
                [
                    ...sum.fuel.periods.map((p) => [
                        p.span || '', p.q || '', price3(p), fmtPct(p.pct), String(p.n), money(p.base), money(p.amt),
                    ]),
                    ['Total', '', '', '', String(sum.fuel.n), money(sum.fuel.base), money(sum.fuel.amt)],
                ],
                {
                    0: { cellWidth: 85 }, 1: { cellWidth: 60 }, 2: { ...R, cellWidth: 80 }, 3: { ...R, cellWidth: 65 },
                    4: { ...R, cellWidth: 35 }, 5: { ...R, cellWidth: 80 }, 6: { ...R, cellWidth: 75 },
                },
                { tableWidth: 'wrap' },
            );
            const threshold = parseFuelIndex(fuelCtx.rows).below?.price;
            const notes = [
                'Equipment % adder per the Fuel Surcharge Index: the average of the 13 weekly U.S. EIA Gulf Coast (PADD 3) on-highway diesel prices for a quarter sets the adder that applies from the 1st of the month after that quarter ends.',
                `The adder applies to equipment subtotals only — not labor, per diem, or materials${threshold ? `; no credit applies below $${threshold.toFixed(2)} per gallon` : ''}.`,
                ...(fx.noFuel.n ? [`Equipment that carries no engine of its own — towed trailers, machine attachments, rigging and hand tools — is held out of the base: ${fx.noFuel.n} line${fx.noFuel.n === 1 ? '' : 's'}, ${money(fx.noFuel.base)}. The classification is per equipment row on the job's rate schedule.`] : []),
                ...(sum.fuel.periods.some((p) => p.frozen) ? ['Adders on lines already on this summary are the rates in force when each line was added.'] : []),
                ...(fuelCtx.settings?.exFuel !== false ? ['Fuel purchases (cost type 52300) bill at cost and are not part of the surcharge base.'] : []),
                ...(fUnk.n ? [`INTERNAL: ${fUnk.n} line${fUnk.n === 1 ? '' : 's'} (${money(fUnk.base)}) are equipment that does not map to a rate schedule row, so nothing classifies them as fuelled or not — they are in the base by default. Map the tier in Rates & Rules to settle it.`] : []),
                ...(sum.fuel.missing.length ? [`No EIA average entered for ${sum.fuel.missing.join(', ')} — those lines carry no surcharge on this summary.`] : []),
            ];
            doc.setFont('helvetica', 'normal').setFontSize(6.5).setTextColor('#475569');
            notes.forEach((n) => {
                const wrapped = doc.splitTextToSize(n, W - 2 * M);
                doc.text(wrapped, M, y - 8);
                y += wrapped.length * 8;
            });
            y += 12;
        }

        // ── Attachment index ──
        // What evidence rides with this backup and where it sits. One row per
        // document however many lines stand behind it, so an invoice that paid
        // for six cost rows is listed — and printed — once.
        if (entries.length) {
            if (y + 90 > H - M) { doc.addPage(); y = M; }
            doc.setFont('helvetica', 'bold').setFontSize(9).setTextColor('#0f172a');
            doc.text('ATTACHMENT INDEX', M, y + 8);
            const attLinks = entries.map((e) => e.url || '');
            const whereText = (e) => {
                if (e.status === 'merge' && e.startPage) {
                    const n = e.pages || 1;
                    return n > 1 ? `pages ${e.startPage}–${e.startPage + n - 1}` : `page ${e.startPage}`;
                }
                if (e.status === 'attach') return 'attached to the email';
                // Not in the file — say why, and point at the copy that exists.
                const src = !e.url ? '' : e.kind === 'invoice' ? ' — open in TAP' : ' — open from SharePoint';
                if (e.why === 'type') return `${(attExt(e.name) || 'this file type').toUpperCase()} can't go in a PDF${src}`;
                if (e.why === 'size') return `too large to embed${src}`;
                if (e.why === 'corrupt') return `wouldn't open${src}`;
                if (e.why === 'nolink') return 'no document link on the cost row';
                return `couldn't be pulled in${src}`;
            };
            autoTable(doc, {
                startY: y + 14,
                margin: { left: M, right: M, top: M },
                head: [['Exhibit', 'Type', 'Document', 'Backs', 'Pages', 'Where in this PDF']],
                body: entries.map((e) => [
                    e.label,
                    e.kind === 'invoice' ? 'Vendor invoice' : e.kind === 'receiving' ? 'Receiving doc' : 'Timecard',
                    e.title || e.name || '',
                    e.kind === 'invoice' || e.kind === 'receiving'
                        ? `${e.keys.length} line${e.keys.length === 1 ? '' : 's'}`
                        : (tcFileDate(e.file) ? fDate(tcFileDate(e.file)) : ''),
                    e.status === 'merge' ? String(e.pages || 1) : '',
                    whereText(e),
                ]),
                styles: { font: 'helvetica', fontSize: 7, cellPadding: 3, textColor: '#0f172a', lineColor: '#e2e8f0', lineWidth: 0.5, overflow: 'linebreak' },
                headStyles: { fillColor: '#f1f5f9', textColor: '#475569', fontStyle: 'bold', fontSize: 6.5 },
                columnStyles: { 0: { cellWidth: 50, fontStyle: 'bold' }, 1: { cellWidth: 70 }, 3: { cellWidth: 70 }, 4: { cellWidth: 40, halign: 'right' }, 5: { cellWidth: 110 } },
                didParseCell: (d) => {
                    if (d.section !== 'body') return;
                    if (d.column.index === 2 && attLinks[d.row.index]) d.cell.styles.textColor = '#1d4ed8';
                    if (d.column.index === 5 && entries[d.row.index].status !== 'merge') d.cell.styles.textColor = '#b45309';
                },
                didDrawCell: (d) => {
                    if (d.section !== 'body' || d.column.index !== 2) return;
                    const url = attLinks[d.row.index];
                    if (url) doc.link(d.cell.x, d.cell.y, d.cell.width, d.cell.height, { url });
                },
            });
            y = doc.lastAutoTable.finalY + 18;
        }

        // ── Line detail, from a fresh page ──
        doc.addPage();
        y = M;
        doc.setFont('helvetica', 'bold').setFontSize(13).setTextColor('#0f172a');
        doc.text(`Billing Summary${tag ? ' ' + tag : ''} — ${selectedJob} — backup detail`, M, M + 6);
        doc.setFont('helvetica', 'normal').setFontSize(8).setTextColor('#475569');
        doc.text(`Prepared ${stamp} by ${userName || ''}   ·   ${lineCount} line${lineCount === 1 ? '' : 's'}${sum.hrs ? `   ·   ${round1(sum.hrs)} hours` : ''}   ·   cost ${money(sum.cost)}`, M, M + 20);
        y = M + 34;
        // Which exhibit backs each line, and the page it starts on. Several
        // lines commonly share one document — they all point at the same page
        // — and a labor day covered by two crew sheets lists both exhibits.
        const refsByKey = {};
        entries.forEach((e) => (e.keys || []).forEach((k) => { (refsByKey[k] = refsByKey[k] || []).push(e); }));
        const refText = (t) => {
            const refs = (t.key && refsByKey[t.key]) || [];
            if (!refs.length) return t.pdf ? 'TAP link' : '';
            return refs
                .map((e) => (e.status === 'merge' && e.startPage ? `${e.label} p.${e.startPage}` : `${e.label} link`))
                .join('\n');
        };
        // Each section prints only the columns that mean something for what
        // it is: a PO number or vendor invoice on a labor line, or hours on
        // a materials line, is noise to the AR reader — so it isn't there.
        // `sub` marks the numeric tail the subtotal row fills in.
        const dateCol = { h: 'Work Date', v: (t) => fDate(t.date) };
        const codeCols = [
            { h: 'Cost Code', v: (t) => t.cc },
            { h: 'Cost Type', v: (t) => (t.ctDesc ? `${t.ct} ${t.ctDesc}` : String(t.ct ?? '')) },
        ];
        const backupCol = { h: 'Backup', v: refText, w: 48, backup: true };
        const vinvCol = { h: 'Vendor Inv # (TAP)', v: (t) => t.vinvNo || (t.pdf ? 'TAP' : ''), link: true };
        const moneyCols = [
            { h: 'Cost', v: (t) => money(t.cost), right: true, sub: 'cost' },
            { h: 'Rate / Markup', v: (t) => (t.hourly ? rateText(t) || 'rate' : t.pt && !pNum(t.mk) ? 'at cost' : `+${t.mk}%`), right: true, sub: 'blank' },
            { h: 'Bill Amount', v: (t) => money(t.bill), right: true, sub: 'bill' },
        ];
        const hoursCol = { h: 'Hours', v: hoursText, right: true, sub: 'hrs' };
        const GROUP_COLS = {
            Labor: [
                dateCol,
                // The rate sheet row a person bills as rides under the name,
                // for reconciling against the sheet.
                { h: 'Employee', v: (t) => (t.ba ? `${t.who || ''}\nas ${t.ba}` : (t.who || '')), w: 92 },
                ...codeCols,
                { h: 'Description', v: (t) => t.ccDesc || t.ds || '', w: 84 },
                backupCol, hoursCol, ...moneyCols,
            ],
            Equipment: [
                dateCol,
                // Unit and rate sheet item only: the client reads the rate
                // sheet, not the JDE tier the unit costs under.
                { h: 'Equipment', v: (t) => clientEquipLabel(t, equipBills), w: 92 },
                ...codeCols,
                { h: 'Description', v: (t) => t.ccDesc || t.ds || '', w: 78 },
                { h: 'PO #', v: (t) => t.po || '' },
                vinvCol, backupCol, hoursCol, ...moneyCols,
            ],
            'Third Party': [
                dateCol,
                { h: 'Vendor', v: (t) => t.who || '', w: 92 },
                ...codeCols,
                { h: 'Description', v: (t) => t.ccDesc || t.ds || '', w: 88 },
                { h: 'PO #', v: (t) => t.po || '' },
                vinvCol, backupCol, ...moneyCols,
            ],
            // Freight/delivery: the PO line's own description ("Freight In")
            // is what says why it's here, so it leads over the phase name.
            [PT_GROUP]: [
                dateCol,
                { h: 'Vendor', v: (t) => t.who || '', w: 92 },
                ...codeCols,
                { h: 'Description', v: (t) => t.ds || t.ccDesc || '', w: 88 },
                { h: 'PO #', v: (t) => t.po || '' },
                vinvCol, backupCol, ...moneyCols,
            ],
        };
        sum.groups.forEach((g) => {
            if (y + 60 > H - M) { doc.addPage(); y = M; }
            doc.setFont('helvetica', 'bold').setFontSize(9).setTextColor('#0f172a');
            doc.text(String(g.g).toUpperCase(), M, y + 8);
            const cols = GROUP_COLS[g.g] || GROUP_COLS['Third Party'];
            const body = g.lines.map((t) => cols.map((c) => c.v(t)));
            const tail = cols.filter((c) => c.sub).map((c) => (
                c.sub === 'hrs' ? (g.hrs ? String(round1(g.hrs)) : '')
                    : c.sub === 'cost' ? money(g.cost)
                    : c.sub === 'bill' ? money(g.bill) : ''));
            body.push([
                { content: `${g.g} subtotal — ${g.lines.length} line${g.lines.length === 1 ? '' : 's'}`, colSpan: cols.length - tail.length },
                ...tail,
            ]);
            // Equipment carries the fuel surcharge under its subtotal — one
            // row per adder period, landing in the Rate / Markup and Bill
            // Amount columns — then the section total with it in.
            if (g.g === 'Equipment' && sum.fuel.amt) {
                const lead = cols.length - 2;
                sum.fuel.periods.filter((p) => p.amt).forEach((p) => body.push([
                    {
                        content: `Fuel surcharge — ${p.span} work, EIA Gulf Coast diesel ${p.q} avg ${p.price != null ? '$' + Number(p.price).toFixed(3) : 'n/a'}/gal — ${fmtPct(p.pct)} × ${money(p.base)} equipment`,
                        colSpan: lead,
                    },
                    `+${fmtPct(p.pct)}`,
                    money(p.amt),
                ]));
                body.push([
                    { content: 'Equipment total with fuel surcharge', colSpan: lead + 1 },
                    money(g.bill + sum.fuel.amt),
                ]);
            }
            const columnStyles = {};
            cols.forEach((c, i) => {
                const s = {};
                if (c.right) s.halign = 'right';
                if (c.w) s.cellWidth = c.w;
                if (Object.keys(s).length) columnStyles[i] = s;
            });
            const linkIdx = cols.indexOf(vinvCol);
            const backupIdx = cols.indexOf(backupCol);
            // Vendor invoice number links to the scanned invoice in TAP when the
            // cost row carries a media-object URL.
            const links = g.lines.map((t) => t.pdf || null);
            autoTable(doc, {
                startY: y + 14,
                margin: { left: M, right: M, top: M },
                head: [cols.map((c) => c.h)],
                body,
                styles: { font: 'helvetica', fontSize: 6.5, cellPadding: 2.5, textColor: '#0f172a', lineColor: '#e2e8f0', lineWidth: 0.5, overflow: 'linebreak' },
                headStyles: { fillColor: '#f1f5f9', textColor: '#475569', fontStyle: 'bold', fontSize: 6 },
                columnStyles,
                didParseCell: (d) => {
                    if (d.section !== 'body') return;
                    if (d.row.index >= g.lines.length) {
                        d.cell.styles.fontStyle = 'bold';
                        d.cell.styles.fillColor = '#f8fafc';
                    } else if (d.column.index === linkIdx && links[d.row.index]) {
                        d.cell.styles.textColor = '#1d4ed8';
                    } else if (d.column.index === backupIdx && d.cell.raw) {
                        // Green where every document is in this file, amber
                        // where any only got as far as a link.
                        const raw = String(d.cell.raw);
                        d.cell.styles.textColor = raw.includes(' p.') && !raw.includes('link') ? '#166534' : '#b45309';
                    }
                },
                didDrawCell: (d) => {
                    if (d.section !== 'body' || d.column.index !== linkIdx) return;
                    const url = links[d.row.index];
                    if (url) doc.link(d.cell.x, d.cell.y, d.cell.width, d.cell.height, { url });
                },
            });
            y = doc.lastAutoTable.finalY + 18;
        });
        if (y + 24 > H - M) { doc.addPage(); y = M; }
        doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor('#0f172a');
        doc.text(`Total to invoice: ${money(sum.bill)}`, W - M, y + 8, { align: 'right' });
        if (sum.fuel.amt) {
            doc.setFont('helvetica', 'normal').setFontSize(8).setTextColor('#475569');
            doc.text(`Lines ${money(sum.lineBill)} + equipment fuel surcharge ${money(sum.fuel.amt)}`, W - M, y + 20, { align: 'right' });
        }
        return doc;
    };

    // The job's SharePoint timecards: the catalog the backup bundle
    // fills when it's already loaded, otherwise straight off the function.
    const jobTimecardFiles = async (job) => {
        if (tcCat.job === job && tcCat.data) return tcCat.data.files || [];
        try {
            const res = await loadJobTimecards({ job });
            setTcCat({ job, loading: false, data: res || {} });
            return res?.files || [];
        } catch (e) {
            setTcCat({ job, loading: false, data: { error: true } });
            return [];
        }
    };

    // The job's receiving-doc coordinates out of JDE's F00165 index, cached
    // per job. GT4311 only — the PO-detail structure receiving is filed on;
    // GT4301 header attachments are quotes and PO prints, not billing
    // evidence. A manifest failure means the backup goes out without
    // receiving docs, not that it fails.
    const jobReceivingDocs = async (job) => {
        if (moCat.job === job && moCat.docs) return moCat.docs;
        try {
            const data = await loadJdeBackupManifest({ job, obj: 'GT4311' });
            const docs = data?.docs || [];
            setMoCat({ job, docs });
            return docs;
        } catch (e) {
            setMoCat({ job, docs: [] });
            return [];
        }
    };

    // The backup PDF with its evidence bound in: the deduped scanned invoices
    // behind the lines first, then the timecards, each document once. Returns
    // the merged bytes — null when there was nothing to merge or the merge
    // wasn't possible, in which case the caller still has the base `doc` — and
    // the plan, so the caller can report what made it in.
    const buildBackupBundle = async (sum, stamp, tag, opts = {}) => {
        // A timecard backs every labor line worked on the day it covers, the
        // way a scanned invoice backs the lines it paid for — that's what puts
        // "TC-3 p.12" on a labor line. Two crews' sheets on one day both claim
        // that day's lines; the reader finds the name on one of them.
        const laborKeys = {};
        (sum.groups.find((g) => g.g === 'Labor')?.lines || []).forEach((t) => {
            const d = String(t.date || '').slice(0, 10);
            if (d && t.key) (laborKeys[d] = laborKeys[d] || []).push(t.key);
        });
        const entries = labelAttachments([
            ...(opts.invoices === false ? [] : invoiceDocsFor(sum)),
            ...receivingDocsFor(sum, opts.receiving || []),
            ...(opts.timecards || []).map((f) => ({
                kind: 'timecard', file: f, name: f.name, size: f.size,
                // The label says what the evidence proves, not what the file
                // is called — AR reads "crew timecard for the 3rd", the
                // SharePoint filename is nobody's concern.
                title: tcFileDate(f)
                    ? `Crew timecard — ${fDate(tcFileDate(f))}`
                    : `Crew timecard — ${String(f.name || '').replace(/\.\w+$/, '')}`,
                proxy: tcProxyUrl(f), url: f.webUrl || '', keys: laborKeys[tcFileDate(f)] || [],
            })),
        ]);
        await loadAttachmentBytes(entries, {
            maxBytes: opts.maxBytes,
            canAttachSeparately: opts.canAttachSeparately,
        });

        // The line detail prints the page each document starts on, and the
        // base document's own length is what those numbers count from — so
        // lay out, render, and lay out again until the base stops moving.
        // Two passes in practice: the column's width doesn't change with the
        // digits in it.
        let basePages = 0;
        let doc = null;
        for (let pass = 0; pass < 3; pass += 1) {
            layoutAttachments(entries, basePages);
            doc = await buildBackupPdf(sum, stamp, tag, entries);
            const n = doc.internal?.getNumberOfPages?.() || 0;
            if (!n || n === basePages) break;
            basePages = n;
        }

        let bytes = null;
        if (entries.some((e) => e.status === 'merge')) {
            try {
                bytes = await mergeAttachments(doc.output('arraybuffer'), entries);
            } catch (e) {
                // pdf-lib unavailable or the merge blew up wholesale — the
                // backup still goes out, with its evidence demoted to links.
                entries.forEach((x) => { if (x.status === 'merge') x.status = 'link'; });
                layoutAttachments(entries, basePages);
                doc = await buildBackupPdf(sum, stamp, tag, entries);
                bytes = null;
            }
        }
        return { doc, bytes, entries };
    };

    // ArrayBuffer → base64 for email attachments; btoa chokes on large
    // buffers passed whole, so feed it in chunks.
    const bufToB64 = (buf) => {
        const bytes = new Uint8Array(buf);
        const CHUNK = 0x8000;
        let bin = '';
        for (let i = 0; i < bytes.length; i += CHUNK) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
        return btoa(bin);
    };

    // Excel workbook for a summary's lines (tag = its number). Shared by the
    // Summaries view download and the FTMS hand-off attachment.
    const buildSummaryWorkbook = async (rowsIn, tag) => {
        // Not-billable lines never belong on a summary — belt and braces in
        // case a caller ever hands one in.
        const rows = rowsIn.filter((t) => !t.nb);
        const sum = summaryOf(rows);
        const stamp = moment().format('YYYY-MM-DD');

        const ExcelJS = (await import('exceljs')).default;
        const wb = new ExcelJS.Workbook();
        wb.creator = 'Fusion Billing';
        const MONEY = '"$"#,##0.00';
        const HOURS = '#,##0.0';
        const headFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
        const headFont = { bold: true, size: 9, color: { argb: 'FF475569' } };

        // Sheet 1 — the rollup of the summary's lines.
        const ws = wb.addWorksheet('Summary');
        ws.columns = [{ width: 22 }, { width: 14 }, { width: 42 }, { width: 9 }, { width: 10 }, { width: 14 }, { width: 14 }];
        ws.addRow([`Billing Summary${tag ? ' ' + tag : ''}`]).font = { bold: true, size: 14 };
        [['Job', selectedJob], ...(tag ? [['Summary #', tag]] : []), ['Prepared', stamp], ['Prepared By', userName || ''], ['Lines', rows.length]]
            .forEach((kv) => {
                const r = ws.addRow(kv);
                r.getCell(1).font = { bold: true, size: 10, color: { argb: 'FF64748B' } };
            });

        // One labeled table: styled header row, data rows with number formats
        // (fmts keys are 1-based column indexes), optional bold total row.
        const section = (label, header, dataRows, fmts, total) => {
            ws.addRow([]);
            ws.addRow([label]).font = { bold: true, size: 11 };
            ws.addRow(header).eachCell((c) => {
                c.font = headFont;
                c.fill = headFill;
                c.border = { bottom: { style: 'thin', color: { argb: 'FFCBD5E1' } } };
            });
            const applyFmts = (r) => Object.entries(fmts).forEach(([i, z]) => { r.getCell(Number(i)).numFmt = z; });
            dataRows.forEach((vals) => applyFmts(ws.addRow(vals)));
            if (total) {
                const t = ws.addRow(total);
                t.font = { bold: true };
                applyFmts(t);
                header.forEach((_, i) => { t.getCell(i + 1).border = { top: { style: 'thin', color: { argb: 'FF94A3B8' } } }; });
            }
        };
        section('Groups', ['Group', 'Lines', 'Hours', 'Cost', 'Markup', 'Bill Amount'],
            [
                ...sum.groups.map((g) => [g.g, g.lines.length, round1(g.hrs), g.cost, g.bill - g.cost, g.bill]),
                ...(sum.fuel.amt ? [['Fuel Surcharge (Equipment)', '', '', '', sum.fuel.amt, sum.fuel.amt]] : []),
            ],
            { 3: HOURS, 4: MONEY, 5: MONEY, 6: MONEY },
            ['Total', rows.length, round1(sum.hrs), sum.cost, sum.bill - sum.cost, sum.bill]);
        // Same two-part story the PDF cover tells: what the adder applies to,
        // then the rate it applied at.
        const fx = sum.fuel.excl || { noFuel: { base: 0, n: 0 }, fuelBuy: { base: 0, n: 0 }, passThru: { base: 0, n: 0 } };
        if (sum.fuel.periods.length || fx.noFuel.n || fx.fuelBuy.n) {
            section('Fuel Surcharge — What the Adder Applies To', ['Equipment on This Summary', 'Lines', 'Amount'],
                [
                    ['Equipment billed', sum.fuel.n + fx.noFuel.n + fx.fuelBuy.n + fx.passThru.n, round2(sum.fuel.base + fx.noFuel.base + fx.fuelBuy.base + fx.passThru.base)],
                    ...(fx.noFuel.n ? [['Less: equipment that burns no fuel (per rate sheet)', fx.noFuel.n, -fx.noFuel.base]] : []),
                    ...(fx.fuelBuy.n ? [['Less: fuel purchases billed at cost (52300)', fx.fuelBuy.n, -fx.fuelBuy.base]] : []),
                    ...(fx.passThru.n ? [['Less: pass-through freight/delivery, re-billed at cost', fx.passThru.n, -fx.passThru.base]] : []),
                ],
                { 3: MONEY },
                ['Surcharge base (fuel burning equipment)', sum.fuel.n, sum.fuel.base]);
        }
        if (sum.fuel.periods.length) {
            section('Fuel Surcharge — Rate by Period', ['Equipment Work Dates', 'EIA Price Quarter', 'Gulf Coast Diesel Avg $/gal', 'Adder', 'Lines', 'Fuelled Equipment', 'Fuel Surcharge'],
                sum.fuel.periods.map((p) => [p.span, p.q, p.price != null ? p.price : 'not entered', p.pct / 100, p.n, p.base, p.amt]),
                { 3: '"$"0.000', 4: '0.0%', 6: MONEY, 7: MONEY },
                ['Total', '', '', '', sum.fuel.n, sum.fuel.base, sum.fuel.amt]);
        }
        section('By Cost Type', ['Group', 'Cost Type', 'Description', 'Lines', 'Hours', 'Cost', 'Bill Amount'],
            sum.groups.flatMap((g) => g.types.map((c) => [g.g, c.ct, c.desc, c.n, round1(c.hrs), c.cost, c.bill])),
            { 5: HOURS, 6: MONEY, 7: MONEY });
        section('By Cost Code', ['Group', 'Cost Code', 'Description', 'Lines', 'Hours', 'Cost', 'Bill Amount'],
            sum.groups.flatMap((g) => g.codes.map((c) => [g.g, c.cc, c.desc, c.n, round1(c.hrs), c.cost, c.bill])),
            { 5: HOURS, 6: MONEY, 7: MONEY });
        // Labor by billed-as classification — the rollup AR reconciles against
        // the rate sheet. Only when labor lines exist on the summary.
        const laborClasses = sum.groups.flatMap((g) => g.classes || []);
        if (laborClasses.length) {
            section('Labor by Rate Sheet Classification', ['Classification', 'JDE Classification(s)', 'Lines', 'Hours', 'Cost', 'Bill Amount'],
                laborClasses.map((c) => [c.ba, c.jde, c.n, round1(c.hrs), c.cost, c.bill]),
                { 4: HOURS, 5: MONEY, 6: MONEY });
        }

        // Sheet 2 — one row per line, filterable, header frozen.
        const detail = sum.groups.flatMap((g) => g.lines.map((t) => ({ Group: g.g, ...detailRow(t) })));
        const keys = detail.length ? Object.keys(detail[0]) : [];
        const wsD = wb.addWorksheet('Detail', { views: [{ state: 'frozen', ySplit: 1 }] });
        const widths = {
            Group: 12, 'Work Date': 11, 'Cost Code': 10, 'Cost Code Description': 28, Class: 9, 'Cost Type': 9,
            'Pass-Through': 8, 'Cost Type Description': 24, 'Employee / Vendor': 24, 'JDE Classification': 20, 'Bills As': 24, Equipment: 14, 'PO Number': 10, 'P-Card': 7,
            'Vendor Invoice Status': 13, 'Vendor Invoice #': 15, 'Vendor Invoice Date': 13, 'TAP Invoice Link': 16,
            Description: 34, Hours: 8, Cost: 12, Rate: 11, 'Markup %': 9, 'Bill Amount': 12, Status: 10,
            'Not Billable Reason': 18, 'Invoice #': 10, 'Review Status': 12, 'Reviewed By': 16, 'Reviewed On': 11, 'Last Note': 28,
        };
        wsD.columns = keys.map((k) => ({ header: k, key: k, width: widths[k] || 14 }));
        detail.forEach((d) => wsD.addRow(d));
        wsD.getRow(1).eachCell((c) => { c.font = headFont; c.fill = headFill; });
        Object.entries({ Cost: MONEY, 'Bill Amount': MONEY, Hours: '#,##0.00' }).forEach(([k, z]) => {
            const i = keys.indexOf(k);
            if (i >= 0) wsD.getColumn(i + 1).numFmt = z;
        });
        if (keys.length) wsD.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: keys.length } };

        const buffer = await wb.xlsx.writeBuffer();
        // Lead with the summary number so the file is identifiable in a
        // downloads folder or an email full of attachments.
        return { buffer, sum, stamp, filename: `${tag ? tag + '_' : ''}Billing_Summary_${selectedJob}_${stamp}.xlsx` };
    };

    const downloadSummaryExcel = async (rows, tag) => {
        if (!rows.length) return;
        try {
            const { buffer, sum, filename } = await buildSummaryWorkbook(rows, tag);
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
            showToast(`Excel summary downloaded — ${rows.length} lines, ${money(sum.bill)}`, 'success');
        } catch (e) {
            showToast(`Excel export failed — ${e?.message || e}`, 'error');
        }
    };

    // PDF backup for the same lines — a direct download, no print dialog.
    // The evidence comes with it: every scanned vendor invoice behind the
    // lines (once each, however many lines it paid for) and the timecards
    // covering the labor dates, bound in behind the line detail.
    const downloadSummaryPdf = async (rows, tag) => {
        if (!rows.length) return;
        try {
            const stamp = moment().format('YYYY-MM-DD');
            const sum = summaryOf(rows);
            const [files, moDocs] = await Promise.all([
                jobTimecardFiles(selectedJob).then((f) => timecardsInRange(f, rows)),
                jobReceivingDocs(selectedJob),
            ]);
            const planned = invoiceDocsFor(sum).length + receivingDocsFor(sum, moDocs).length + files.length;
            if (planned) showToast(`Building the backup — pulling ${planned} document${planned === 1 ? '' : 's'}…`, 'info');
            const { doc, bytes, entries } = await buildBackupBundle(sum, stamp, tag, {
                timecards: files, receiving: moDocs, maxBytes: ATT_MAX_BYTES,
            });
            const name = `${tag ? tag + '_' : ''}Billing_Summary_${selectedJob}_${stamp}_Backup.pdf`;
            if (bytes) {
                const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
                const a = document.createElement('a');
                a.href = url;
                a.download = name;
                a.click();
                URL.revokeObjectURL(url);
            } else {
                // Nothing to bind in (or nothing that would bind) — jsPDF's own
                // save is the shortest path to the same file.
                doc.save(name);
            }
            if (!entries.length) return;
            const inv = entries.filter((e) => e.kind === 'invoice' && e.status === 'merge').length;
            const rcv = entries.filter((e) => e.kind === 'receiving' && e.status === 'merge').length;
            const tc = entries.filter((e) => e.kind === 'timecard' && e.status === 'merge').length;
            const missed = entries.filter((e) => e.status !== 'merge').length;
            showToast(
                `Backup downloaded — ${inv} invoice${inv === 1 ? '' : 's'}, ${rcv} receiving doc${rcv === 1 ? '' : 's'}, ${tc} timecard${tc === 1 ? '' : 's'} bound in${missed ? `, ${missed} linked only` : ''}`,
                missed ? 'warn' : 'success',
            );
        } catch (e) {
            showToast(`PDF export failed — ${e?.message || e}`, 'error');
        }
    };

    // Push a summary into FTMS's Create New Invoice wizard. Builds the same
    // two documents a summary produces (the summary workbook and the backup
    // detail PDF), hands them to the FTMS browser extension along with the
    // header fields and one Unit Price line per invoice group, and opens the
    // wizard. The extension fills General / Attachment / Line Items and stops
    // — nobody but a human submits a client invoice. Without the extension
    // installed the payload downloads as a .fusion-ftms.json to load from the
    // extension's popup, so the button still does something useful.
    const uploadToFtms = async (v) => {
        const rows = summaryLines(v);
        if (!rows.length) {
            showToast(`No lines found for ${v.no} — nothing to send to FTMS`, 'warn');
            return;
        }
        let payload;
        try {
            // The workbook build is what produces the summaryOf() rollup and
            // the canonical filename stem, so it runs even though only the
            // PDF is attached.
            const { sum, filename } = await buildSummaryWorkbook(rows, v.no);
            const stamp = moment().format('YYYY-MM-DD');
            const [files, moDocs] = await Promise.all([
                jobTimecardFiles(selectedJob).then((f) => timecardsInRange(f, rows)),
                jobReceivingDocs(selectedJob),
            ]);
            // The same backup the Summaries PDF button produces — evidence and
            // all, so what FTMS carries matches what we filed.
            const bundle = await buildBackupBundle(sum, stamp, v.no, {
                timecards: files, receiving: moDocs, maxBytes: FTMS_MAX_BYTES,
            });
            payload = buildFtmsPayload({
                summary: v,
                sum,
                lines: rows,
                job: selectedJob,
                jobData: selectedJobData,
                userName,
                // PDF only — FTMS's attachment input is accept="application/pdf".
                // The workbook isn't lost: it's the Excel button one slot to
                // the left.
                attachments: [
                    {
                        name: filename.replace(/\.xlsx$/, '_Backup.pdf'),
                        mime: 'application/pdf',
                        b64: bufToB64(bundle.bytes || bundle.doc.output('arraybuffer')),
                    },
                ],
            });
        } catch (e) {
            showToast(`Could not build the FTMS handoff — ${e?.message || e}`, 'error');
            return;
        }
        // A few jobs carry no Client_Name on the job master. FTMS needs a
        // client before the invoice will validate, so flag it here rather
        // than letting the wizard open and stall.
        if (!payload.invoice.clientName) {
            showToast(`No client on job ${selectedJob} — you'll have to pick Client in FTMS by hand`, 'warn');
        }
        const { via } = await sendToFtms(payload);
        if (via === 'extension') {
            // The extension opens the wizard itself once it has the payload,
            // so opening it here too would leave a stray blank tab.
            const msg = `${v.no} sent to FTMS — review the wizard and submit`;
            // The hand-off is the stage change: a summary still on our side
            // moves to Uploaded to FTMS on the way out. Re-sending one that is
            // already uploaded (or past it) leaves the stage alone.
            if (!versionLocked && (v.status === 'Draft' || v.status === 'Kicked Back')) {
                setInvoiceStatus(v, 'Uploaded to FTMS', '', msg);
            } else {
                showToast(msg, 'success');
            }
            return;
        }
        window.open(FTMS_ADD_URL, '_blank', 'noopener');
        showToast(`FTMS extension not detected — handoff file downloaded. Load it from the extension popup.`, 'warn');
    };

    // Retired with the AR hand-off stage — nothing calls this now that the

    // Lines behind a stored summary: the live cost-history rows billed to it,
    // falling back to the frozen snapshots if cost history no longer carries a
    // row (GL corrections change the row hash and orphan the snapshot).
    // Descriptions come off every transaction — billed lines and labor are
    // out of the Third Party grid's filter options but still on summaries.
    const { phaseDesc, ctypeDesc } = useMemo(() => {
        const pd = {};
        const cd = {};
        txns.forEach((t) => {
            if (t.cc && t.ccDesc && !pd[t.cc]) pd[t.cc] = t.ccDesc;
            if (t.ct && t.ctDesc && !cd[t.ct]) cd[t.ct] = t.ctDesc;
        });
        return { phaseDesc: pd, ctypeDesc: cd };
    }, [txns]);

    // A frozen summary line as a txn-shaped row.
    const snapRow = (l, v) => {
        const cost = pNum(l.c);
        return {
            // The snapshot never froze the phase description — look it up
            // from the job's live cost history so the backup's phase-code
            // rollup still names each code.
            date: l.d, cc: l.cc || '', ccDesc: phaseDesc[l.cc] || '', ct: l.ct || '', ctDesc: ctypeDesc[l.ct] || '',
            cls: bucketOf(l.ct), who: l.who || '', eq: '', po: l.po || '',
            // The snapshot never froze the TAP link (the Notes blob is
            // capped) — look it up live off the cost-history key the
            // snapshot does carry, the same way the invoice detail does.
            key: l.key, vinv: '', vinvDate: '', vinvNo: l.vn || '', ds: l.ds || '', pdf: pdfByKey[l.key] || '',
            jde: l.lc || '', ba: l.ba || '', pt: !!l.pt,
            hrs: pNum(l.hr), cost, mk: pNum(l.m), hourly: l.b != null, fs: l.fs,
            bill: l.b != null ? pNum(l.b) : cost * (1 + pNum(l.m) / 100),
            nb: false, nbReason: '', inv: v.no, rs: '', lastRev: null, hist: [],
        };
    };

    const summaryLines = (v) => {
        const live = txns.filter((x) => x.inv === v.no);
        if (live.length) return live;
        return (invTotals[v.no]?.lines || []).map((l) => snapRow(l, v));
    };

    // An opened invoice's lines as its two grids show them: each frozen
    // line laid over the live line behind it (`src` — its PO line, vendor
    // invoice and history, and what the line fixes act on), so a row reads
    // what the summary bills. `le` are the lines the Labor & Equipment grid
    // holds (jdeClaims), `tp` the rest, for the Third Party grid.
    const invoiceRows = (v) => {
        const byKey = new Map(txns.filter((x) => x.inv === v.no).map((x) => [x.key, x]));
        const le = [];
        const tp = [];
        (invTotals[v.no]?.lines || []).forEach((l) => {
            const src = byKey.get(l.key) || null;
            const cost = pNum(l.c);
            const hrs = pNum(l.hr);
            const hourly = l.b != null;
            const row = {
                ...(src || snapRow(l, v)),
                src,
                date: l.d || src?.date, ds: l.ds || src?.ds || '', who: l.who || src?.who || '',
                cost, hrs, mk: pNum(l.m), hourly, pt: !!l.pt,
                bill: hourly ? pNum(l.b) : cost * (1 + pNum(l.m) / 100),
                // Frozen sheet rate on newer snapshots; derived from the
                // frozen bill for lines snapped before ra/ru existed.
                rateAmt: l.ra || (hourly && hrs ? pNum(l.b) / hrs : 0),
                rateUom: l.ru || (hourly ? 'hr' : ''),
                ba: l.ba || src?.ba || '',
                inv: v.no, nb: false, noRate: false,
            };
            (src && jdeClaims(src) ? le : tp).push(row);
        });
        return { le, tp };
    };

    // Labor QA per summary (billingQa summaryQa) — the row's count tag, the
    // open row's findings and the pre-export checklist's Labor QA step.
    // Only the Summaries view reads it, so it only runs there.
    const sumQa = useMemo(() => {
        if (viewMode !== 'invoices') return {};
        const jobCodes = new Set(txns.map((t) => t.cc).filter(Boolean));
        const pdPolicy = settings?.pd || DEFAULT_SETTINGS.pd;
        // hotelPd: the job's contract bills a hotel stay for someone with no
        // per diem as per diem (billingContract terms), so QA lists them.
        const ctx = { ratemap: laborRatemap, staffCls: fieldStaffCls, rateByCls: sheetLabor.byName, jobCodes, pdRate: pdPolicy.rate, pdPolicy, hotelPd: !!contractRules };
        const out = {};
        invoices.forEach((v) => {
            if (v.status === 'Void') return;
            try {
                const r = summaryQa(v.no, summaryLines(v), txns, ctx);
                if (r) out[v.no] = r;
            } catch (e) { /* a summary the checks can't read just carries no tag */ }
        });
        return out;
        // summaryLines reads txns + invTotals (the lookups it also reads don't feed the checks).
    }, [viewMode, invoices, txns, invTotals, laborRatemap, fieldStaffCls, sheetLabor, settings]);

    // Where each summary's invoice approval stands in DocuSign — the Invoices
    // list's STATUS. The envelope is the 'inv…' approval closing out the
    // summary's work dates (billingRecon.summaryEnvelope — the same match the
    // Reconciliation tab's Invoice column makes). { label, note, env }.
    const sumSign = useMemo(() => {
        const { inv } = statusContext({ envelopes: jobSigs.job === selectedJob ? jobSigs.list : [] });
        const md = (t) => (t ? moment.utc(t).local().format('M/D') : '');
        const out = {};
        invoices.forEach((v) => {
            const dates = (invTotals[v.no]?.lines || []).map((l) => String(l.d || '').slice(0, 10)).filter(Boolean).sort();
            const env = summaryEnvelope({ no: v.no, first: dates[0], last: dates[dates.length - 1] }, inv);
            const st = envStatus(env);
            const sigs = signersOf(env);
            // Out under another number (F00294169 reissued as F00294315): say which.
            const asNo = /^inv(f\d{8})$/i.exec(String(env?.Doc_ID || ''))?.[1].toUpperCase();
            const as = asNo && asNo !== String(v.no || '').toUpperCase() ? ` · as ${asNo}` : '';
            out[v.no] = st === SIG_STATUS.signed ? { label: 'Signed', note: `${md(env.Completed_At_Utc)}${as}`, env }
                : st === SIG_STATUS.rejected ? { label: 'Declined', note: `${md(env.Declined_At_Utc)}${as}`, env }
                    : st === SIG_STATUS.sent || st === SIG_STATUS.routing
                        ? { label: 'In DocuSign', note: `${sigs.length ? `${sigs.filter((x) => x.signed).length} of ${sigs.length} signed` : md(env.Sent_At_Utc)}${as}`, env }
                        // No envelope, a DocuSign draft, or one voided back to the desk.
                        : { label: 'Not Sent', note: '', env: null };
        });
        return out;
    }, [invoices, invTotals, jobSigs, selectedJob]);

    // The summaries the Invoices view lists under the approvals: with
    // invoices in DocuSign, only those still on our side (draft, kicked back)
    // and the one a link opened; with none, every summary, as before.
    const summaryRows = approvalRows.length
        ? invoices.filter((v) => NOT_SENT_STAGES.includes(v.status) || v.no === openInvoice)
        : invoices;
    // An invoice reissued under a new number is one invoice: summary F00294169
    // went out as F00294315, whose approval says it replaces it.
    const invoiceCount = useMemo(() => {
        const replaced = new Set(approvalRows.map((r) => String(r.replaces || '').toUpperCase()).filter(Boolean));
        return new Set([
            ...invoices.filter((v) => v.status !== 'Void').map((v) => String(v.no).toUpperCase()),
            ...approvalRows.map((r) => String(r.no).toUpperCase()),
        ].filter((no) => !replaced.has(no))).size;
    }, [invoices, approvalRows]);

    // ── The pre-export chat (billingAiChat) ──
    // What the assistant in the Pre-Export Checks modal can see, and the
    // three edits it can propose. Every action here is the same write a
    // control on this page already makes — the assistant has no write path
    // of its own, and nothing it proposes moves until someone clicks Apply.
    // Lines are addressed by their index in `rows`, not by key: the keys are
    // long, the model only has to echo a number back, and a stale index
    // resolves to a line that has moved and is refused rather than silently
    // hitting the wrong one.
    const AI_MAX_LINES = 600;
    const exportCheckAi = (inv, rows) => {
        const pick = (p) => {
            const list = (Array.isArray(p.lines) ? p.lines : [])
                .map((i) => rows[Number(i)])
                .filter(Boolean);
            if (!list.length) throw new Error('none of those lines are on this summary');
            return list;
        };
        const mustBeOpen = () => {
            if (!NOT_SENT_STAGES.includes(inv.status)) {
                throw new Error(`${inv.no} is ${inv.status} — kick it back and reopen it to Draft first`);
            }
        };
        return {
            locked: versionLocked || !OPEN_STAGES.includes(inv.status),
            context: (checks) => {
                const sum = summaryOf(rows);
                const dates = rows.map((t) => t.date).filter(Boolean).sort();
                return {
                    document: 'Invoice backup PDF — cost-plus summary for the client',
                    summary: inv.no,
                    stage: inv.status,
                    job: String(selectedJob),
                    client: selectedJobData?.AR_AB_Name || selectedJobData?.Client_Name || '',
                    terms: settings.terms,
                    workDates: dates.length ? `${fDate(dates[0])} – ${fDate(dates[dates.length - 1])}` : null,
                    markupPctByClass: settings.mk,
                    passThroughPct: settings.pt ? settings.pt.mk : null,
                    // The billable rubric this job bills under — judge lines
                    // against it, not against general cost-plus habits.
                    contract: contractRules ? {
                        rubric: `${RUBRIC.name} ${RUBRIC.date} (${RUBRIC.who})`,
                        serviceOrder: contractFor(selectedJob).so,
                        precedence: 'The Service Order governs; MEPC Exhibit B only where the Service Order is silent; the 9/25 rubric settles the gray areas.',
                        terms: contractTerms(selectedJob).map((x) => `${x.k}: ${x.text}`),
                    } : null,
                    totals: {
                        cost: round2(sum.cost),
                        lines: round2(sum.lineBill),
                        fuelSurcharge: round2(sum.fuel.amt),
                        invoice: round2(sum.bill),
                    },
                    groups: sum.groups.map((g) => ({
                        group: g.g, lines: g.lines.length, hours: round2(g.hrs),
                        cost: round2(g.cost), bills: round2(g.bill),
                    })),
                    preExportChecks: checks || 'still running',
                    rateSheetLaborClasses: sheetLabor.list.map((r) => r.name),
                    lineCount: rows.length,
                    lines: rows.slice(0, AI_MAX_LINES).map((t, i) => ({
                        i,
                        d: fDate(t.date),
                        group: groupOf(t.cls, t.pt),
                        cc: t.cc,
                        ccDesc: t.ccDesc,
                        ct: t.ct,
                        who: t.who || t.eq || '',
                        desc: t.ds || '',
                        po: t.po || '',
                        vendorInv: t.vinvNo || '',
                        hrs: round2(t.hrs),
                        cost: round2(t.cost),
                        billsAs: t.ba || '',
                        jdeClass: t.jde || '',
                        mkPct: t.hourly ? null : t.mk,
                        rate: t.hourly ? t.rateAmt : null,
                        passThrough: t.pt ? 'Y' : '',
                        ...(t.cr ? { contract: `${effectLabel(t.cr.effect)} — ${t.cr.label} (${t.cr.clause})` } : {}),
                        bill: round2(t.bill),
                        ...(t.nb ? { notBillable: true } : {}),
                    })),
                    ...(rows.length > AI_MAX_LINES
                        ? { truncated: `only the first ${AI_MAX_LINES} of ${rows.length} lines are listed above` }
                        : {}),
                };
            },
            actions: {
                remove_line: {
                    args: '{lines, reason, neverBill}',
                    desc: 'take lines off this summary — they go back to Unbilled and can be billed on a later '
                        + 'summary. Set neverBill true to also mark them not billable so they never come back. '
                        + 'reason is <=60 characters and lands on the audit trail. Only works on a Draft or '
                        + 'Kicked Back summary.',
                    apply: (p) => {
                        mustBeOpen();
                        removeLinesFromSummary(inv, pick(p), { nb: Boolean(p.neverBill), reason: p.reason });
                    },
                },
                labor_class: {
                    args: '{lines, cls}',
                    desc: 'fix the billed classification on labor lines — they re-price at that rate sheet '
                        + 'class and the frozen snapshot follows. cls must be one of rateSheetLaborClasses '
                        + 'exactly, or "" to clear back to the Rates & Rules map.',
                    apply: (p) => {
                        const cls = String(p.cls || '');
                        if (cls && !sheetLabor.byName[cls]) {
                            throw new Error(`"${cls}" is not on the job's rate sheet`);
                        }
                        setBillClass(pick(p), cls, inv);
                    },
                },
                pass_through: {
                    args: '{lines, value}',
                    desc: 'bill lines at cost as pass-through freight/delivery (value "Y"), force them back '
                        + 'onto their class markup (value "N"), or hand them back to the Rates & Rules '
                        + 'pass-through rules (value ""). Labor never passes through.',
                    apply: (p) => {
                        const v = ['Y', 'N'].includes(p.value) ? p.value : '';
                        setPassThru(pick(p), v, inv);
                    },
                },
            },
        };
    };

    const clearFilters = () => {
        setSearch(''); setCcFilter([]); setCcExclude(false); setClassFilter([]); setWhoFilter([]);
        setCtFilter([]); setCtExclude(false); setPcardOnly(false); setPtOnly(false); setPoFilter([]);
        setStatusFilter('All'); setReviewFilter('All'); setSigFilter('All'); setDateFrom(''); setDateTo('');
    };

    // Keeps the filter card and the grid's header row visible while the page
    // scrolls. position:sticky can't do this — the horizontal ScrollView
    // between the header and the vertical scroller is itself a scroll
    // container, and sticky only pins against the nearest one. So a
    // document-level scroll listener (capture — scroll events don't bubble,
    // and which ancestor actually scrolls depends on the app shell) translates
    // both down by however far they've scrolled past the top edge — filter
    // card at the edge, header row snug beneath it. Reading the applied
    // offset back off each element keeps the math right across remounts and
    // layout changes.
    useEffect(() => {
        // Where the element would sit with no transform applied, plus the
        // offset currently applied to it.
        const measure = (el) => {
            const m = /translateY\((-?[\d.]+)px\)/.exec(el.style.transform || '');
            const prev = m ? parseFloat(m[1]) : 0;
            return { prev, top: el.getBoundingClientRect().top - prev };
        };
        const apply = (el, prev, want, z) => {
            if (want === prev) return;
            el.style.transform = want ? `translateY(${want}px)` : '';
            el.style.boxShadow = want ? '0 2px 4px rgba(15, 23, 42, 0.08)' : '';
            if (z) el.style.zIndex = want ? z : '';
        };
        const sync = () => {
            const header = document.getElementById('billing-grid-header');
            if (!header) return;
            const bar = document.getElementById('billing-filter-card');
            // The pinning edge: the nearest ancestor that genuinely scrolls
            // vertically, else the viewport top (window scrolling).
            let edge = 0;
            let p = header.parentElement;
            while (p && p !== document.documentElement) {
                const o = window.getComputedStyle(p).overflowY;
                if ((o === 'auto' || o === 'scroll') && p.scrollHeight > p.clientHeight + 1) {
                    edge = p.getBoundingClientRect().top;
                    break;
                }
                p = p.parentElement;
            }
            const barH = bar ? bar.offsetHeight : 0;
            const h = measure(header);
            // Clamp so the header parks at the table's last row instead of
            // floating on past the grid.
            const maxY = Math.max(0, header.parentElement.scrollHeight - header.offsetHeight);
            const headerWant = Math.min(maxY, Math.max(0, Math.round(edge + barH - h.top)));
            apply(header, h.prev, headerWant);
            if (bar) {
                const b = measure(bar);
                // The card rides just above the header, so when the header
                // parks at the last row the card parks with it.
                const barWant = Math.max(0, Math.min(
                    headerWant + Math.round(h.top - b.top) - barH,
                    Math.max(0, Math.round(edge - b.top))));
                apply(bar, b.prev, barWant, '10');
            }
        };
        document.addEventListener('scroll', sync, true);
        window.addEventListener('resize', sync);
        return () => {
            document.removeEventListener('scroll', sync, true);
            window.removeEventListener('resize', sync);
        };
    }, []);

    // One entry of a Third Party grid on COLS: a week ({ w }), PO ({ g }) or
    // PO line ({ ln }) header, or a line ({ t }). `x` unset, it is the
    // Third Party tab's own grid — selection, the tab's open state. `x` set,
    // an opened invoice's third-party lines: no selection, the invoice's own
    // open state (x.isOpen / x.toggle), and its line fixes in place of the
    // checkbox (x.inv, x.canFix, x.canRemove).
    const renderTpEntry = (e, i, x = null) => {
        const poIsOpen = (g) => (x ? x.isOpen(`g|${g.po}`, true) : !!poOpen[g.po]);
        const lnIsOpen = (ln) => (x ? x.isOpen(`l|${ln.key}`, true) : !!lineOpen[ln.key]);
        const setLnOpen = (ln, on) => (x ? x.setOpen(`l|${ln.key}`, on) : setLineOpen((p) => ({ ...p, [ln.key]: on })));
        const some = (g) => !x && groupSome(g);
        const poName = (po) => (po ? (/^po/i.test(po) ? po : 'PO ' + po) : 'No PO');
        // A COST figure that opens its cost basis (billingCostBasis.js):
        // the JDE entries it sums, and the PO line's ordered / received /
        // open when it is one PO line.
        const costCell = (amount, rows, title, pol, weight) => (
            <Pressable
                title="How is this cost calculated?"
                onPress={(ev) => {
                    if (ev && ev.stopPropagation) ev.stopPropagation();
                    setCostBasis({ title, rows, po: rows[0]?.po || '', pol: pol || '' });
                }}
                style={[gtd, { width: W.cost, alignItems: 'flex-end', cursor: 'pointer' }]}
            >
                <Text style={{
                    fontSize: 12, color: '#1e293b', fontWeight: weight,
                    textDecorationLine: 'underline', textDecorationStyle: 'dotted', textDecorationColor: '#94a3b8',
                }}>
                    {money(amount)}
                </Text>
            </Pressable>
        );
        // INVOICED: what summaries already bill for a PO / PO line
        // (tpInvoiced entry), with the summary numbers under it when `subs`.
        // An opened invoice has no such column.
        // OPEN AMOUNT: the PO / PO line's open balance (poOpenAmt entry) —
        // '—' when the open commitments carry nothing for it. Entries are
        // pieces of a line, so they stay '—'. An opened invoice has no
        // such column.
        const poOpenOf = (po) => (openKey(po) ? poOpenAmt[openKey(po)] : null);
        const openCell = (v, weight, title) => {
            if (x) return null;
            if (!v) return <Text style={[gtd, { width: W.opn, textAlign: 'right', color: '#94a3b8' }]}>—</Text>;
            return (
                <Text
                    title={`${title ? `${title} — ` : ''}${money(v.open)} ordered, not received yet (not in COST)`}
                    style={[gtd, { width: W.opn, textAlign: 'right', fontWeight: weight, color: v.open ? '#1e293b' : '#94a3b8' }]}
                >
                    {money(v.open)}
                </Text>
            );
        };
        const invdCell = (v, weight, subs) => {
            if (x) return null;
            if (!v) return <Text style={[gtd, { width: W.invd, textAlign: 'right', color: '#94a3b8' }]}>—</Text>;
            return (
                <View
                    title={`${money(v.bill)} on ${v.invs.join(', ')} — ${v.n} ${v.n === 1 ? 'entry' : 'entries'}, ${money(v.cost)} cost`}
                    style={[gtd, { width: W.invd, alignItems: 'flex-end' }]}
                >
                    <Text style={{ fontSize: 12, color: '#1e40af', fontWeight: weight }}>{money(v.bill)}</Text>
                    {subs ? (
                        <Text style={{ fontSize: 9, color: '#64748b', marginTop: 1 }} numberOfLines={1}>
                            on {v.invs[0]}{v.invs.length > 1 ? ` +${v.invs.length - 1}` : ''}
                        </Text>
                    ) : null}
                </View>
            );
        };
        if (e.w) {
            // Week header (week grouping): its checkbox and totals run
            // on the week's `open` lines through the group helpers,
            // which only read `.open`.
            const w = e.w;
            const closed = hdrClosed(w);
            const people = w.groups.filter((g) => !g.nbGroup).length;
            const lineCount = `${w.rows.length} line${w.rows.length === 1 ? '' : 's'}`;
            return (
                <View
                    key={'w:' + w.we}
                    style={{
                        flexDirection: 'row', alignItems: 'center',
                        borderBottomWidth: 1, borderColor: '#cbd5e1',
                        backgroundColor: groupSome(w) ? '#dbeafe' : '#e2e8f0',
                    }}
                >
                    <Pressable
                        onPress={(ev) => {
                            if (ev && ev.stopPropagation) ev.stopPropagation();
                            if (w.open.length) toggleGroupSel(w);
                        }}
                        style={[gtd, { width: W.sel, cursor: w.open.length ? 'pointer' : 'default' }]}
                    >
                        <CheckBox
                            checked={groupChecked(w)}
                            indeterminate={!groupChecked(w) && groupSome(w)}
                            disabled={!w.open.length}
                            onPress={(ev) => {
                                if (ev && ev.stopPropagation) ev.stopPropagation();
                                toggleGroupSel(w);
                            }}
                        />
                    </Pressable>
                    <Pressable
                        onPress={() => setWeekClosed((p) => ({ ...p, [w.we]: !closed }))}
                        style={{
                            width: GROUP_LABEL_W, flexDirection: 'row', alignItems: 'center',
                            paddingTop: 5, paddingBottom: 5, paddingRight: 8, cursor: 'pointer',
                        }}
                    >
                        <Text style={{ width: 14, fontSize: 11, color: '#475569' }}>{closed ? '▸' : '▾'}</Text>
                        <Text style={{ fontSize: 12, fontWeight: '700', color: '#0f172a' }} numberOfLines={1}>{w.label}</Text>
                        <SigStanding sig={w.sig} loading={jobSigs.loading} />
                        <Text style={{ fontSize: 11, color: '#64748b', marginLeft: 8, whiteSpace: 'nowrap' }}>
                            {w.vendorHdr
                                ? `${w.pos ? `${w.pos} PO${w.pos === 1 ? '' : 's'}` : 'no PO'} · ${lineCount}`
                                : `${people ? `${people} ${people === 1 ? 'name' : 'names'}` : 'nothing billable'} · ${lineCount}`}
                            {w.onSummary ? ` · ${w.onSummary} on a summary` : ''}
                        </Text>
                    </Pressable>
                    <Text style={[gtd, { width: W.hrs, textAlign: 'right', fontWeight: '700', color: w.hrs ? '#1e293b' : '#94a3b8' }]}>
                        {w.hrs ? (Math.round(w.hrs * 10) / 10).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '—'}
                    </Text>
                    {/* A vendor header sums its POs' open; a week spans POs, so it stays blank. */}
                    {w.vendorHdr
                        ? openCell(sumOpen(w.groups.map((g) => poOpenOf(g.po))), '700', w.label)
                        : <View style={{ width: W.opn }} />}
                    {costCell(w.cost, w.rows, w.label, '', '700')}
                    <View style={{ width: W.mk }} />
                    <Text style={[gtd, { width: W.bill, textAlign: 'right', fontWeight: '700' }]}>{money(w.bill)}</Text>
                    {/* A vendor header sums its POs; a week spans POs, so it stays blank. */}
                    {w.vendorHdr
                        ? invdCell(sumInvd(w.groups.map((g) => tpInvoiced[`p:${g.po}`])), '700')
                        : <View style={{ width: W.invd }} />}
                    <View style={{ width: GROUP_TAIL_W }} />
                </View>
            );
        }
        if (e.ln) {
            // PO line inside an opened PO, laid out on the same columns
            // as its entries: a column shows what the entries agree on
            // (a count where they split), hours / cost / bill are summed,
            // and the line # cell opens it to the entries.
            const ln = e.ln;
            const lnOpen = lnIsOpen(ln);
            const rs = ln.rows;
            const toggleLine = () => setLnOpen(ln, !lnOpen);
            const dash = <Text style={{ fontSize: 12, color: '#94a3b8' }}>—</Text>;
            const split = (f, noun) => (
                <Text style={{ fontSize: 11, color: '#64748b' }} numberOfLines={1}>
                    {new Set(rs.map((t) => f(t) || '')).size} {noun}
                </Text>
            );
            const dates = rs.map((t) => t.date).filter(Boolean).sort();
            const d0 = dates[0];
            const d1 = dates[dates.length - 1];
            const rev = shared(rs, reviewOf);
            const cc = shared(rs, (t) => t.cc);
            const ct = shared(rs, (t) => t.ct);
            const who = shared(rs, (t) => t.who || '');
            const eq = shared(rs, (t) => t.eqDs || t.eq || '');
            const vinv = shared(rs, (t) => t.vinv || '');
            const vinvNo = shared(rs, (t) => t.vinvNo || '');
            const vinvDate = shared(rs, (t) => t.vinvDate || '');
            const pdfs = [...new Set(rs.map((t) => t.pdf).filter(Boolean))];
            const mk = shared(rs, mkText);
            const hist = rs.reduce((s, t) => s + t.hist.length, 0);
            // Billable toggle over the line's open entries: on while any
            // of them bills; flipping it moves them all together.
            const lnBillable = ln.open.filter((t) => !t.nb);
            return (
                <View
                    key={'l:' + ln.key}
                    style={{
                        flexDirection: 'row', alignItems: 'center',
                        borderBottomWidth: 1, borderColor: '#eef2f6',
                        backgroundColor: some(ln) ? '#eff6ff' : '#fcfdfe',
                    }}
                >
                    <View style={{
                        width: ROW_INDENT_W - 2, alignSelf: 'stretch',
                        borderRightWidth: 2, borderRightColor: '#e2e8f0', borderRightStyle: 'solid',
                    }} />
                    {x ? <View style={{ width: W.sel - ROW_INDENT_W }} /> : (
                    <Pressable
                        onPress={(ev) => {
                            if (ev && ev.stopPropagation) ev.stopPropagation();
                            if (ln.open.length) toggleGroupSel(ln);
                        }}
                        style={[gtd, { width: W.sel - ROW_INDENT_W, paddingLeft: 2, paddingRight: 1, cursor: ln.open.length ? 'pointer' : 'default' }]}
                    >
                        <CheckBox
                            checked={groupChecked(ln)}
                            indeterminate={!groupChecked(ln) && groupSome(ln)}
                            disabled={!ln.open.length}
                            onPress={(ev) => {
                                if (ev && ev.stopPropagation) ev.stopPropagation();
                                toggleGroupSel(ln);
                            }}
                        />
                    </Pressable>
                    )}
                    <Pressable
                        onPress={toggleLine}
                        title={lnOpen ? 'Hide the entries' : 'Show the entries on this PO line'}
                        style={{ width: W.date, paddingTop: 2, paddingBottom: 2, paddingLeft: 6, paddingRight: 4, cursor: 'pointer' }}
                    >
                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                            <Text style={{ width: 12, fontSize: 11, color: '#64748b' }}>{lnOpen ? '▾' : '▸'}</Text>
                            <Text style={{ fontSize: 12, fontWeight: '700', color: '#1e293b', whiteSpace: 'nowrap' }}>
                                {ln.pol ? `Line ${ln.pol}` : 'No line #'}
                            </Text>
                        </View>
                        <Text style={{ fontSize: 10, color: '#64748b', marginLeft: 12 }} numberOfLines={1}>
                            {!d0 ? '—' : d0 === d1 ? fDate(d0) : `${shortDate(d0)}–${shortDate(d1)}`}
                        </Text>
                    </Pressable>
                    <Pressable
                        onPress={toggleLine}
                        style={{ width: W.ds, paddingTop: 2, paddingBottom: 2, paddingLeft: 8, paddingRight: 8, cursor: 'pointer' }}
                    >
                        <Text style={{ fontSize: 12, color: '#1e293b' }} numberOfLines={1}>{ln.ds || '—'}</Text>
                        <Text style={{ fontSize: 10, color: '#94a3b8' }} numberOfLines={1}>
                            {rs.length} {rs.length === 1 ? 'entry' : 'entries'}
                            {!x && tpInvoiced[`l:${ln.key}`] ? ` · ${tpInvoiced[`l:${ln.key}`].n} on a summary` : ''}
                        </Text>
                    </Pressable>
                    <View style={{ width: W.cc, paddingTop: 2, paddingBottom: 2, paddingLeft: 8, paddingRight: 8 }}>
                        {cc === undefined ? split((t) => t.cc, 'cost codes') : (
                            <>
                                <Text style={{ fontSize: 12, color: '#1e293b' }}>{cc}</Text>
                                {rs[0].ccDesc ? <Text style={{ fontSize: 10, color: '#94a3b8' }} numberOfLines={1}>{rs[0].ccDesc}</Text> : null}
                            </>
                        )}
                    </View>
                    <View
                        title={ln.open.length && lnBillable.length && lnBillable.length < ln.open.length
                            ? `${lnBillable.length} of ${ln.open.length} open entries billable` : undefined}
                        style={[gtd, { width: W.nbt, alignItems: 'center' }]}
                    >
                        <NbToggle
                            billable={lnBillable.length > 0}
                            disabled={!ln.open.length || versionLocked}
                            reason={ln.open.length ? shared(ln.open, (t) => t.nbReason) : undefined}
                            onPress={(ev) => {
                                if (ev && ev.stopPropagation) ev.stopPropagation();
                                if (lnBillable.length) setNotBillable(lnBillable, true, '', true);
                                else setNotBillable(ln.open, false, '', true);
                            }}
                        />
                        {lnBillable.length && lnBillable.length < ln.open.length ? (
                            <Text style={{ fontSize: 9, color: '#92400e', marginTop: 1 }}>{lnBillable.length} of {ln.open.length}</Text>
                        ) : null}
                    </View>
                    <View style={{ width: W.ct, paddingTop: 2, paddingBottom: 2, paddingLeft: 8, paddingRight: 8 }}>
                        {ct === undefined ? split((t) => t.ct, 'cost types') : (
                            <>
                                <Text style={{ fontSize: 12, color: '#1e293b' }}>{ct}</Text>
                                {rs[0].ctDesc ? <Text style={{ fontSize: 10, color: '#94a3b8' }} numberOfLines={1}>{rs[0].ctDesc}</Text> : null}
                            </>
                        )}
                    </View>
                    {ln.hj ? (
                        <View title={rentalHoursTip(ln.hj, selectedJob)} style={[gtd, { width: W.hrs, alignItems: 'flex-end' }]}>
                            <Text style={{ fontSize: 12, color: '#1e293b' }}>
                                {(Math.round(ln.hrs * 10) / 10).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                            </Text>
                            <Text style={{ fontSize: 9, color: '#94a3b8' }}>HeavyJob</Text>
                        </View>
                    ) : (
                    <Text style={[gtd, { width: W.hrs, textAlign: 'right', color: ln.hrs ? '#1e293b' : '#94a3b8' }]}>
                        {ln.hrs ? (Math.round(ln.hrs * 10) / 10).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '—'}
                    </Text>
                    )}
                    {openCell(ln.pol && openKey(rs[0].po) ? poOpenAmt[openKey(rs[0].po, ln.pol)] : null, '600', `${poName(rs[0].po)} · Line ${ln.pol}`)}
                    {costCell(ln.cost, rs, [poName(rs[0].po), ln.pol ? `Line ${ln.pol}` : 'No line #', who].filter(Boolean).join(' · '), ln.pol, '600')}
                    <Text style={[gtd, {
                        width: W.mk, textAlign: 'right',
                        color: isNoRateText(mk) ? '#9a3412' : mk && mk !== '—' ? '#1e293b' : '#94a3b8',
                        fontWeight: isNoRateText(mk) ? '700' : undefined,
                    }]}>{mk === undefined ? 'mixed' : mk}</Text>
                    <Text style={[gtd, { width: W.bill, textAlign: 'right', fontWeight: '700' }]}>{money(ln.bill)}</Text>
                    {invdCell(tpInvoiced[`l:${ln.key}`], '700', true)}
                    <View style={[gtd, { width: W.eq }]}>
                        {eq === undefined ? split((t) => t.eqDs || t.eq, 'units') : <Text title={rs[0].eq || undefined} style={{ fontSize: 12, color: eq ? '#1e293b' : '#94a3b8' }} numberOfLines={1}>{eq || '—'}</Text>}
                    </View>
                    <View style={[gtd, { width: W.vinv }]}>
                        {vinv === undefined ? split((t) => t.vinv, 'statuses') : vinv ? <Badge kind={vinv} /> : dash}
                    </View>
                    <View style={[gtd, { width: W.vinvNo }]}>
                        {vinvNo === undefined ? split((t) => t.vinvNo, 'invoices')
                            : !vinvNo ? dash
                            : pdfs.length ? invNoLink(pdfs[0], vinvNo, [who, `#${vinvNo}`, rs[0].po && `PO ${rs[0].po}`].filter(Boolean).join(' · '))
                            : <Text title={vinvNo} style={{ fontSize: 12, color: '#1e293b' }} numberOfLines={1}>{vinvNo}</Text>}
                        {vinvNo && pdfs.length > 1 ? (
                            <Pressable onPress={() => setLnOpen(ln, true)} style={{ cursor: 'pointer' }}>
                                <Text style={{ fontSize: 9, color: '#64748b', marginTop: 1 }}>+{pdfs.length - 1} more PDF{pdfs.length > 2 ? 's' : ''}</Text>
                            </Pressable>
                        ) : null}
                    </View>
                    <View style={[gtd, { width: W.vinvDate }]}>
                        {vinvDate === undefined ? split((t) => t.vinvDate, 'dates')
                            : vinvDate ? <Text style={{ fontSize: 12, color: '#64748b' }}>{vinvDate}</Text> : dash}
                    </View>
                    <Pressable
                        title={`History is kept per entry${hist ? ` — ${hist} event${hist === 1 ? '' : 's'} across them` : ''}; opens the line to them`}
                        onPress={() => setLnOpen(ln, true)}
                        style={[gtd, { width: W.hist, flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer' }]}
                    >
                        <Text style={{ fontSize: 14, color: hist ? '#1e40af' : '#94a3b8' }}>↺</Text>
                        {hist ? <Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '600', marginLeft: 4 }}>{hist}</Text> : null}
                    </Pressable>
                    <View style={[gtd, { width: W.rev }]}>
                        {rev === undefined ? split(reviewOf, 'reviews')
                            : rev !== 'Not Reviewed' ? <Badge kind={rev} /> : dash}
                    </View>
                    <View style={[gtd, { width: W.sig }]}>{dash}</View>
                </View>
            );
        }
        if (e.cx) {
            // A PO line the grid leaves out, listed under its opened PO for
            // context (poContext): on the PO line's columns, muted, with no
            // checkbox or toggle — the note under the description says why
            // it isn't an open row.
            const cx = e.cx;
            const rs = cx.rows;
            const oc = cx.oc;
            const openRs = rs.filter((t) => !t.inv);
            const invd = tpInvoiced[`l:${cx.key}`];
            const onInv = invd ? `on ${invd.invs.join(', ')}` : 'on a summary';
            const note = !rs.length ? 'ordered · nothing received yet'
                : !openRs.length ? onInv
                : openRs.length < rs.length ? `part ${onInv} · the rest hidden by the filters`
                : 'hidden by the filters';
            const billTag = !rs.length ? '' : !openRs.length ? 'billed' : openRs.some((t) => !t.nb) ? 'billable' : 'not billable';
            const dash = <Text style={{ fontSize: 12, color: '#cbd5e1' }}>—</Text>;
            const val = (f, ocVal) => (rs.length ? shared(rs, f) : ocVal || '');
            const cell = (v, noun, sub) => (v === undefined
                ? <Text style={{ fontSize: 11, color: '#94a3b8' }} numberOfLines={1}>{noun}</Text>
                : !v ? dash : (
                    <>
                        <Text style={{ fontSize: 12, color: '#64748b' }} numberOfLines={1}>{v}</Text>
                        {sub ? <Text style={{ fontSize: 10, color: '#94a3b8' }} numberOfLines={1}>{sub}</Text> : null}
                    </>
                ));
            const dates = rs.map((t) => t.date).filter(Boolean).sort();
            const d0 = dates[0];
            const d1 = dates[dates.length - 1];
            const cc = val((t) => t.cc, oc?.Phase_Code);
            const ct = val((t) => t.ct, oc?.Cost_Type_Code);
            const who = val((t) => t.who || '', oc?.Supplier_AB_Name);
            const eq = val((t) => t.eqDs || t.eq || '');
            const vinv = val((t) => t.vinv || '');
            const vinvNo = val((t) => t.vinvNo || '');
            const vinvDate = val((t) => t.vinvDate || '');
            const pdf = rs.find((t) => t.pdf)?.pdf;
            const mk = openRs.length ? shared(openRs, mkText) : '';
            return (
                <View
                    key={'cx:' + cx.key}
                    title={`${poName(cx.po)}${cx.pol ? ` · Line ${cx.pol}` : ''} — ${note}. Shown for context; not selectable here.`}
                    style={{
                        flexDirection: 'row', alignItems: 'center',
                        borderBottomWidth: 1, borderColor: '#eef2f6', backgroundColor: '#f8fafc',
                    }}
                >
                    <View style={{
                        width: ROW_INDENT_W - 2, alignSelf: 'stretch',
                        borderRightWidth: 2, borderRightColor: '#e2e8f0', borderRightStyle: 'solid',
                    }} />
                    <View style={{ width: W.sel - ROW_INDENT_W }} />
                    <View style={{ width: W.date, paddingTop: 2, paddingBottom: 2, paddingLeft: 18, paddingRight: 4 }}>
                        <Text style={{ fontSize: 12, fontWeight: '600', color: '#64748b', whiteSpace: 'nowrap' }}>
                            {cx.pol ? `Line ${cx.pol}` : 'No line #'}
                        </Text>
                        <Text style={{ fontSize: 10, color: '#94a3b8' }} numberOfLines={1}>
                            {!d0 ? '—' : d0 === d1 ? fDate(d0) : `${shortDate(d0)}–${shortDate(d1)}`}
                        </Text>
                    </View>
                    <View style={{ width: W.ds, paddingTop: 2, paddingBottom: 2, paddingLeft: 8, paddingRight: 8 }}>
                        <Text style={{ fontSize: 12, color: '#64748b' }} numberOfLines={1}>{cx.ds || '—'}</Text>
                        <Text style={{ fontSize: 10, color: '#94a3b8' }} numberOfLines={1}>{note}</Text>
                    </View>
                    <View style={{ width: W.cc, paddingTop: 2, paddingBottom: 2, paddingLeft: 8, paddingRight: 8 }}>
                        {cell(cc, 'cost codes', rs.length ? rs[0].ccDesc : oc?.Phase_Code_Description)}
                    </View>
                    <View
                        title={billTag === 'not billable' ? shared(openRs, (t) => t.nbReason) || undefined : undefined}
                        style={[gtd, { width: W.nbt, alignItems: 'center' }]}
                    >
                        {billTag ? <Text style={{ fontSize: 10, color: '#64748b', whiteSpace: 'nowrap' }}>{billTag}</Text> : dash}
                    </View>
                    <View style={{ width: W.ct, paddingTop: 2, paddingBottom: 2, paddingLeft: 8, paddingRight: 8 }}>
                        {cell(ct, 'cost types', rs.length ? rs[0].ctDesc : oc?.Cost_Type_Description)}
                    </View>
                    <View style={[gtd, { width: W.hrs, alignItems: 'flex-end' }]}>{dash}</View>
                    {openCell(cx.pol ? poOpenAmt[openKey(cx.po, cx.pol)] : null, '400', `${poName(cx.po)} · Line ${cx.pol}`)}
                    {rs.length
                        ? costCell(cx.cost, rs, [poName(cx.po), cx.pol ? `Line ${cx.pol}` : 'No line #', who].filter(Boolean).join(' · '), cx.pol, '400')
                        : <View style={[gtd, { width: W.cost, alignItems: 'flex-end' }]}>{dash}</View>}
                    <Text style={[gtd, { width: W.mk, textAlign: 'right', color: '#94a3b8' }]}>{mk === undefined ? 'mixed' : mk || '—'}</Text>
                    {/* Nothing here is up for invoicing from this row, so BILL
                        AMOUNT stays grey: what its hidden open entries would bill. */}
                    <Text style={[gtd, { width: W.bill, textAlign: 'right', color: '#94a3b8' }]}>{openRs.length ? money(cx.bill) : '—'}</Text>
                    {invdCell(invd, '600', true)}
                    <View style={[gtd, { width: W.eq }]}>{cell(eq, 'units')}</View>
                    <View style={[gtd, { width: W.vinv }]}>
                        {vinv === undefined ? cell(vinv, 'statuses') : vinv ? <Badge kind={vinv} /> : dash}
                    </View>
                    <View style={[gtd, { width: W.vinvNo }]}>
                        {vinvNo && pdf
                            ? invNoLink(pdf, vinvNo, [who, `#${vinvNo}`, `PO ${cx.po}`].filter(Boolean).join(' · '))
                            : cell(vinvNo, 'invoices')}
                    </View>
                    <View style={[gtd, { width: W.vinvDate }]}>{cell(vinvDate, 'dates')}</View>
                    <View style={{ width: W.hist + W.rev + W.sig }} />
                </View>
            );
        }
        if (e.g) {
            const g = e.g;
            const expanded = poIsOpen(g);
            // JDE POs are bare numbers, but a few carry their own "PO"
            // prefix — do not stutter on those.
            const poLabel = g.label || (g.po ? (/^po/i.test(g.po) ? g.po : 'PO ' + g.po) : 'No PO');
            return (
                <View
                    key={'g:' + (g.po || '(none)')}
                    style={{
                        flexDirection: 'row', alignItems: 'center',
                        borderBottomWidth: 1, borderColor: '#e2e8f0',
                        backgroundColor: some(g) ? '#eff6ff' : '#f8fafc',
                    }}
                >
                    {x ? <View style={{ width: W.sel }} /> : (
                    <Pressable
                        onPress={(ev) => {
                            if (ev && ev.stopPropagation) ev.stopPropagation();
                            if (g.open.length) toggleGroupSel(g);
                        }}
                        style={[gtd, { width: W.sel, cursor: g.open.length ? 'pointer' : 'default' }]}
                    >
                        <CheckBox
                            checked={groupChecked(g)}
                            indeterminate={!groupChecked(g) && groupSome(g)}
                            disabled={!g.open.length}
                            onPress={(ev) => {
                                if (ev && ev.stopPropagation) ev.stopPropagation();
                                toggleGroupSel(g);
                            }}
                        />
                    </Pressable>
                    )}
                    <Pressable
                        onPress={() => (x ? x.setOpen(`g|${g.po}`, !expanded) : toggleGroupOpen(g))}
                        style={{
                            width: GROUP_LABEL_W, flexDirection: 'row', alignItems: 'center',
                            paddingTop: 4, paddingBottom: 4, paddingRight: 8, cursor: 'pointer',
                            paddingLeft: g.week || g.inVendor ? 18 : 0,
                        }}
                    >
                        <Text style={{ width: 14, fontSize: 11, color: '#475569' }}>{expanded ? '▾' : '▸'}</Text>
                        <Text style={{ fontSize: 12, fontWeight: g.nbGroup ? '600' : '700', color: g.nbGroup ? '#64748b' : '#0f172a' }}>
                            {poLabel}
                        </Text>
                        {g.week && !g.nbGroup ? <SigStanding sig={g.sig} loading={jobSigs.loading} /> : null}
                        {/* The grid has no vendor column, so the PO header names it
                            open or closed — except under a vendor header, which
                            already says it. */}
                        {g.vendor && !g.inVendor ? (
                            <Text style={{ fontSize: 11, color: '#64748b', marginLeft: 8 }} numberOfLines={1}>{g.vendor}</Text>
                        ) : null}
                        {g.topDs && g.vendor && !g.inVendor ? (
                            <Text style={{ fontSize: 11, color: '#94a3b8', marginLeft: 6 }}>·</Text>
                        ) : null}
                        {g.topDs ? (
                            <Text
                                title="Description of the PO's largest line by cost"
                                style={{ fontSize: 11, color: '#334155', marginLeft: 6, maxWidth: 460 }}
                                numberOfLines={1}
                            >
                                {g.topDs}
                            </Text>
                        ) : null}
                        {g.topDs && g.moreDs ? (
                            <Text style={{ fontSize: 11, color: '#94a3b8', marginLeft: 4, whiteSpace: 'nowrap' }}>+{g.moreDs} more</Text>
                        ) : null}
                        <Text style={{ fontSize: 11, color: '#94a3b8', marginLeft: 8 }}>
                            {/* Split by PO line, the count reads lines · entries so it
                                matches the Line rows under it. */}
                            {g.lines
                                ? `${g.lines.length} line${g.lines.length === 1 ? '' : 's'} · ${g.rows.length} ${g.rows.length === 1 ? 'entry' : 'entries'}`
                                : `${g.rows.length} line${g.rows.length === 1 ? '' : 's'}`}
                            {g.nbGroup
                                ? (g.info ? ` · ${g.info} read-only: labor cost that never bills (burden, taxes, insurance …)` : '')
                                : !x && g.po && tpInvoiced[`p:${g.po}`] ? ` · ${tpInvoiced[`p:${g.po}`].n} on a summary` : ''}
                            {/* The PO's lines with no open row here — listed when it opens (poContext). */}
                            {!x && poContext[g.po] ? ` · +${poContext[g.po].length} other PO line${poContext[g.po].length === 1 ? '' : 's'}` : ''}
                        </Text>
                    </Pressable>
                    <Text style={[gtd, { width: W.hrs, textAlign: 'right', fontWeight: '600', color: g.hrs ? '#1e293b' : '#94a3b8' }]}>
                        {g.hrs ? (Math.round(g.hrs * 10) / 10).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '—'}
                    </Text>
                    {/* The whole PO's open — its lines with nothing received
                        yet aren't in the grid, but their open counts here. */}
                    {openCell(poOpenOf(g.po), '600', poLabel)}
                    {costCell(g.cost, g.rows, [poLabel, g.vendor].filter(Boolean).join(' · '), '', '600')}
                    <View style={{ width: W.mk }} />
                    <Text style={[gtd, { width: W.bill, textAlign: 'right', fontWeight: '700' }]}>{money(g.bill)}</Text>
                    {invdCell(g.po ? tpInvoiced[`p:${g.po}`] : null, '700')}
                    <View style={{ width: GROUP_TAIL_W }} />
                </View>
            );
        }
        const { t, sub } = e;
        return (
        <View
            key={t.key}
            onMouseEnter={x ? undefined : () => paintRow(t)}
            style={{
                flexDirection: 'row', alignItems: 'center',
                borderBottomWidth: 1, borderColor: '#f1f5f9',
                backgroundColor: rowSel(t) && !t.inv ? '#eff6ff' : sub ? '#f8fafc' : i % 2 ? '#fafafa' : '#fff',
            }}
        >
            {x || groupPo ? (
                <View style={{
                    width: ROW_INDENT_W - 2, alignSelf: 'stretch',
                    borderRightWidth: 2, borderRightColor: '#e2e8f0', borderRightStyle: 'solid',
                }} />
            ) : null}
            {x ? (
                // On an invoice the checkbox slot takes the line off it
                // while it has not gone out — back to Unbilled.
                x.canRemove && t.key ? (
                    <Pressable
                        title={`Remove from ${x.inv.no} — back to Unbilled`}
                        onPress={(ev) => {
                            if (ev && ev.stopPropagation) ev.stopPropagation();
                            removeFromSummary(x.inv, t.key);
                        }}
                        style={[gtd, { width: W.sel - ROW_INDENT_W, paddingLeft: 2, paddingRight: 1, alignItems: 'center', cursor: 'pointer' }]}
                    >
                        <Text style={{ fontSize: 12, color: '#b91c1c', fontWeight: '700' }}>✕</Text>
                    </Pressable>
                ) : <View style={{ width: W.sel - ROW_INDENT_W }} />
            ) : (
            <Pressable
                onMouseDown={(e) => startPaint(t, e)}
                onPress={(e) => {
                    // The whole cell is the click target, not just
                    // the 15px box.
                    if (e && e.stopPropagation) e.stopPropagation();
                    if (!t.inv && !t.nbInv) pickRow(t, e);
                }}
                style={[gtd, {
                    width: groupPo ? W.sel - ROW_INDENT_W : W.sel,
                    paddingLeft: groupPo ? 2 : 8,
                    paddingRight: groupPo ? 1 : 8,
                    cursor: t.inv || t.nbInv ? 'default' : 'pointer',
                }]}
            >
                <CheckBox
                    checked={rowSel(t) && !t.inv && !t.nbInv}
                    indeterminate={!t.inv && !t.nbInv && !rowSel(t) && rowSome(t)}
                    disabled={!!t.inv || !!t.nbInv}
                    onPress={(e) => {
                        if (e && e.stopPropagation) e.stopPropagation();
                        pickRow(t, e);
                    }}
                />
            </Pressable>
            )}
            {t.parts ? (
                // A rolled week: its date span, and the toggle that lists its days.
                <Pressable
                    title={foldOpen[t.key] ? 'Hide the days' : 'Show the days behind this row'}
                    onPress={(ev) => {
                        if (ev && ev.stopPropagation) ev.stopPropagation();
                        setFoldOpen((p) => ({ ...p, [t.key]: !p[t.key] }));
                    }}
                    style={{ width: W.date, paddingTop: 2, paddingBottom: 2, paddingLeft: 8, paddingRight: 4, cursor: 'pointer' }}
                >
                    <Text style={{ fontSize: 12, color: '#1e293b' }} numberOfLines={1}>
                        {t.dateFrom === t.dateTo ? fDate(t.dateFrom) : `${shortDate(t.dateFrom)}–${shortDate(t.dateTo)}`}
                    </Text>
                    <Text style={{ fontSize: 10, color: '#1e40af' }} numberOfLines={1}>
                        {foldOpen[t.key] ? '▾' : '▸'} {t.days} day{t.days === 1 ? '' : 's'}{t.parts.length > t.days ? ` · ${t.parts.length} lines` : ''}
                    </Text>
                </Pressable>
            ) : (
                <Text style={[gtd, { width: W.date, paddingLeft: sub ? 20 : 8, color: sub ? '#64748b' : '#1e293b' }]}>{fDate(t.date)}</Text>
            )}
            <View style={[gtd, { width: W.ds, flexDirection: 'row', alignItems: 'center' }]}>
                {x ? (
                    // On an invoice still open to line fixes the tag sets
                    // pass-through on any non-labor line (faded while it
                    // isn't one) and the frozen line re-prices (setPassThru).
                    t.pt || (x.canFix && t.src && t.cls !== 'Labor') ? (
                        <Pressable
                            title={x.canFix && t.src
                                ? (t.pt ? 'Pass-through — click to bill on the class markup' : 'Click to bill as pass-through')
                                : 'Pass-through — frozen on the summary'}
                            onPress={(e) => {
                                if (e && e.stopPropagation) e.stopPropagation();
                                if (x.canFix && t.src) flipPassThru(t.src, x.inv);
                            }}
                            style={{ cursor: x.canFix && t.src ? 'pointer' : 'default', opacity: t.pt ? 1 : 0.35 }}
                        >
                            <PtTag strong={!!t.pt} />
                        </Pressable>
                    ) : null
                ) : t.pt ? (
                    <Pressable
                        title={[
                            t.inv ? 'Pass-through — frozen on the summary' : t.ptOv === 'Y' ? 'Pass-through — set on this line' : `Pass-through — rule: ${t.ptWhy}`,
                            t.inv || versionLocked ? '' : 'click to bill on the class markup instead',
                        ].filter(Boolean).join(' · ')}
                        onPress={(e) => {
                            if (e && e.stopPropagation) e.stopPropagation();
                            if (!t.inv && !versionLocked) flipPassThru(t);
                        }}
                        style={{ cursor: t.inv || versionLocked ? 'default' : 'pointer' }}
                    >
                        <PtTag strong={t.ptOv === 'Y'} />
                    </Pressable>
                ) : null}
                {t.cr && (t.cr.effect === 'warn' || t.cr.effect === 'cost') ? <CtTag rule={t.cr} /> : null}
                {t.pcard ? (
                    <View title="P-card charge" style={{ borderWidth: 1, borderColor: '#cbd5e1', borderStyle: 'solid', borderRadius: 3, paddingTop: 0, paddingBottom: 0, paddingLeft: 3, paddingRight: 3, marginRight: 4 }}>
                        <Text style={{ fontSize: 8, fontWeight: '700', color: '#64748b', letterSpacing: 0.4 }}>P-CARD</Text>
                    </View>
                ) : null}
                <Text style={{ fontSize: 12, color: '#1e293b', flexShrink: 1 }} numberOfLines={1}>{t.ds || '—'}</Text>
            </View>
            <View style={{ width: W.cc, paddingTop: 2, paddingBottom: 2, paddingLeft: 8, paddingRight: 8 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Text style={{ fontSize: 12, color: '#1e293b' }}>{t.cc}</Text>
                </View>
                {t.ccDesc ? <Text style={{ fontSize: 10, color: '#94a3b8' }} numberOfLines={1}>{t.ccDesc}</Text> : null}
            </View>
            <View style={[gtd, { width: W.nbt, alignItems: 'center' }]}>
                <NbToggle
                    billable={!t.nb}
                    disabled={!!t.inv || !!t.nbInv || versionLocked}
                    reason={t.nbReason}
                    onPress={(e) => {
                        if (e && e.stopPropagation) e.stopPropagation();
                        setNotBillable(partsOf(t), !t.nb, '', true);
                    }}
                />
            </View>
            <View style={{ width: W.ct, paddingTop: 2, paddingBottom: 2, paddingLeft: 8, paddingRight: 8 }}>
                <Text style={{ fontSize: 12, color: '#1e293b' }}>{t.ct}</Text>
                {t.ctDesc ? <Text style={{ fontSize: 10, color: '#94a3b8' }} numberOfLines={1}>{t.ctDesc}</Text> : null}
            </View>
            <Text style={[gtd, { width: W.hrs, textAlign: 'right', color: showsHours(t) ? '#1e293b' : '#94a3b8' }]}>{hoursText(t)}</Text>
            {openCell(null)}
            {costCell(t.cost, partsOf(t), [
                fDate(t.date), t.po ? `${poName(t.po)}${t.pol ? ` · Line ${t.pol}` : ''}` : t.cc, t.who || t.ds,
            ].filter(Boolean).join(' · '), t.pol)}
            <Text style={[gtd, {
                width: W.mk, textAlign: 'right',
                color: t.nb ? '#94a3b8' : t.noRate ? '#9a3412' : '#1e293b',
                fontWeight: t.noRate && !t.nb ? '700' : undefined,
            }]}>{mkText(t)}</Text>
            <Text style={[gtd, { width: W.bill, textAlign: 'right', fontWeight: '600', color: t.nb || t.noRate ? '#94a3b8' : '#1e293b' }]}>{t.nb || t.noRate ? '—' : money(t.bill)}</Text>
            {/* An entry on this grid is open by definition — its PO line's
                header carries what already went on a summary. */}
            {invdCell(null)}
            <View style={{ width: W.eq, paddingTop: 2, paddingBottom: 2, paddingLeft: 8, paddingRight: 8 }}>
                <Text title={t.eq || undefined} style={{ fontSize: 12, color: '#1e293b' }} numberOfLines={1}>{t.eqDs || t.eq || '—'}</Text>
                {(t.tier || t.rentUsage) && !t.inv ? (
                    <Text
                        title={t.poJob ? `The PO is charged to ${t.poJob}; the hours are on this job's cards` : undefined}
                        style={{ fontSize: 9, color: t.pt || (t.ct === '52200' && !t.rentUsage) || equipBills[t.tier] ? '#94a3b8' : '#92400e', marginTop: 1 }}
                        numberOfLines={1}
                    >
                        {t.pt
                            ? `pass-through · ${t.mk ? `cost +${t.mk}%` : 'at cost'}`
                            : t.rentUsage
                            ? `${equipBills[t.tier] ? `HeavyJob hours → ${equipBills[t.tier].name}` : t.tier ? 'not on rate sheet · no rate' : 'no JDE tier · no rate'}${t.poJob ? ` · PO on ${t.poJob}` : ''}`
                            : t.ct === '52200'
                            ? t.ruUnit && t.nb ? 'rental · bills on HeavyJob hours' : t.ruMissing ? `rental · not in HeavyJob · cost +${t.mk}%` : `rental · cost +${t.mk}%`
                            : equipBills[t.tier]
                                ? t.hourly ? `→ ${equipBills[t.tier].name}` : `no hours · +${t.mk}%`
                                : `not on rate sheet · +${t.mk}%`}
                    </Text>
                ) : null}
            </View>
            <View style={[gtd, { width: W.vinv }]}>
                {t.vinv ? <Badge kind={t.vinv} /> : <Text style={{ fontSize: 12, color: '#94a3b8' }}>—</Text>}
            </View>
            <View style={[gtd, { width: W.vinvNo }]}>
                {t.pdf
                    ? invNoLink(t.pdf, t.vinvNo || 'PDF', [t.who, t.vinvNo && `#${t.vinvNo}`, t.po && `PO ${t.po}`].filter(Boolean).join(' · '))
                    : t.vinvNo
                    ? <Text title={t.vinvNo} style={{ fontSize: 12, color: '#1e293b' }} numberOfLines={1}>{t.vinvNo}</Text>
                    : <Text style={{ fontSize: 12, color: '#94a3b8' }}>—</Text>}
            </View>
            <View style={[gtd, { width: W.vinvDate }]}>
                <Text
                    title={t.vinvDate ? (t.vinv === 'Received' ? 'Last received' : 'Voucher GL date') : undefined}
                    style={{ fontSize: 12, color: t.vinvDate ? '#64748b' : '#94a3b8' }}
                >
                    {t.vinvDate || '—'}
                </Text>
            </View>
            <Pressable
                title={t.parts
                    ? `History is kept per day${t.hist.length ? ` — ${t.hist.length} event${t.hist.length === 1 ? '' : 's'} across the days` : ''}; opens the row to them`
                    : t.hist.length ? `Line history — ${t.hist.length} event${t.hist.length === 1 ? '' : 's'}` : 'Line history'}
                onPress={(e) => {
                    if (e && e.stopPropagation) e.stopPropagation();
                    // A rolled row's history lives on its days — open the row to them.
                    if (t.parts) setFoldOpen((p) => ({ ...p, [t.key]: true }));
                    else openHistory(t);
                }}
                style={[gtd, { width: W.hist, flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer' }]}
            >
                <Text style={{ fontSize: 14, color: t.hist.length ? '#1e40af' : '#94a3b8' }}>↺</Text>
                {t.hist.length ? (
                    <Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '600', marginLeft: 4 }}>{t.hist.length}</Text>
                ) : null}
            </Pressable>
            <View style={[gtd, { width: W.rev }]}>
                {reviewOf(t) !== 'Not Reviewed' ? (
                    <>
                        <Badge kind={reviewOf(t)} />
                        {t.lastRev ? (
                            <Text style={{ fontSize: 9, color: '#94a3b8', marginTop: 1 }}>
                                {initials(t.lastRev.u)} {fDate(t.lastRev.t)}
                            </Text>
                        ) : null}
                    </>
                ) : (
                    <Text style={{ fontSize: 12, color: '#94a3b8' }}>—</Text>
                )}
            </View>
            <View style={[gtd, { width: W.sig }]}>
                {!sigTicketed(t)
                    ? <Text style={{ fontSize: 12, color: '#94a3b8' }}>—</Text>
                    : jobSigs.loading ? <Text style={{ fontSize: 11, color: '#cbd5e1' }}>…</Text>
                    : t.parts ? (() => {
                        // A rolled week: Signed when every day is, else how many are.
                        const st = signedStanding(t.parts.map((p) => p.date), signedBy);
                        if (st.k && st.stage !== 'Signed') return <Text style={{ fontSize: 10, color: '#92400e' }}>{st.k} of {st.n} days</Text>;
                        return <SigTag sig={st.stage ? { stage: st.stage, docs: [] } : null} />;
                    })()
                    : <SigTag sig={sigOf(t)} />}
            </View>
        </View>
        );
    };

    if (!selectedJob) {
        return (
            <View style={{ flex: 1, alignItems: 'center', paddingTop: 80 }}>
                <Text style={{ fontSize: 14, color: '#64748b' }}>Pick a job in the top menu to start billing.</Text>
            </View>
        );
    }


    return (
        <ScrollView style={{ flex: 1, backgroundColor: '#f8fafc' }}>
            <View style={{ paddingTop: 22, paddingBottom: 40, paddingLeft: 26, paddingRight: 26 }}>
                {billingLoadError ? (
                    <View style={{ backgroundColor: '#fef2f2', borderRadius: 6, borderWidth: 1, borderColor: '#ef4444', paddingTop: 8, paddingBottom: 8, paddingLeft: 12, paddingRight: 12, marginTop: 8, marginBottom: 8 }}>
                        <Text style={{ fontSize: 12, color: '#991b1b' }}>
                            Couldn&apos;t load this job&apos;s billing records — billing is read-only until they load. Reload the page to try again.
                        </Text>
                    </View>
                ) : versionLocked ? (
                    <View style={{ backgroundColor: '#f1f5f9', borderRadius: 6, borderWidth: 1, borderColor: '#cbd5e1', paddingTop: 8, paddingBottom: 8, paddingLeft: 12, paddingRight: 12, marginTop: 8, marginBottom: 8 }}>
                        <Text style={{ fontSize: 12, color: '#475569' }}>
                            Loading this job&apos;s billing records…
                        </Text>
                    </View>
                ) : null}

                <View style={[card, { marginTop: 10, paddingTop: 10, paddingBottom: 10, marginBottom: 10 }]}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-start' }}>
                        {/* The trailer TV board (billingDashboard.js, /billing-tv)
                            covers the VoltaGrid pair only — keep in step with
                            its DASHBOARD_JOBS. */}
                        {/* {['54026070', '54026071'].includes(String(selectedJob)) ? (
                            <View style={{ marginRight: 10 }}>
                                <OutlineButton label="Dashboard ↗" onPress={() => window.open('/billing-tv', '_blank', 'noopener')} />
                            </View>
                        ) : null} */}
                        <View style={{ flexDirection: 'row' }}>
                            {['recon', 'transactions', 'invoices', /* 'invview', */ 'timecards', /* 'manual', */ 'workbook', 'report' /* , 'rates' */].map((m) => (
                                <Chip
                                    key={m}
                                    label={VIEW_LABELS[m] || (m === 'invoices'
                                        ? `Invoices (${invoiceCount})` : m)}
                                    active={viewMode === m}
                                    onPress={() => { setViewMode(m); setOpenInvoice(null); }}
                                />
                            ))}
                        </View>
                    </View>
                    {/* The money roll-up is the Summaries view's picture of the
                        job; the other views carry their own numbers and the
                        strip only competes with them there. Selection totals
                        follow the selection instead, wherever it is made.
                        The 10 inset lines the labels up with the view chips'
                        text (the chips draw no border, so their padding is
                        the edge the eye reads). */}
                    {(() => {
                        const roll = viewMode === 'invoices';
                        if (!roll && !selectedTxns.length && !selHours) return null;
                        return (
                            <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-start', marginTop: 8, paddingLeft: 10 }}>
                                {roll ? (
                                    <>
                                        {/* Three stats: No Rate rides under Ready
                                            to Invoice, Outstanding A/R under
                                            Already Billed. */}
                                        <Stat
                                            first w={180} label="READY TO INVOICE" value={money0(unbilledAmt)} sub={`(${unbilled.length})`} tone="#166534"
                                            note={`${money0(noRateAmt)} no rate (${noRateTxns.length})`}
                                            noteTone={noRateTxns.length ? '#9a3412' : undefined}
                                        />
                                        <Stat
                                            w={180} label="ALREADY BILLED" value={money0(billedAmt)} sub={`(${billedTxns.length} on ${invoices.filter((v) => v.status !== 'Void').length} inv)`}
                                            note={openInvs.length ? `${money0(outstandingAmt)} outstanding (${openInvs.length} open)` : 'nothing outstanding'}
                                            noteTone={openInvs.length ? '#92400e' : undefined}
                                        />
                                        <Stat w={180} label="NOT BILLABLE" value={money0(nbAmt)} sub={`(${nbTxns.length})`} tone="#475569" />
                                    </>
                                ) : null}
                                {selectedTxns.length ? <Stat first={!roll} label="SELECTED TOTAL" value={money(selTotal)} sub={`(${selectedTxns.length})`} tone="#1e40af" /> : null}
                                {selHours ? <Stat first={!roll && !selectedTxns.length} label="SELECTED HOURS" value={selHours.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} tone="#1e40af" /> : null}
                            </View>
                        );
                    })()}
                </View>

                {viewMode === 'transactions' ? (
                    <>
                        {/* Third Party's views: the cost lines themselves,
                            the PO-line invoice chase list
                            (billingInvoiceChase.js), and each vendor
                            invoice's lien waiver (billingLienWaivers.js). */}
                        <View style={{ flexDirection: 'row', marginBottom: 4 }}>
                            {[{ k: 'lines', label: 'Purchase Orders' }, { k: 'chase', label: 'Bill Invoice' }, { k: 'liens', label: 'Lien Waivers' }].map((o) => (
                                <Chip key={o.k} label={o.label} active={tpView === o.k} onPress={() => setTpView(o.k)} />
                            ))}
                        </View>
                        {tpView === 'liens' ? (
                            <BillingLienWaivers
                                jobKey={selectedJob}
                                userEmail={userEmail || ''}
                                poStatus={poStatus}
                                txns={txns}
                                invAmtByKey={invAmtByKey}
                            />
                        ) : tpView === 'chase' ? (
                            <BillingInvoiceChase
                                jobKey={selectedJob}
                                txns={txns}
                                invAmtByKey={invAmtByKey}
                                poStatus={poStatus}
                                openTapDoc={openTapDoc}
                                // Ticked chase rows select their cost lines in
                                // the grid's own selection, so the buttons open
                                // the same Create / Add to Invoice modal.
                                sel={sel}
                                setSel={setSel}
                                selCount={selectedTxns.length}
                                locked={versionLocked}
                                canAdd={addableInvs.length > 0}
                                onCreate={() => selectedTxns.length && !versionLocked && setShowModal('create')}
                                onAdd={openAddModal}
                                onOpenInvoice={(no) => { setViewMode('invoices'); setOpenInvoice(no); }}
                                // Same write as the grid's BILLABLE switch — keeps
                                // the in-progress selection.
                                onMakeBillable={(list) => setNotBillable(list, false, '', true)}
                                CheckBox={CheckBox}
                            />
                        ) : (
                        <>
                        <View id="billing-filter-card" style={card}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
                                <TextInput
                                    value={search}
                                    onChangeText={setSearch}
                                    placeholder="Search cost code, vendor, PO, description..."
                                    style={{
                                        width: 300, fontSize: 12, borderRadius: 4, borderColor: '#cbd5e1',
                                        paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8,
                                        marginRight: 10, marginBottom: 6,
                                    }}
                                />
                                {[
                                    { m: 'po', label: 'Group by PO' },
                                    { m: 'vendor', label: 'Group by vendor', tip: 'Vendor → PO → PO line, largest spend first' },
                                    { m: 'week', label: 'Group by week', tip: 'Week ending → person / unit, with the week\'s not-billable cost last' },
                                ].map((o) => (
                                    <Pressable
                                        key={o.m}
                                        title={o.tip}
                                        onPress={() => setGroupMode((m) => (m === o.m ? '' : o.m))}
                                        style={{ flexDirection: 'row', alignItems: 'center', marginRight: 14, marginBottom: 6, cursor: 'pointer' }}
                                    >
                                        <CheckBox
                                            checked={groupMode === o.m}
                                            onPress={(e) => {
                                                // The label is the click target too — without this the
                                                // box's own click would bubble and toggle twice.
                                                if (e && e.stopPropagation) e.stopPropagation();
                                                setGroupMode((m) => (m === o.m ? '' : o.m));
                                            }}
                                        />
                                        <Text style={{ fontSize: 12, color: '#1e293b', marginLeft: 6 }}>{o.label}</Text>
                                    </Pressable>
                                ))}
                                <OutlineButton label="Export to Excel" onPress={exportExcel} disabled={!visible.length} />
                                <View style={{ width: 8 }} />
                                <Pressable
                                    onPress={() => selectedTxns.length && !versionLocked && setShowModal('create')}
                                    style={{
                                        paddingTop: 5, paddingBottom: 5, paddingLeft: 14, paddingRight: 14,
                                        borderRadius: 4, marginBottom: 6,
                                        backgroundColor: selectedTxns.length && !versionLocked ? '#0f172a' : '#cbd5e1',
                                    }}
                                >
                                    <Text style={{ fontSize: 12, color: '#fff', fontWeight: '600' }}>
                                        {selectedTxns.length ? `Create Invoice (${selectedTxns.length})` : 'Create Invoice'}
                                    </Text>
                                </Pressable>
                                <View style={{ width: 8 }} />
                                {/* Only Draft / Kicked Back invoices take new lines — the
                                    button stays greyed while there are none. */}
                                <OutlineButton
                                    label={selectedTxns.length ? `Add to Invoice (${selectedTxns.length})` : 'Add to Invoice'}
                                    onPress={openAddModal}
                                    disabled={!selectedTxns.length || versionLocked || !addableInvs.length}
                                />
                                <View style={{ flexGrow: 1 }} />
                            </View>

                            <View style={{ flexDirection: 'row', alignItems: 'flex-start', flexWrap: 'wrap', zIndex: 5, marginTop: 4 }}>
                                <FilterGroup label="STATUS">
                                    {STATUS_CHIPS.map((s) => (
                                        <ListOpt key={s} label={s} active={statusFilter === s} count={statusCounts[s] || 0} onPress={() => setStatusFilter(s)} />
                                    ))}
                                </FilterGroup>
                                <FilterGroup label="REVIEW">
                                    {['Reviewed', 'Flagged', 'Not Reviewed'].map((s) => (
                                        <ListOpt key={s} label={s} active={reviewFilter === s} count={reviewCounts[s] || 0} onPress={() => setReviewFilter(reviewFilter === s ? 'All' : s)} />
                                    ))}
                                </FilterGroup>
                                <FilterGroup label="SIGNED SUMMARY">
                                    {['Signed', 'Not signed'].map((s) => (
                                        <ListOpt key={s} label={s} active={sigFilter === s} count={sigCounts[s] || 0} onPress={() => setSigFilter(sigFilter === s ? 'All' : s)} />
                                    ))}
                                </FilterGroup>
                                <FilterGroup label="TYPE">
                                    {[
                                        { v: 'Equipment', t: 'Equipment' },
                                        { v: 'Materials', t: 'Materials' },
                                        { v: 'Subcontractors', t: 'Subs' },
                                        { v: 'Other', t: 'Other' },
                                    ].map((c) => (
                                        <ListOpt key={c.v} label={c.t} active={classFilter.includes(c.v)} onPress={() => toggleIn(classFilter, setClassFilter, c.v)} />
                                    ))}
                                    <View style={{ borderTopWidth: 1, borderColor: '#f1f5f9', marginTop: 3, paddingTop: 3, alignSelf: 'stretch' }}>
                                        <ListOpt label="P-Card only" active={pcardOnly} onPress={() => setPcardOnly(!pcardOnly)} />
                                        <ListOpt label="Pass-through only" active={ptOnly} count={tpTxns.filter((t) => t.pt).length} onPress={() => setPtOnly(!ptOnly)} />
                                    </View>
                                </FilterGroup>
                                <FilterGroup label="REFINE">
                                    <FilterDropdown
                                        label="Cost code"
                                        summary={ccFilter.length
                                            ? `Cost code (${ccExclude ? 'all except ' : ''}${ccFilter.length})`
                                            : 'Cost code'}
                                        options={filterOptions.costCodes}
                                        selected={ccFilter}
                                        exclude={ccExclude}
                                        onToggleExclude={setCcExclude}
                                        onToggle={(v) => toggleIn(ccFilter, setCcFilter, v)}
                                        onClear={() => setCcFilter([])}
                                        renderLabel={(o) => (o.desc ? `${o.value} — ${String(o.desc).slice(0, 38)}` : o.value)}
                                    />
                                    <FilterDropdown
                                        label="Cost type"
                                        summary={ctFilter.length
                                            ? `Cost type (${ctExclude ? 'all except ' : ''}${ctFilter.length})`
                                            : 'Cost type'}
                                        options={filterOptions.costTypes}
                                        selected={ctFilter}
                                        exclude={ctExclude}
                                        onToggleExclude={setCtExclude}
                                        onToggle={(v) => toggleIn(ctFilter, setCtFilter, v)}
                                        onClear={() => setCtFilter([])}
                                        renderLabel={(o) => (o.desc ? `${o.value} — ${String(o.desc).slice(0, 38)}` : o.value)}
                                    />
                                    <FilterDropdown
                                        label="Vendor"
                                        options={filterOptions.people}
                                        selected={whoFilter}
                                        onToggle={(v) => toggleIn(whoFilter, setWhoFilter, v)}
                                        onClear={() => setWhoFilter([])}
                                    />
                                    <FilterDropdown
                                        label="PO"
                                        options={filterOptions.pos}
                                        selected={poFilter}
                                        onToggle={(v) => toggleIn(poFilter, setPoFilter, v)}
                                        onClear={() => setPoFilter([])}
                                        renderLabel={(o) => (o.desc ? `${o.value} — ${String(o.desc).slice(0, 38)}` : o.value)}
                                    />
                                </FilterGroup>
                                <FilterGroup label="WORK DATE" last>
                                    <DateInput value={dateFrom} onValueChange={setDateFrom} />
                                    <DateInput value={dateTo} onValueChange={setDateTo} />
                                    <Pressable onPress={clearFilters} style={{ marginRight: 10, marginBottom: 6 }}>
                                        <Text style={{ fontSize: 12, color: '#0f172a', fontWeight: '600' }}>Clear filters</Text>
                                    </Pressable>
                                </FilterGroup>
                            </View>

                            {selectedRows.length && !versionLocked ? (
                                <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', borderTopWidth: 1, borderColor: '#e2e8f0', marginTop: 4, paddingTop: 10, backgroundColor: '#fff' }}>
                                    <Text style={{ fontSize: 12, fontWeight: '700', color: '#1e40af', marginRight: 10, marginBottom: 6 }}>
                                        {selectedRows.length} selected
                                    </Text>
                                    <Pressable onPress={() => setSel({})} style={{ marginRight: 14, marginBottom: 6 }}>
                                        <Text style={{ fontSize: 11, color: '#64748b', textDecorationLine: 'underline' }}>Deselect all</Text>
                                    </Pressable>
                                    <Text style={{ fontSize: 11, color: '#64748b', marginRight: 8, marginBottom: 6 }}>Set review status:</Text>
                                    <OutlineButton label="Reviewed" onPress={() => setReviewStatus('Reviewed')} />
                                    <View style={{ width: 6 }} />
                                    <OutlineButton label="Flagged" onPress={() => setReviewStatus('Flagged')} />
                                    <View style={{ width: 6 }} />
                                    <OutlineButton label="Clear review" onPress={() => setReviewStatus('')} />
                                    <View style={{ width: 18 }} />
                                    <OutlineButton label={`Billable (${selectedNB.length})`} disabled={!selectedNB.length} onPress={() => setNotBillable(selectedNB, false, '')} />
                                    <View style={{ width: 6 }} />
                                    <OutlineButton label={`Not Billable (${selectedBillable.length})`} disabled={!selectedBillable.length} onPress={() => { setNbReason(''); setNbPrompt(true); }} />
                                    {(() => {
                                        // Labor never passes through — the buttons count what they'd touch.
                                        const ptRows = selectedRows.filter((t) => t.cls !== 'Labor' && !t.inv);
                                        if (!ptRows.length) return null;
                                        return (
                                            <>
                                                <View style={{ width: 18 }} />
                                                <Text style={{ fontSize: 11, color: '#64748b', marginRight: 8, marginBottom: 6 }}>Pass-through:</Text>
                                                <OutlineButton label={`Pass-through (${ptRows.length})`} onPress={() => { setPassThru(ptRows, 'Y'); setSel({}); }} />
                                                <View style={{ width: 6 }} />
                                                <OutlineButton label="Not pass-through" onPress={() => { setPassThru(ptRows, 'N'); setSel({}); }} />
                                                {ptRows.some((t) => t.ptOv) ? (
                                                    <>
                                                        <View style={{ width: 6 }} />
                                                        <OutlineButton label="Use rules" onPress={() => { setPassThru(ptRows, ''); setSel({}); }} />
                                                    </>
                                                ) : null}
                                            </>
                                        );
                                    })()}
                                </View>
                            ) : null}

                        </View>

                        <View style={card}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
                                <Text style={{ fontSize: 14, fontWeight: '700', color: '#0f172a', flexGrow: 1 }}>
                                    Transactions available to invoice
                                </Text>
                                {groupPo && poGroups.length ? (
                                    <Pressable onPress={toggleAllGroups} style={{ marginRight: 14 }}>
                                        <Text style={{ fontSize: 12, color: '#0f172a', fontWeight: '600' }}>
                                            {anyGroupOpen ? 'Collapse all' : 'Expand all'}
                                        </Text>
                                    </Pressable>
                                ) : null}
                                {selectedRows.length ? (
                                    <Pressable onPress={() => setSel({})} style={{ marginRight: 14 }}>
                                        <Text style={{ fontSize: 12, color: '#0f172a', fontWeight: '600' }}>
                                            Clear selection ({selectedRows.length})
                                        </Text>
                                    </Pressable>
                                ) : null}
                                <Text style={{ fontSize: 12, color: '#64748b' }}>
                                    {visible.length === tpTxns.length
                                        ? `${tpTxns.length} transactions`
                                        : `${visible.length} of ${tpTxns.length} transactions`}
                                    {tpBilled ? ` · ${tpBilled} on invoices not shown` : ''}
                                    {weekView
                                        ? ` · ${weekView.weeks.length} week${weekView.weeks.length === 1 ? '' : 's'}`
                                        : vendorView ? ` · ${vendorView.weeks.length} vendor${vendorView.weeks.length === 1 ? '' : 's'}`
                                        : groupPo ? ` · ${poGroups.length} PO group${poGroups.length === 1 ? '' : 's'}` : ''}
                                    {' · '}{money(visible.reduce((s, t) => s + t.bill, 0))} bill value
                                </Text>
                            </View>
                            {allChecked && openMatches.length > visibleOpen.length && !openMatches.every((t) => sel[t.key]) ? (
                                <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#eff6ff', borderRadius: 4, paddingTop: 6, paddingBottom: 6, paddingLeft: 10, paddingRight: 10, marginBottom: 6 }}>
                                    <Text style={{ fontSize: 12, color: '#1e293b' }}>
                                        All {visibleOpen.length} rows shown are selected.
                                    </Text>
                                    <Pressable
                                        onPress={() => setSel((p) => {
                                            const next = { ...p };
                                            openMatches.forEach((t) => { next[t.key] = true; });
                                            return next;
                                        })}
                                        style={{ marginLeft: 10 }}
                                    >
                                        <Text style={{ fontSize: 12, color: '#1e40af', fontWeight: '600' }}>
                                            Select all {openMatches.length} matching transactions
                                        </Text>
                                    </Pressable>
                                </View>
                            ) : null}
                            <ScrollView horizontal>
                                <View style={{ minWidth: TABLE_W }}>
                                    {/* Pinned by syncStickyHeader — position:sticky can't cross
                                        the horizontal ScrollView to reach the page scroller, so
                                        the page's onScroll translates this row instead. */}
                                    <View id="billing-grid-header" style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#e2e8f0', alignItems: 'center', backgroundColor: '#fff', zIndex: 2 }}>
                                        <View style={[th, { width: W.sel }]}>
                                            <CheckBox checked={allChecked} disabled={!visibleOpen.length} onPress={toggleAll} />
                                        </View>
                                        {COLS.slice(1).map((c) => (
                                            <Pressable key={c.key} onPress={c.sortable ? () => onSort(c.key) : undefined} style={{ width: c.w }}>
                                                <Text style={[th, { textAlign: c.right ? 'right' : 'left' }]}>
                                                    {c.label}{c.sortable && sortKey === c.key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                                                </Text>
                                            </Pressable>
                                        ))}
                                    </View>
                                    {loadingCost && !txns.length ? (
                                        <SkeletonRows cells={COLS.map((c) => ({ width: c.w }))} rows={8} />
                                    ) : null}
                                    {flatRows.map((e, i) => renderTpEntry(e, i))}
                                    {!loadingCost && !visible.length ? (
                                        <Text style={{ fontSize: 13, color: '#64748b', paddingTop: 14, paddingBottom: 6 }}>
                                            No transactions match the current filters.
                                        </Text>
                                    ) : null}
                                </View>
                            </ScrollView>
                            {(groupPo ? poGroups.length : visible.length) > 250 && !showAll ? (
                                <Pressable onPress={() => setShowAll(true)} style={{ marginTop: 8 }}>
                                    <Text style={{ fontSize: 12, color: '#0f172a', fontWeight: '600' }}>
                                        {groupPo
                                            ? `Showing first 250 ${weekView ? 'groups' : 'POs'} — show all ` + poGroups.length
                                            : 'Showing first 250 — show all ' + visible.length}
                                    </Text>
                                </Pressable>
                            ) : null}
                        </View>
                        </>
                        )}
                    </>
                ) : viewMode === 'timecards' ? (
                    // Time Card Approval: the day-centric tracker (Day/Night
                    // shifts, expandable HeavyJob detail, ticket preview).
                    <BillingTimecardApproval
                        jobKey={selectedJob}
                        jobLabel={selectedJob}
                        ratemap={laborRatemap}
                        staffCls={fieldStaffCls}
                        staffClsFile={fieldMap?.file || null}
                        equipName={equipName}
                        invoices={invoices}
                        billed={billed}
                        prefill={tcPrefill}
                        onPrefillUsed={() => setTcPrefill(null)}
                        userRows={billingUserRows}
                        saveRows={saveRows}
                        versionLocked={versionLocked}
                        userName={userName}
                        userEmail={userEmail}
                        settings={settings}
                        thirdParty={thirdPartyOutlook}
                    />
                ) : /* viewMode === 'manual' ? (
                    // Custom Ticket: the hand-entered time card + document
                    // upload for days the HEET feed has not landed yet.
                    <BillingManualTicket
                        jobKey={selectedJob}
                        jobName={selectedJobData?.Job_Description || ''}
                        client={String(selectedJobData?.AR_AB_Name || selectedJobData?.Client_Name || '').trim()}
                        ratemap={laborRatemap}
                        userRows={billingUserRows}
                        saveRows={saveRows}
                        versionLocked={versionLocked}
                    />
                ) : viewMode === 'invview' ? (
                    // Invoices (old tab): the job's issued summaries split
                    // into Labor / Equipment / Third Party, over the unbilled
                    // rows in the same three sections. Read-only — it derives
                    // everything from txns and invTotals and writes nothing
                    // back.
                    <BillingInvoices
                        txns={txns}
                        invoices={invoices}
                        invTotals={invTotals}
                        summaryLines={summaryLines}
                        groupOf={groupOf}
                        bucketOf={bucketOf}
                        fDate={fDate}
                        Badge={Badge}
                    />
                ) : */ viewMode === 'recon' ? (
                    // Reconciliation: the HeavyJob (at send vs now) and
                    // Invoice (ticket vs JDE) checks over the stored
                    // snapshots in PC.Field_Ticket_Reconciliation.
                    <BillingReconciliation
                        jobKey={selectedJob}
                        txns={txns}
                        costLoading={Boolean(loadingCost)}
                        envelopes={jobSigs.job === selectedJob ? jobSigs.list : []}
                        invoices={invoices}
                        invTotals={invTotals}
                        equipTiers={jobEquipTiers}
                        ticketDocs={ticketDocs}
                        onCreateTicket={(p) => { setTcPrefill({ ...p, nonce: Date.now() }); setViewMode('timecards'); }}
                        onCreateInvoice={(weeks) => { setWbPrefill({ weeks, build: true, nonce: Date.now() }); setViewMode('workbook'); }}
                        invoiceDrafts={invoiceDrafts}
                        userName={userName || ''}
                        userEmail={userEmail || ''}
                    />
                ) : viewMode === 'workbook' ? (
                    <BillingInvoiceWorkbook
                        saveRows={saveRows}
                        versionLocked={versionLocked}
                        jobKey={String(selectedJob)}
                        jobName={String(selectedJobData?.BU_Description || selectedJobData?.Job_Description || '').trim()}
                        client={String(selectedJobData?.AR_AB_Name || selectedJobData?.Client_Name || '').trim()}
                        userName={userName || ''}
                        userRows={billingUserRows}
                        settings={settings}
                        equipName={equipName}
                        txns={txns}
                        envelopes={jobSigs.job === selectedJob ? jobSigs.list : []}
                        prefill={wbPrefill}
                        onPrefillUsed={() => setWbPrefill(null)}
                    />
                ) : viewMode === 'report' ? (
                    // Cost Report: the job's cost history totaled by cost
                    // code, cost type and billable status.
                    <BillingReport
                        txns={reportTxns}
                        jobKey={selectedJob}
                        jobName={String(selectedJobData?.BU_Description || selectedJobData?.Job_Description || '').trim()}
                        client={String(selectedJobData?.AR_AB_Name || selectedJobData?.Client_Name || '').trim()}
                        userName={userName || ''}
                        contractRules={contractRules}
                        fuelCtx={fuelCtx}
                        // By cost type reads what was invoiced off the
                        // invoices in DocuSign; until this job's are in it
                        // shows its revenue as loading, not as $0.
                        invoiceRows={approvalRows}
                        invoicesReady={jobSigs.job === selectedJob && !jobSigs.loading}
                    />
                ) : (
                    <View style={card}>
                        <Text style={{ fontSize: 14, fontWeight: '700', color: '#0f172a', marginBottom: 8 }}>Invoices</Text>
                        {/* Every invoice out for approval or approved in DocuSign,
                            built in Fusion or not, above the summaries built here. */}
                        <BillingInvoiceApprovals
                            envelopes={jobSigs.job === selectedJob ? jobSigs.list : []}
                            loading={jobSigs.loading}
                            consoleUrlBase={jobSigs.consoleUrlBase}
                        />
                        {/* Once the job has invoices in DocuSign, the list above is
                            the invoices and the sent summaries are not listed
                            again (Seth 10/26). A summary still being built here
                            keeps its row, and so does one opened from a link
                            (Bill Invoice, Line History). */}
                        {summaryRows.length && approvalRows.length ? (
                            <Text style={{ fontSize: 13, fontWeight: '600', color: '#0f172a', marginBottom: 6 }}>Billing summaries built in Fusion</Text>
                        ) : null}
                        {summaryRows.length ? (
                        <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#e2e8f0' }}>
                            <Text style={[th, { width: 110 }]}>SUMMARY #</Text>
                            <Text style={[th, { width: 80 }]}>DATE</Text>
                            <Text style={[th, { width: 60, textAlign: 'right' }]}>LINES</Text>
                            <Text style={[th, { width: 152 }]}>INCLUDES</Text>
                            <Text style={[th, { width: 104 }]}>LABOR QA</Text>
                            <Text style={[th, { width: 110, textAlign: 'right' }]}>COST</Text>
                            <Text style={[th, { width: 110, textAlign: 'right' }]}>MARKUP</Text>
                            <Text style={[th, { width: 120, textAlign: 'right' }]}>TOTAL</Text>
                            <Text style={[th, { width: 138 }]}>STATUS</Text>
                            <Text style={[th, { flexGrow: 1 }]} />
                        </View>
                        ) : null}
                        {summaryRows.map((v) => {
                            const t = invTotals[v.no] || { lines: [], cost: 0, total: 0 };
                            const isOpen = openInvoice === v.no;
                            // JDE billings cross-check: does the summary total show up
                            // as an invoice amount in the billings query yet?
                            const bMatch = isOpen
                                ? (jobBillings || []).find((b) => Math.abs(pNum(b.Invoice_Amount) - t.total) < 0.01)
                                : null;
                            return (
                                <View key={v.no}>
                                    <Pressable
                                        onPress={() => setOpenInvoice(isOpen ? null : v.no)}
                                        style={{ flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, borderColor: '#f1f5f9', backgroundColor: isOpen ? '#f8fafc' : '#fff' }}
                                    >
                                        <Text style={[td, { width: 110, fontWeight: '600', color: '#1e40af' }]}>{v.no}</Text>
                                        <Text style={[td, { width: 80 }]}>{fDate(v.date)}</Text>
                                        <Text style={[td, { width: 60, textAlign: 'right' }]}>{t.lines.length}</Text>
                                        <View style={[td, { width: 152, flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }]}>
                                            {invGroups(t.lines).map((g) => <GroupTag key={g} g={g} />)}
                                        </View>
                                        <View style={[td, { width: 104 }]}>
                                            {sumQa[v.no] ? <QaTag areas={sumQa[v.no].areas} /> : null}
                                        </View>
                                        <Text style={[td, { width: 110, textAlign: 'right' }]}>{money(t.cost)}</Text>
                                        <Text style={[td, { width: 110, textAlign: 'right' }]}>{money(t.total - t.cost)}</Text>
                                        <Text style={[td, { width: 120, textAlign: 'right', fontWeight: '600' }]}>{money(t.total)}</Text>
                                        <View style={[td, { width: 138 }]}>
                                            {/* Where the invoice approval stands in DocuSign (sumSign);
                                                a voided summary keeps its Void. … while the envelopes
                                                load, so one that is out never flashes Not Sent first. */}
                                            {v.status === 'Void' ? <Badge kind="Void" />
                                                : jobSigs.loading && !sumSign[v.no]?.env ? <Text style={{ fontSize: 11, color: '#cbd5e1' }}>…</Text>
                                                    : <Badge kind={sumSign[v.no]?.label || 'Not Sent'} />}
                                            {v.status !== 'Void' && sumSign[v.no]?.note ? (
                                                <Text style={{ fontSize: 9, color: '#94a3b8', marginTop: 2 }}>{sumSign[v.no].note}</Text>
                                            ) : null}
                                        </View>
                                        <View style={{ flexDirection: 'row', flexGrow: 1, justifyContent: 'flex-end', alignItems: 'center', paddingRight: 8 }}>
                                            <View style={{ width: 62, alignItems: 'center' }}>
                                                {!versionLocked && DELETABLE_STAGES.includes(v.status) ? (
                                                    <OutlineButton
                                                        label="Delete"
                                                        onPress={(e) => {
                                                            if (e && e.stopPropagation) e.stopPropagation();
                                                            setDelPrompt(v);
                                                        }}
                                                    />
                                                ) : null}
                                            </View>
                                        </View>
                                    </Pressable>
                                    {isOpen ? (
                                        <View style={{ backgroundColor: '#f8fafc', borderBottomWidth: 1, borderColor: '#e2e8f0', paddingTop: 8, paddingBottom: 10, paddingLeft: 16, paddingRight: 16 }}>
                                            <Text style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>
                                                Issued {fDate(v.date)} · {v.terms || settings.terms}
                                            </Text>
                                            <Text style={{ fontSize: 11, color: bMatch ? '#166534' : '#94a3b8', marginBottom: 4 }}>
                                                {bMatch
                                                    ? `Billings query: matched — invoice ${bMatch.Invoice_Number || '?'} · ${String(bMatch.Invoice_Date || '').split('T')[0]} · ${money(bMatch.Invoice_Amount)}`
                                                    : `Billings query: no invoice matching ${money(t.total)} yet`}
                                            </Text>
                                            {(() => {
                                                // The approval envelope behind STATUS, signer by signer.
                                                const env = sumSign[v.no]?.env;
                                                const nameOf = (x) => (env?.recipients || []).find((r) => String(r.Email || '').trim().toLowerCase() === x.email.toLowerCase())?.Name || x.email;
                                                return (
                                                    <Text style={{ fontSize: 11, color: env ? '#475569' : '#94a3b8', marginBottom: 4 }}>
                                                        {env
                                                            ? [`DocuSign: ${env.Document_Name || env.Subject || 'invoice approval'}`,
                                                                ...signersOf(env).map((x) => `${nameOf(x)} ${x.signed ? `signed${x.at ? ' ' + moment.utc(x.at).local().format('M/D') : ''}` : x.declined ? 'declined' : 'waiting'}`),
                                                            ].join(' · ')
                                                            : 'DocuSign: no invoice approval sent for these work dates yet'}
                                                    </Text>
                                                );
                                            })()}
                                            {v.h?.length ? (
                                                <View style={{ marginBottom: 8 }}>
                                                    {v.h.map((e, i) => (
                                                        <View key={i} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 2 }}>
                                                            <Text style={{ fontSize: 10, color: '#94a3b8', width: 92 }}>{e.t}</Text>
                                                            <Badge kind={e.s} />
                                                            <Text style={{ fontSize: 10, color: '#64748b', marginLeft: 6 }}>
                                                                {e.u}{e.r ? ` — ${e.r}` : ''}
                                                            </Text>
                                                        </View>
                                                    ))}
                                                </View>
                                            ) : null}
                                            {sumQa[v.no] ? (
                                                <View style={{ backgroundColor: '#fff', borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 6, paddingTop: 8, paddingBottom: 4, paddingLeft: 12, paddingRight: 12, marginBottom: 10 }}>
                                                    <QaFindings
                                                        areas={sumQa[v.no].areas}
                                                        note={`${sumQa[v.no].cur.length} person-day${sumQa[v.no].cur.length === 1 ? '' : 's'} checked against ${sumQa[v.no].hist.length} earlier on the job`}
                                                    />
                                                </View>
                                            ) : null}
                                            {(() => {
                                                // The lines read the way the tabs they came off read them:
                                                // labor and equipment on the Labor & Equipment grid (the
                                                // field ticket against JDE, scoped to this summary), the
                                                // rest on the Third Party grid, PO › Line.
                                                const { le, tp } = invoiceRows(v);
                                                const canFix = !versionLocked && OPEN_STAGES.includes(v.status);
                                                const canRemove = !versionLocked && NOT_SENT_STAGES.includes(v.status);
                                                const x = {
                                                    inv: v, canFix, canRemove,
                                                    isOpen: (k, dflt) => invOpen[`${v.no}|${k}`] ?? dflt,
                                                    setOpen: (k, on) => setInvOpen((p) => ({ ...p, [`${v.no}|${k}`]: on })),
                                                };
                                                const entries = poGroupsOf(tp).flatMap((g) => [{ g }, ...(!x.isOpen(`g|${g.po}`, true) ? []
                                                    : g.lines ? g.lines.flatMap((ln) => [{ ln }, ...(x.isOpen(`l|${ln.key}`, true) ? ln.rows.map((r) => ({ t: r })) : [])])
                                                    : g.rows.map((r) => ({ t: r })))]);
                                                const leHrs = le.filter((r) => r.ct !== '51500').reduce((s, r) => s + r.hrs, 0);
                                                const head = (label, rows, hrs) => (
                                                    <View style={{ flexDirection: 'row', alignItems: 'baseline', marginTop: 12, marginBottom: 6 }}>
                                                        <Text style={{ fontSize: 11, fontWeight: '700', color: '#0f172a', letterSpacing: 0.6 }}>{label}</Text>
                                                        <Text style={{ fontSize: 11, color: '#64748b', marginLeft: 8 }}>
                                                            {[
                                                                `${rows.length} line${rows.length === 1 ? '' : 's'}`,
                                                                hrs ? `${round1(hrs)} hrs` : '',
                                                                money(rows.reduce((s, r) => s + r.bill, 0)),
                                                            ].filter(Boolean).join(' · ')}
                                                        </Text>
                                                    </View>
                                                );
                                                return (
                                                    <>
                                                        {le.length ? (
                                                            <>
                                                                {head('LABOR & EQUIPMENT', le, leHrs)}
                                                                <View style={{ backgroundColor: '#fff', borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 6, paddingTop: 10, paddingBottom: 6, paddingLeft: 12, paddingRight: 12 }}>
                                                                    <BillingReconciliation
                                                                        invoice={v.no}
                                                                        jobKey={selectedJob}
                                                                        txns={txns}
                                                                        costLoading={Boolean(loadingCost)}
                                                                        envelopes={jobSigs.job === selectedJob ? jobSigs.list : []}
                                                                        invoices={invoices}
                                                                        invTotals={invTotals}
                                                                        equipTiers={jobEquipTiers}
                                                                        ticketDocs={ticketDocs}
                                                                        userName={userName || ''}
                                                                        userEmail={userEmail || ''}
                                                                        // Same fixes the old line list had: the class a
                                                                        // labor line bills as (re-freezes it here), and
                                                                        // taking lines off before the summary goes out.
                                                                        onReclass={canFix ? (lines) => {
                                                                            setLcApplyAll(false);
                                                                            setLcPrompt({ t: lines[0], also: lines.slice(1), inv: v });
                                                                        } : undefined}
                                                                        onRemove={canRemove ? (lines) => removeLinesFromSummary(v, lines) : undefined}
                                                                    />
                                                                </View>
                                                            </>
                                                        ) : null}
                                                        {tp.length ? (
                                                            <>
                                                                {head('THIRD PARTY', tp, 0)}
                                                                <ScrollView horizontal style={{ backgroundColor: '#fff', borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 6 }}>
                                                                    <View style={{ minWidth: TABLE_W }}>
                                                                        <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#e2e8f0', alignItems: 'center' }}>
                                                                            <View style={{ width: W.sel }} />
                                                                            {COLS.slice(1).filter((c) => c.key !== 'invd' && c.key !== 'opn').map((c) => (
                                                                                <Text key={c.key} style={[th, { width: c.w, textAlign: c.right ? 'right' : 'left' }]}>{c.label}</Text>
                                                                            ))}
                                                                        </View>
                                                                        {entries.map((e, i) => renderTpEntry(e, i, x))}
                                                                    </View>
                                                                </ScrollView>
                                                            </>
                                                        ) : null}
                                                        {(() => {
                                                            // Not-billable lines this summary closed out at $0 — the
                                                            // period's cost that doesn't bill, by why, off their
                                                            // frozen snapshots. Never in the totals or the exports.
                                                            const nbl = Object.entries(nbLines)
                                                                .filter(([, rec]) => rec.inv === v.no && rec.snap)
                                                                .map(([key, rec]) => ({ key, ...rec.snap }))
                                                                .sort((a, b) => String(a.d).localeCompare(String(b.d)));
                                                            if (!nbl.length) return null;
                                                            const byWhy = {};
                                                            nbl.forEach((l) => { (byWhy[l.rr || 'Not billable'] ||= []).push(l); });
                                                            const cost = nbl.reduce((s, l) => s + pNum(l.c), 0);
                                                            return (
                                                                <>
                                                                    <View style={{ flexDirection: 'row', alignItems: 'baseline', marginTop: 12, marginBottom: 6 }}>
                                                                        <Text style={{ fontSize: 11, fontWeight: '700', color: '#0f172a', letterSpacing: 0.6 }}>NOT BILLABLE</Text>
                                                                        <Text style={{ fontSize: 11, color: '#64748b', marginLeft: 8 }}>
                                                                            {`${nbl.length} line${nbl.length === 1 ? '' : 's'} · ${money(cost)} cost · closed on this summary at $0`}
                                                                        </Text>
                                                                    </View>
                                                                    <View style={{ backgroundColor: '#fff', borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 6, paddingTop: 4, paddingBottom: 4 }}>
                                                                        {Object.entries(byWhy).sort((a, b) => b[1].reduce((s, l) => s + pNum(l.c), 0) - a[1].reduce((s, l) => s + pNum(l.c), 0)).map(([why, ls]) => {
                                                                            const k = `nb|${why}`;
                                                                            const on = x.isOpen(k, false);
                                                                            return (
                                                                                <View key={why}>
                                                                                    <Pressable onPress={() => x.setOpen(k, !on)} style={{ flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, borderColor: '#f1f5f9', cursor: 'pointer' }}>
                                                                                        <Text style={[td, { width: 24, color: '#64748b' }]}>{on ? '▾' : '▸'}</Text>
                                                                                        <Text style={[td, { flexGrow: 1, flexShrink: 1 }]} numberOfLines={1}>{why}</Text>
                                                                                        <Text style={[td, { width: 80, textAlign: 'right', color: '#64748b' }]}>{ls.length} line{ls.length === 1 ? '' : 's'}</Text>
                                                                                        <Text style={[td, { width: 110, textAlign: 'right' }]}>{money(ls.reduce((s, l) => s + pNum(l.c), 0))}</Text>
                                                                                        <View style={{ width: 28 }} />
                                                                                    </Pressable>
                                                                                    {on ? ls.map((l) => (
                                                                                        <View key={l.key} style={{ flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, borderColor: '#f8fafc', backgroundColor: '#fafafa' }}>
                                                                                            <Text style={[td, { width: 24 }]} />
                                                                                            <Text style={[td, { width: 76, color: '#64748b' }]}>{fDate(l.d)}</Text>
                                                                                            <Text style={[td, { flexGrow: 1, flexShrink: 1 }]} numberOfLines={1}>{[l.who, l.ds].filter(Boolean).join(' · ') || '—'}</Text>
                                                                                            <Text style={[td, { width: 60, color: '#64748b' }]}>{l.ct}</Text>
                                                                                            <Text style={[td, { width: 70, color: '#64748b' }]}>{l.cc}</Text>
                                                                                            <Text style={[td, { width: 56, textAlign: 'right', color: '#64748b' }]}>{pNum(l.hr) ? round1(pNum(l.hr)) : ''}</Text>
                                                                                            <Text style={[td, { width: 110, textAlign: 'right' }]}>{money(pNum(l.c))}</Text>
                                                                                            {x.canRemove ? (
                                                                                                <Pressable title={`Release from ${v.no} — back to not billable`} onPress={() => releaseNbLine(v, l.key)} style={{ width: 28, alignItems: 'center', cursor: 'pointer' }}>
                                                                                                    <Text style={{ fontSize: 12, color: '#b91c1c', fontWeight: '700' }}>✕</Text>
                                                                                                </Pressable>
                                                                                            ) : <View style={{ width: 28 }} />}
                                                                                        </View>
                                                                                    )) : null}
                                                                                </View>
                                                                            );
                                                                        })}
                                                                    </View>
                                                                </>
                                                            );
                                                        })()}
                                                        {!le.length && !tp.length && !Object.values(nbLines).some((rec) => rec.inv === v.no) ? (
                                                            <Text style={{ fontSize: 12, color: '#64748b', paddingTop: 8 }}>No lines on this summary.</Text>
                                                        ) : null}
                                                    </>
                                                );
                                            })()}
                                            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: 8 }}>
                                                <View>
                                                    {!versionLocked && NOT_SENT_STAGES.includes(v.status) ? (
                                                        <OutlineButton label="Add Lines" onPress={() => openAddPicker(v)} />
                                                    ) : null}
                                                </View>
                                                <View style={{ width: 260 }}>
                                                    {t.hrs ? (
                                                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 }}>
                                                            <Text style={{ fontSize: 12, color: '#64748b' }}>Labor hours</Text>
                                                            <Text style={{ fontSize: 12, color: '#1e293b' }}>{round1(t.hrs)}</Text>
                                                        </View>
                                                    ) : null}
                                                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 }}>
                                                        <Text style={{ fontSize: 12, color: '#64748b' }}>Cost subtotal</Text>
                                                        <Text style={{ fontSize: 12, color: '#1e293b' }}>{money(t.cost)}</Text>
                                                    </View>
                                                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 }}>
                                                        <Text style={{ fontSize: 12, color: '#64748b' }}>Markup</Text>
                                                        <Text style={{ fontSize: 12, color: '#1e293b' }}>{money((t.lineTotal ?? t.total) - t.cost)}</Text>
                                                    </View>
                                                    {(() => {
                                                        // What of the total is pass-through freight/delivery —
                                                        // its own invoice group on the backup and FTMS.
                                                        const ptl = t.lines.filter((l) => l.pt);
                                                        if (!ptl.length) return null;
                                                        const ptBill = ptl.reduce((s, l) => s + (l.b != null ? pNum(l.b) : pNum(l.c) * (1 + pNum(l.m) / 100)), 0);
                                                        return (
                                                            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 }}>
                                                                <Text style={{ fontSize: 12, color: '#64748b' }}>incl. pass-through ({ptl.length} line{ptl.length === 1 ? '' : 's'})</Text>
                                                                <Text style={{ fontSize: 12, color: '#1e293b' }}>{money(ptBill)}</Text>
                                                            </View>
                                                        );
                                                    })()}
                                                    {t.fuel?.amt ? (
                                                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 }}>
                                                            <Text style={{ fontSize: 12, color: '#64748b' }}>Fuel surcharge ({t.fuel.periods.filter((p) => p.amt).map((p) => fmtPct(p.pct)).join(' / ')} equip)</Text>
                                                            <Text style={{ fontSize: 12, color: '#1e293b' }}>{money(t.fuel.amt)}</Text>
                                                        </View>
                                                    ) : null}
                                                    {t.fuel?.excl?.noFuel?.n ? (
                                                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 }}>
                                                            <Text style={{ fontSize: 11, color: '#94a3b8' }}>
                                                                {t.fuel.excl.noFuel.n} equipment line{t.fuel.excl.noFuel.n === 1 ? '' : 's'} burn no fuel — {money(t.fuel.excl.noFuel.base)} held out of the base
                                                            </Text>
                                                        </View>
                                                    ) : null}
                                                    {t.fuel?.unk?.n ? (
                                                        <Text style={{ fontSize: 11, color: '#b45309', marginBottom: 3 }}>
                                                            {t.fuel.unk.n} equipment line{t.fuel.unk.n === 1 ? '' : 's'} ({money(t.fuel.unk.base)}) map to no rate sheet row — surcharged by default, map the tier under Rates &amp; Rules
                                                        </Text>
                                                    ) : null}
                                                    {t.fuel?.missing?.length ? (
                                                        <Text style={{ fontSize: 11, color: '#b45309', marginBottom: 3 }}>
                                                            No diesel price entered for {t.fuel.missing.join(', ')} — enter it under Rates &amp; Rules
                                                        </Text>
                                                    ) : null}
                                                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', borderTopWidth: 1, borderColor: '#e2e8f0', paddingTop: 5 }}>
                                                        <Text style={{ fontSize: 13, fontWeight: '700', color: '#0f172a' }}>Total</Text>
                                                        <Text style={{ fontSize: 13, fontWeight: '700', color: '#0f172a' }}>{money(t.total)}</Text>
                                                    </View>
                                                </View>
                                            </View>
                                        </View>
                                    ) : null}
                                </View>
                            );
                        })}
                        {!invoices.length && !approvalRows.length ? (
                            <Text style={{ fontSize: 13, color: '#64748b', paddingTop: 14, paddingBottom: 6 }}>
                                No invoices yet — select unbilled transactions on the Transactions view and create one.
                            </Text>
                        ) : null}
                    </View>
                )}

                {/* Rates & Rules rides under the invoice list rather than on
                    a tab of its own — the rate sheet, markup and billability
                    rules are what these invoices price off. Collapsed until
                    asked for, so the list stays the page. */}
                {viewMode === 'invoices' ? (
                    <>
                        <BillingContractDocs jobKey={selectedJob} />
                        <Pressable
                            onPress={() => setRatesOpen((o) => !o)}
                            style={[card, { flexDirection: 'row', alignItems: 'center', paddingTop: 10, paddingBottom: 10, marginBottom: ratesOpen ? 10 : 16, cursor: 'pointer' }]}
                        >
                            <Text style={{ fontSize: 11, color: '#475569', width: 16 }}>{ratesOpen ? '▾' : '▸'}</Text>
                            <Text style={{ fontSize: 14, fontWeight: '700', color: '#0f172a' }}>
                                {`Rates & Rules — ${selectedJob || '—'}`}
                            </Text>
                            <Text style={{ fontSize: 12, color: '#64748b', marginLeft: 10 }}>
                                Rate sheet, markup &amp; settings, classification maps, billability rules — saved for this job only
                            </Text>
                        </Pressable>
                        {ratesOpen ? (
                            <BillingRates
                                userRows={billingUserRows}
                                saveRows={saveRows}
                                versionLocked={versionLocked}
                                jobKey={selectedJob}
                                jobName={selectedJobData?.Job_Description || selectedJobData?.BU_Description || ''}
                                client={String(selectedJobData?.AR_AB_Name || selectedJobData?.Client_Name || '').trim()}
                                userName={userName}
                                jdeClasses={jdeLaborClasses}
                                equipTiers={jobEquipTiers}
                                settings={settings}
                                costRows={jobCostDetail}
                                txns={txns}
                                neverBilled={neverBilledTxns}
                                fieldMap={fieldMap}
                                rentBasis={rentBasis}
                                rentalUsage={rentalUsage}
                            />
                        ) : null}
                    </>
                ) : null}
            </View>

            {exportCheck ? (() => {
                const inv = invoices.find((v) => v.no === exportCheck.tag);
                // The summary was deleted out from under the open modal.
                if (!inv) return null;
                const ecRows = summaryLines(inv);
                return (
                    <BillingExportCheck
                        rows={ecRows}
                        tag={exportCheck.tag}
                        ctx={{
                            settings,
                            billsAs,
                            equipBills,
                            rateCfg,
                            storedTotal: invTotals[exportCheck.tag]?.total,
                            fuel: fuelSurcharge(ecRows, fuelCtx),
                            clientName: selectedJobData?.AR_AB_Name || selectedJobData?.Client_Name || '',
                            loadTimecards: () => jobTimecardFiles(selectedJob),
                            laborQa: sumQa[exportCheck.tag] ? qaItems(sumQa[exportCheck.tag].areas) : null,
                            contract: contractRules ? contractFor(selectedJob) : null,
                        }}
                        ai={exportCheckAi(inv, ecRows)}
                        onCancel={() => setExportCheck(null)}
                        onProceed={async () => {
                            // Close only after the build — the modal's button holds
                            // the loading state while the backup documents pull in.
                            await downloadSummaryPdf(ecRows, exportCheck.tag);
                            setExportCheck(null);
                        }}
                    />
                );
            })() : null}

            {showModal && selectedTxns.length ? (() => {
                const adding = showModal === 'add';
                const addInv = adding ? addableInvs.find((v) => v.no === addTarget) : null;
                const addCur = addInv ? invTotals[addInv.no] || { lines: [], total: 0 } : null;
                return (
                <Pressable
                    onPress={() => setShowModal(false)}
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 50,
                        backgroundColor: 'rgba(15, 23, 42, 0.5)', alignItems: 'center', justifyContent: 'center',
                        cursor: 'default', padding: 24,
                    }}
                >
                    <Pressable
                        onPress={(e) => e.stopPropagation && e.stopPropagation()}
                        style={{
                            backgroundColor: '#fff', borderRadius: 8, width: 760, maxWidth: '96vw', maxHeight: '88vh',
                            overflowY: 'auto', boxShadow: '0 20px 60px rgba(15, 23, 42, 0.3)', cursor: 'default',
                        }}
                    >
                        <View style={{ paddingTop: 16, paddingBottom: 12, paddingLeft: 22, paddingRight: 22, borderBottomWidth: 1, borderColor: '#e2e8f0', flexDirection: 'row', alignItems: 'center' }}>
                            <View style={{ flexGrow: 1 }}>
                                <Text style={{ fontSize: 16, fontWeight: '700', color: '#0f172a' }}>{adding ? 'Add to Invoice' : 'Create Invoice'}</Text>
                                <Text style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                                    {adding
                                        ? `${selectedTxns.length} transactions · ${selectedJob}`
                                        : `${nextInvoiceNo().no} · ${selectedTxns.length} transactions · ${selectedJob} · ${settings.terms}`}
                                </Text>
                            </View>
                            <Pressable onPress={() => setShowModal(false)}>
                                <Text style={{ fontSize: 18, color: '#64748b' }}>×</Text>
                            </Pressable>
                        </View>
                        <View style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 22, paddingRight: 22 }}>
                            {adding ? (
                                <View style={{ marginBottom: 14 }}>
                                    <Text style={[th, { paddingLeft: 0 }]}>ADD TO</Text>
                                    <View style={{ borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 4, maxHeight: 190, overflowY: 'auto' }}>
                                        {addableInvs.map((v) => {
                                            const on = v.no === addTarget;
                                            const cur = invTotals[v.no] || { lines: [], total: 0 };
                                            return (
                                                <Pressable
                                                    key={v.no}
                                                    onPress={() => setAddTarget(v.no)}
                                                    style={{
                                                        flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, borderColor: '#f1f5f9',
                                                        backgroundColor: on ? '#eff6ff' : '#fff', cursor: 'pointer',
                                                    }}
                                                >
                                                    <View style={{ width: 28, alignItems: 'center' }}>
                                                        <View style={{
                                                            width: 12, height: 12, borderRadius: 6, borderWidth: on ? 4 : 1, borderStyle: 'solid',
                                                            borderColor: on ? '#1e40af' : '#94a3b8', backgroundColor: '#fff',
                                                        }}
                                                        />
                                                    </View>
                                                    <Text style={[td, { width: 110, fontWeight: '600', color: '#1e40af' }]}>{v.no}</Text>
                                                    <Text style={[td, { width: 80 }]}>{fDate(v.date)}</Text>
                                                    <View style={[td, { width: 110 }]}><Badge kind={v.status} /></View>
                                                    <Text style={[td, { flexGrow: 1, textAlign: 'right', color: '#64748b' }]}>
                                                        {cur.lines.length} line{cur.lines.length === 1 ? '' : 's'}
                                                    </Text>
                                                    <Text style={[td, { width: 110, textAlign: 'right', fontWeight: '600' }]}>{money(cur.total)}</Text>
                                                </Pressable>
                                            );
                                        })}
                                    </View>
                                </View>
                            ) : null}
                            <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#e2e8f0' }}>
                                <Text style={[th, { width: 78 }]}>DATE</Text>
                                <Text style={[th, { width: 104 }]}>COST CODE</Text>
                                <Text style={[th, { flexGrow: 1 }]}>DESCRIPTION</Text>
                                <Text style={[th, { width: 95, textAlign: 'right' }]}>COST</Text>
                                <Text style={[th, { width: 66, textAlign: 'right' }]}>MARKUP</Text>
                                <Text style={[th, { width: 100, textAlign: 'right' }]}>AMOUNT</Text>
                            </View>
                            {selectedTxns.slice(0, 100).map((t) => (
                                <View key={t.key} style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#f1f5f9' }}>
                                    <Text style={[td, { width: 78 }]}>{fDate(t.date)}</Text>
                                    <Text style={[td, { width: 104 }]}>{t.cc}</Text>
                                    <Text style={[td, { flexGrow: 1 }]} numberOfLines={1}>{t.ds || t.who || '—'}</Text>
                                    <Text style={[td, { width: 95, textAlign: 'right' }]}>{money(t.cost)}</Text>
                                    <Text style={[td, { width: 66, textAlign: 'right' }]}>{t.hourly ? rateText(t) || 'rate' : t.pt ? (t.mk ? `PT +${t.mk}%` : 'PT at cost') : `+${t.mk}%`}</Text>
                                    <Text style={[td, { width: 100, textAlign: 'right', fontWeight: '600' }]}>{money(t.bill)}</Text>
                                </View>
                            ))}
                            {selectedTxns.length > 100 ? (
                                <Text style={{ fontSize: 11, color: '#64748b', paddingTop: 6 }}>
                                    + {selectedTxns.length - 100} more lines on the invoice
                                </Text>
                            ) : null}
                            <View style={{ alignItems: 'flex-end', marginTop: 10 }}>
                                <View style={{ width: 260 }}>
                                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 }}>
                                        <Text style={{ fontSize: 12, color: '#64748b' }}>Cost subtotal</Text>
                                        <Text style={{ fontSize: 12, color: '#1e293b' }}>{money(selectedTxns.reduce((s, t) => s + t.cost, 0))}</Text>
                                    </View>
                                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 }}>
                                        <Text style={{ fontSize: 12, color: '#64748b' }}>Markup</Text>
                                        <Text style={{ fontSize: 12, color: '#1e293b' }}>{money(selTotal - selectedTxns.reduce((s, t) => s + t.cost, 0))}</Text>
                                    </View>
                                    {selFuel.amt ? (
                                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 }}>
                                            <Text style={{ fontSize: 12, color: '#64748b' }}>Fuel surcharge ({selFuel.periods.filter((p) => p.amt).map((p) => fmtPct(p.pct)).join(' / ')} equip)</Text>
                                            <Text style={{ fontSize: 12, color: '#1e293b' }}>{money(selFuel.amt)}</Text>
                                        </View>
                                    ) : null}
                                    {selFuel.missing.length ? (
                                        <Text style={{ fontSize: 11, color: '#b45309', marginBottom: 3 }}>
                                            No diesel price entered for {selFuel.missing.join(', ')} — enter it under Rates &amp; Rules
                                        </Text>
                                    ) : null}
                                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', borderTopWidth: 1, borderColor: '#e2e8f0', paddingTop: 5 }}>
                                        <Text style={{ fontSize: 13, fontWeight: '700', color: '#0f172a' }}>{adding ? 'Adding' : 'Invoice total'}</Text>
                                        <Text style={{ fontSize: 13, fontWeight: '700', color: '#0f172a' }}>{money(selTotal + selFuel.amt)}</Text>
                                    </View>
                                    {addCur ? (
                                        <>
                                            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 6, marginBottom: 3 }}>
                                                <Text style={{ fontSize: 12, color: '#64748b' }}>{addInv.no} now ({addCur.lines.length} line{addCur.lines.length === 1 ? '' : 's'})</Text>
                                                <Text style={{ fontSize: 12, color: '#1e293b' }}>{money(addCur.total)}</Text>
                                            </View>
                                            <View style={{ flexDirection: 'row', justifyContent: 'space-between', borderTopWidth: 1, borderColor: '#e2e8f0', paddingTop: 5 }}>
                                                <Text style={{ fontSize: 13, fontWeight: '700', color: '#0f172a' }}>New invoice total</Text>
                                                <Text style={{ fontSize: 13, fontWeight: '700', color: '#0f172a' }}>{money(addCur.total + selTotal + selFuel.amt)}</Text>
                                            </View>
                                        </>
                                    ) : null}
                                </View>
                            </View>
                            <Text style={{ fontSize: 11, color: '#64748b', marginTop: 8 }}>
                                {adding
                                    ? `Selected transactions are marked billed at today's markup rates and stay on ${addInv ? addInv.no : 'the invoice'} until they're removed or it is voided. Only Draft and Kicked Back invoices can take new lines.`
                                    : 'Selected transactions are marked billed at today\'s markup rates and stay on this invoice until it is voided.'}
                            </Text>
                        </View>
                        <View style={{ flexDirection: 'row', justifyContent: 'flex-end', paddingTop: 10, paddingBottom: 14, paddingLeft: 22, paddingRight: 22, borderTopWidth: 1, borderColor: '#e2e8f0' }}>
                            <OutlineButton label="Cancel" onPress={() => setShowModal(false)} />
                            <View style={{ width: 10 }} />
                            <Pressable
                                onPress={adding ? confirmAdd : confirmCreate}
                                disabled={adding && !addInv}
                                style={{
                                    paddingTop: 5, paddingBottom: 5, paddingLeft: 14, paddingRight: 14, borderRadius: 4, marginBottom: 6,
                                    backgroundColor: adding && !addInv ? '#cbd5e1' : '#0f172a',
                                }}
                            >
                                <Text style={{ fontSize: 12, color: '#fff', fontWeight: '600' }}>
                                    {adding ? `Add to ${addInv ? addInv.no : 'Invoice'}` : 'Create Invoice'}
                                </Text>
                            </Pressable>
                        </View>
                    </Pressable>
                </Pressable>
                );
            })() : null}

            {addTo ? (
                <BillingAddLines
                    inv={addTo}
                    lines={unbilled}
                    otherLabor={otherLabor}
                    signedBy={signedBy}
                    sigLoading={jobSigs.loading}
                    onAdd={addToSummary}
                    onClose={closeAddPicker}
                    money={money}
                    fDate={fDate}
                    rateText={rateText}
                    CheckBox={CheckBox}
                    DateInput={DateInput}
                />
            ) : null}

            {nbPrompt && selectedTxns.length ? (
                <Pressable
                    onPress={() => setNbPrompt(false)}
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 50,
                        backgroundColor: 'rgba(15, 23, 42, 0.5)', alignItems: 'center', justifyContent: 'center',
                        cursor: 'default', padding: 24,
                    }}
                >
                    <Pressable
                        onPress={(e) => e.stopPropagation && e.stopPropagation()}
                        style={{
                            backgroundColor: '#fff', borderRadius: 8, width: 480, maxWidth: '96vw',
                            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.3)', cursor: 'default',
                        }}
                    >
                        <View style={{ paddingTop: 16, paddingBottom: 12, paddingLeft: 22, paddingRight: 22, borderBottomWidth: 1, borderColor: '#e2e8f0' }}>
                            <Text style={{ fontSize: 16, fontWeight: '700', color: '#0f172a' }}>Mark Not Billable</Text>
                            <Text style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                                {selectedBillable.length} transaction{selectedBillable.length === 1 ? '' : 's'} · {money(selectedBillable.reduce((s, t) => s + t.cost, 0))} cost excluded from invoicing
                            </Text>
                        </View>
                        <View style={{ paddingTop: 12, paddingBottom: 14, paddingLeft: 22, paddingRight: 22 }}>
                            <Text style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>Reason (kept on the line and in its history)</Text>
                            <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginBottom: 6 }}>
                                {NB_QUICK_REASONS.map((r) => (
                                    <Chip key={r} label={r} active={nbReason === r} onPress={() => setNbReason(nbReason === r ? '' : r)} />
                                ))}
                            </View>
                            <TextInput
                                value={nbReason}
                                onChangeText={setNbReason}
                                placeholder="Or type a reason..."
                                style={{
                                    fontSize: 12, borderRadius: 4, borderColor: '#cbd5e1',
                                    paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8,
                                }}
                            />
                        </View>
                        <View style={{ flexDirection: 'row', justifyContent: 'flex-end', paddingTop: 10, paddingBottom: 14, paddingLeft: 22, paddingRight: 22, borderTopWidth: 1, borderColor: '#e2e8f0' }}>
                            <OutlineButton label="Cancel" onPress={() => setNbPrompt(false)} />
                            <View style={{ width: 10 }} />
                            <Pressable
                                onPress={() => setNotBillable(selectedBillable, true, nbReason)}
                                style={{ paddingTop: 5, paddingBottom: 5, paddingLeft: 14, paddingRight: 14, borderRadius: 4, backgroundColor: '#0f172a', marginBottom: 6 }}
                            >
                                <Text style={{ fontSize: 12, color: '#fff', fontWeight: '600' }}>Mark Not Billable</Text>
                            </Pressable>
                        </View>
                    </Pressable>
                </Pressable>
            ) : null}

            {lcPrompt ? (() => {
                const t = lcPrompt.t;
                const onInv = lcPrompt.inv || null;
                const mapped = t.jde ? billsAs[t.jde]?.name || '' : '';
                // Same person, same JDE class, still unbilled — the usual case
                // is payroll coding the person wrong, which hits every one of
                // their timecards, so offer the batch fix.
                // A rolled week row opens on its first day; the other days
                // (`also`) always move with it.
                const also = lcPrompt.also || [];
                const alsoKeys = new Set(also.map((x) => x.key));
                const sibs = txns.filter((x) => !x.inv && x.key !== t.key && !alsoKeys.has(x.key) && x.jde === t.jde && x.who === t.who);
                const isRateLine = t.ct === '51100' || t.ct === '51200';
                // A billed ST/OT line has to land on a priced class — a pick
                // with no sheet rate would re-freeze the snapshot at $0.
                const pickable = (rates) => !(onInv && isRateLine) || (rates && (t.ct === '51200' ? rates.ot : rates.st) > 0);
                const pick = (val) => {
                    setBillClass([t, ...also, ...(lcApplyAll ? sibs : [])], val, onInv);
                    setLcPrompt(null);
                };
                const optionRow = (label, sub, active, ok, onSel) => (
                    <Pressable
                        key={label}
                        onPress={() => { if (ok && !active) onSel(); }}
                        style={{
                            flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
                            paddingTop: 7, paddingBottom: 7, paddingLeft: 10, paddingRight: 10,
                            borderBottomWidth: 1, borderColor: '#f1f5f9',
                            backgroundColor: active ? '#eff6ff' : 'transparent',
                            cursor: ok && !active ? 'pointer' : 'default',
                        }}
                    >
                        <Text style={{ fontSize: 12, color: ok ? '#1e293b' : '#cbd5e1', fontWeight: active ? '700' : '400' }} numberOfLines={1}>
                            {label}{active ? '  ✓' : ''}
                        </Text>
                        <Text style={{ fontSize: 10, color: ok ? '#64748b' : '#cbd5e1', marginLeft: 12, flexShrink: 0 }}>{sub}</Text>
                    </Pressable>
                );
                return (
                    <Pressable
                        onPress={() => setLcPrompt(null)}
                        style={{
                            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 50,
                            backgroundColor: 'rgba(15, 23, 42, 0.5)', alignItems: 'center', justifyContent: 'center',
                            cursor: 'default', padding: 24,
                        }}
                    >
                        <Pressable
                            onPress={(e) => e.stopPropagation && e.stopPropagation()}
                            style={{
                                backgroundColor: '#fff', borderRadius: 8, width: 520, maxWidth: '96vw',
                                boxShadow: '0 20px 60px rgba(15, 23, 42, 0.3)', cursor: 'default',
                            }}
                        >
                            <View style={{ paddingTop: 16, paddingBottom: 12, paddingLeft: 22, paddingRight: 22, borderBottomWidth: 1, borderColor: '#e2e8f0' }}>
                                <Text style={{ fontSize: 16, fontWeight: '700', color: '#0f172a' }}>Adjust Labor Classification</Text>
                                <Text style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                                    {t.who || '—'} · {fDate(t.date)} · JDE class: {t.jde || '—'}{onInv ? ` · on ${onInv.no}` : ''}
                                </Text>
                                <Text style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                                    For lines JDE classified wrong — pick the rate sheet classification this line should bill as.
                                    The fix stays on the line; the job-wide JDE map in Rates &amp; Rules is untouched.
                                </Text>
                            </View>
                            <ScrollView style={{ maxHeight: 320 }}>
                                {optionRow(
                                    `Rates & Rules map — ${mapped || 'unmapped'}`,
                                    mapped && billsAs[t.jde] ? `${money(billsAs[t.jde].st)}/hr ST · ${money(billsAs[t.jde].ot)}/hr OT` : 'no rate',
                                    !t.lcOv,
                                    pickable(t.jde ? billsAs[t.jde] : null),
                                    () => pick(''),
                                )}
                                {sheetLabor.list.map((r) => optionRow(
                                    r.name,
                                    `${money(r.st)}/hr ST · ${money(r.ot)}/hr OT`,
                                    t.lcOv === r.name,
                                    pickable(r),
                                    () => pick(r.name),
                                ))}
                            </ScrollView>
                            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingTop: 10, paddingBottom: 14, paddingLeft: 22, paddingRight: 22, borderTopWidth: 1, borderColor: '#e2e8f0' }}>
                                {sibs.length ? (
                                    <Pressable
                                        onPress={() => setLcApplyAll(!lcApplyAll)}
                                        style={{ flexDirection: 'row', alignItems: 'center', cursor: 'pointer', flexShrink: 1 }}
                                    >
                                        <CheckBox checked={lcApplyAll} onPress={() => setLcApplyAll(!lcApplyAll)} />
                                        <Text style={{ fontSize: 11, color: '#334155', marginLeft: 6, flexShrink: 1 }} numberOfLines={2}>
                                            Also apply to {sibs.length} other unbilled line{sibs.length === 1 ? '' : 's'} for {t.who} ({t.jde})
                                        </Text>
                                    </Pressable>
                                ) : <View />}
                                <OutlineButton label="Cancel" onPress={() => setLcPrompt(null)} />
                            </View>
                        </Pressable>
                    </Pressable>
                );
            })() : null}

            {delPrompt ? (
                <Pressable
                    onPress={() => setDelPrompt(null)}
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 50,
                        backgroundColor: 'rgba(15, 23, 42, 0.5)', alignItems: 'center', justifyContent: 'center',
                        cursor: 'default', padding: 24,
                    }}
                >
                    <Pressable
                        onPress={(e) => e.stopPropagation && e.stopPropagation()}
                        style={{
                            backgroundColor: '#fff', borderRadius: 8, width: 480, maxWidth: '96vw',
                            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.3)', cursor: 'default',
                        }}
                    >
                        <View style={{ paddingTop: 16, paddingBottom: 12, paddingLeft: 22, paddingRight: 22, borderBottomWidth: 1, borderColor: '#e2e8f0' }}>
                            <Text style={{ fontSize: 16, fontWeight: '700', color: '#0f172a' }}>Delete {delPrompt.no}</Text>
                            <Text style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                                {(() => {
                                    const n = Object.values(billed).filter((rec) => rec.inv === delPrompt.no).length;
                                    return n
                                        ? `The summary is removed and its ${n} transaction${n === 1 ? ' goes' : 's go'} back to Unbilled, re-billable on a new summary. This can't be undone.`
                                        : 'The summary is removed. This can’t be undone.';
                                })()}
                            </Text>
                        </View>
                        <View style={{ flexDirection: 'row', justifyContent: 'flex-end', paddingTop: 12, paddingBottom: 14, paddingLeft: 22, paddingRight: 22 }}>
                            <OutlineButton label="Cancel" onPress={() => setDelPrompt(null)} />
                            <View style={{ width: 10 }} />
                            <Pressable
                                onPress={() => {
                                    deleteSummary(delPrompt);
                                    setDelPrompt(null);
                                }}
                                style={{ paddingTop: 5, paddingBottom: 5, paddingLeft: 14, paddingRight: 14, borderRadius: 4, backgroundColor: '#b91c1c', marginBottom: 6 }}
                            >
                                <Text style={{ fontSize: 12, color: '#fff', fontWeight: '600' }}>Delete</Text>
                            </Pressable>
                        </View>
                    </Pressable>
                </Pressable>
            ) : null}

            <CostBasisModal target={costBasis} onClose={() => setCostBasis(null)} />

            {histLine ? (
                <Pressable
                    onPress={() => setHistLine(null)}
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 50,
                        backgroundColor: 'rgba(15, 23, 42, 0.5)', alignItems: 'center', justifyContent: 'center',
                        cursor: 'default', padding: 24,
                    }}
                >
                    <Pressable
                        onPress={(e) => e.stopPropagation && e.stopPropagation()}
                        style={{
                            backgroundColor: '#fff', borderRadius: 8, width: 560, maxWidth: '96vw', maxHeight: '80vh',
                            overflowY: 'auto', boxShadow: '0 20px 60px rgba(15, 23, 42, 0.3)', cursor: 'default',
                        }}
                    >
                        <View style={{ paddingTop: 16, paddingBottom: 12, paddingLeft: 22, paddingRight: 22, borderBottomWidth: 1, borderColor: '#e2e8f0', flexDirection: 'row', alignItems: 'center' }}>
                            <View style={{ flexGrow: 1 }}>
                                <Text style={{ fontSize: 16, fontWeight: '700', color: '#0f172a' }}>Line History</Text>
                                <Text style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                                    {fDate(histLine.date)} · {histLine.cc} · {histLine.who || histLine.ds || '—'} · {money(histLine.cost)}
                                </Text>
                                {histLine.nb && histLine.nbReason ? (
                                    <Text style={{ fontSize: 11, color: '#b91c1c', marginTop: 2 }}>Not billable — {histLine.nbReason}</Text>
                                ) : null}
                                {histLine.pdf ? createElement('a', {
                                    href: tapProxyUrl(histLine.pdf) || histLine.pdf,
                                    target: '_blank',
                                    rel: 'noopener noreferrer',
                                    onClick: (e) => {
                                        e.preventDefault();
                                        openTapDoc(histLine.pdf, [histLine.who, histLine.vinvNo && `#${histLine.vinvNo}`, histLine.po && `PO ${histLine.po}`].filter(Boolean).join(' · '));
                                    },
                                    style: { fontSize: 11, color: '#1e40af', fontWeight: 600, textDecoration: 'none', marginTop: 2 },
                                }, 'View vendor invoice PDF ↗') : null}
                                {histLine.inv ? (
                                    <Pressable
                                        onPress={() => { setHistLine(null); setViewMode('invoices'); setOpenInvoice(histLine.inv); }}
                                        style={{ marginTop: 3, alignSelf: 'flex-start' }}
                                    >
                                        <Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '600', textDecorationLine: 'underline' }}>
                                            Open summary {histLine.inv}
                                        </Text>
                                    </Pressable>
                                ) : histLine.nbInv ? (
                                    <Pressable
                                        onPress={() => { setHistLine(null); setViewMode('invoices'); setOpenInvoice(histLine.nbInv); }}
                                        style={{ marginTop: 3, alignSelf: 'flex-start' }}
                                    >
                                        <Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '600', textDecorationLine: 'underline' }}>
                                            Not billable on summary {histLine.nbInv}
                                        </Text>
                                    </Pressable>
                                ) : null}
                            </View>
                            {histLine.rs ? (
                                <View style={{ marginRight: 8 }}>
                                    <Badge kind={histLine.rs} />
                                </View>
                            ) : null}
                            {/* Badge sets alignSelf: flex-start, which would pin it to the
                                top of this centered header row — the wrapper keeps it on
                                the same line as the review badge and the close button. */}
                            <View>
                                <Badge kind={statusOf(histLine)} />
                            </View>
                            <Pressable onPress={() => setHistLine(null)} style={{ marginLeft: 14 }}>
                                <Text style={{ fontSize: 18, color: '#64748b' }}>×</Text>
                            </Pressable>
                        </View>
                        <View style={{ paddingTop: 12, paddingBottom: 16, paddingLeft: 22, paddingRight: 22 }}>
                            {histLine.hist.length ? [...histLine.hist].reverse().map((e, i) => (
                                <View key={i} style={{ flexDirection: 'row', alignItems: 'baseline', borderBottomWidth: i < histLine.hist.length - 1 ? 1 : 0, borderColor: '#f1f5f9', paddingTop: 7, paddingBottom: 7 }}>
                                    <Text style={{ fontSize: 11, color: '#64748b', width: 108, flexShrink: 0 }}>
                                        {moment.utc(e.t).format('M/D/YY h:mm A')}
                                    </Text>
                                    <Text
                                        style={{
                                            fontSize: 12, color: '#0f172a', flexGrow: 1, flexShrink: 1, paddingRight: 10,
                                            fontWeight: e.a === 'note' ? '400' : '600',
                                            fontStyle: e.a === 'note' ? 'italic' : 'normal',
                                        }}
                                    >
                                        {e.a === 'note' ? `“${e.n || ''}”` : histLabel(e)}
                                    </Text>
                                    {e.u ? <Text style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>{e.u}</Text> : null}
                                </View>
                            )) : (
                                <Text style={{ fontSize: 13, color: '#64748b', paddingTop: 6, paddingBottom: 6 }}>
                                    No activity recorded for this line yet. Reviews, notes, billable changes and invoicing are logged from here on.
                                </Text>
                            )}
                            {histLine.hist.length >= HIST_CAP ? (
                                <Text style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>
                                    Showing the most recent {HIST_CAP} events — older activity has rolled off.
                                </Text>
                            ) : null}
                            {!versionLocked ? (
                                <View style={{ flexDirection: 'row', alignItems: 'center', borderTopWidth: 1, borderColor: '#e2e8f0', marginTop: 10, paddingTop: 10 }}>
                                    <TextInput
                                        value={noteDraft}
                                        onChangeText={(v) => setNoteDraft(v.slice(0, NOTE_MAX))}
                                        onSubmitEditing={addNote}
                                        placeholder="Add a note to this line..."
                                        style={{
                                            flexGrow: 1, fontSize: 12, borderRadius: 4, borderColor: '#cbd5e1',
                                            paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8, marginRight: 8,
                                        }}
                                    />
                                    <OutlineButton label="Add Note" onPress={addNote} disabled={!noteDraft.trim()} />
                                </View>
                            ) : null}
                        </View>
                    </Pressable>
                </Pressable>
            ) : null}

            {/* Inline scanned-invoice viewer (shared with the third-party tabs). */}
            {viewerNode}
        </ScrollView>
    );
};

// The trailer TV dashboard (billingDashboard.js) reads the same stored
// summaries, so it shares the class buckets and the stage normalizer.
export { bucketOf, groupOf, normInvStatus, GROUPS };

export default Billing;
